/* eslint-disable @typescript-eslint/no-explicit-any */
import { EdgeWithMetadata, NodeWithMetadata, WebColaLayout, WebColaTranslator, TransformInfo, LayoutState, WebColaLayoutOptions, WebColaRenderTransitionMode } from './webcolatranslator';
import { InstanceLayout, isInstanceLayout, LayoutNode, ColorSource } from '../../layout/interfaces';
import type { LayoutErrorDetail, LayoutWarning } from '../../layout/error-state';
import type { Layout, ID3StyleLayoutAdaptor } from 'webcola';
import * as d3VendorModule from '../../vendor/d3.v4.min.js';

/**
 * What `cola.d3adaptor()` actually hands back: a Layout plus the D3 shims.
 * The plain `Layout` class has only a STATIC `drag`, so annotating the adaptor
 * as `Layout` hides the instance `drag()` this file calls.
 */
type D3Layout = Layout & ID3StyleLayoutAdaptor;
import { ITuple } from '../../data-instance/interfaces';
import { MAIN_LABEL_FONT_SIZE, SECONDARY_FONT_SIZE, LABEL_LINE_HEIGHT_RATIO, resolveAttrFontSize } from '../../layout/text-extent';
import { FALLBACK_ICON, getInlinableIconSvg } from '../../layout/icon-registry';
import { setLabLightness, type NodeColorParams } from '../../layout/colorpicker';
import { getGraphCSS } from './webcola-cnd-graph.styles';
import {
  type EdgeRouter as SpytialEdgeRouter,
  type RouterHost,
  type RoutingModeDefinition,
  choosePortSides,
  getRoutingMode,
  listRoutingModes,
  TautRouter,
  EDGE_CLEARANCE_PX,
  CROSSING_OPTIMIZATION_EDGE_THRESHOLD,
  clipLineToRectExit,
  getRouteLength,
  filletPath,
  gridRouteToPoints,
  pointsToGridRoute,
  flattenGridRouteBends,
  cleanupOrthogonalRoute,
  getCola,
  requireCola,
  visibleBounds,
  renderedBounds,
  closestPointOnRect,
  clipEndpointToVisibleBoundary,
  addTangentGuides,
  dominantDirection,
  stableEdgePath,
  portAttachment,
  applyPortBasedEndpointsOrthogonal,
  applyEdgeOffsetWithIndex,
  calculateCurvatureWithIndex,
  clampCurvature,
  applyCurvatureToRoute,
  applyLabelDisplacement,
  buildGridRouter,
  touchDirection,
  perpendicularRoute,
  gridRouteMidpoint,
} from './routing';

// The vendored d3 v4 is imported here, NOT read off `window`.
//
// This module registers <webcola-cnd-graph> at module scope (bottom of file),
// and `customElements.define` synchronously upgrades any matching element
// already in the document — which runs the constructor, which needs d3. While
// d3 came off `window`, that registration silently depended on index.ts having
// installed the global first, which it does asynchronously. A page holding the
// element before the bundle evaluated lost that race, and the constructor died
// on `d3.select`. Owning d3 here makes the registration valid at the moment it
// happens. See #574.
//
// It also settles which d3 wins: this file uses the v4-only `d3.event` and
// `d3.mouse` API, so a host page carrying d3 v7 on `window.d3` would break it.
// `window.d3v4` stays honoured as the deliberate opt-out for a host that wants
// to supply its own v4 build.
//
// The vendored bundle is UMD and loads without a DOM, so this module stays
// LOADABLE in Node (SSR, tests, headless evaluation) — it is reachable from the
// npm entries' static import graph via SpytialExplorer / StructuredInputGraph.
//
// Bundlers disagree on what a UMD file becomes, so normalise the shape: esbuild
// hands back a namespace carrying the named exports and NO `default`, while a
// plain CJS interop puts everything under `default`. Take whichever arrived.
const d3Vendor: any = (d3VendorModule as any).default ?? d3VendorModule;

const d3: any = (typeof window !== 'undefined' && window.d3v4) || d3Vendor;


/**
 * Checks if two SVG elements are overlapping.
 * 
 * @param element1 - First element
 * @param element2 - Second element
 * @returns True if elements overlap
 */
function isOverlapping(element1: SVGElement, element2: SVGElement): boolean {
  function hasgetBBox(target: any): target is { getBBox: any } {
    return target && typeof target === 'object' && 'getBBox' in target;
  }

  const bbox1 = hasgetBBox(element1) ? element1.getBBox() : { x: 0, y: 0, width: 0, height: 0 };
  const bbox2 = hasgetBBox(element2) ? element2.getBBox() : { x: 0, y: 0, width: 0, height: 0 };
  
  return !(bbox2.x > bbox1.x + bbox1.width ||
           bbox2.x + bbox2.width < bbox1.x ||
           bbox2.y > bbox1.y + bbox1.height ||
           bbox2.y + bbox2.height < bbox1.y);
}

const DEFAULT_SCALE_FACTOR = 5;

/**
 * WebCola CnD Graph Custom Element
 * Full implementation using WebCola constraint-based layout with D3 integration
 * @field currentLayout - Holds the current custom WebColaLayout instance
 * @field colaLayout - Holds the current layout instance used by WebCola
 * 
 * Features:
 * - Interactive edge input mode with keyboard shortcuts (Cmd/Ctrl)
 * - Visual edge creation by clicking and dragging between nodes
 * - Edge modification by clicking on existing edges in input mode
 * - Centralized state management for IInputDataInstance
 * - Automatic layout regeneration when data instance changes
 * - Self-loop edge support with confirmation
 * - Zoom/pan disable during input mode
 * - Comprehensive event system for external integration
 * 
 * Events Fired:
 * - 'input-mode-activated': When Cmd/Ctrl is pressed to activate input mode
 * - 'input-mode-deactivated': When Cmd/Ctrl is released to deactivate input mode  
 * - 'edge-creation-requested': When user drags between nodes to create a new edge
 *   * event.detail: { relationId: string, sourceNodeId: string, targetNodeId: string, tuple: ITuple }
 * - 'edge-modification-requested': When user clicks on existing edge to modify it
 *   * event.detail: { oldRelationId: string, newRelationId: string, sourceNodeId: string, targetNodeId: string, tuple: ITuple }
 * 
 * External State Management:
 * React components should subscribe to these events and handle:
 * 1. Updating the IInputDataInstance with new atoms/relations
 * 2. Regenerating CnD layout constraints from the updated data
 * 3. Calling renderLayout() to apply changes and re-render the visualization
 * 
 * This ensures React components serve as the single source of truth for state
 * while the WebCola component focuses purely on visualization and user interaction.
 */

/**
 * A named display theme for {@link WebColaCnDGraph}. A theme is the single source
 * of truth for the graph's colors and bundles the three levers:
 *  1. the canvas background (the `--cnd-canvas-bg` slot),
 *  2. the default edge color (the `--cnd-edge-color` slot), and
 *  3. the default node-color palette ({@link GraphTheme.nodeColors}).
 *
 * `light` and `dark` are built in; register more via
 * {@link WebColaCnDGraph.registerTheme} / {@link WebColaCnDGraph.registerThemes}
 * and they appear in the Mode dropdown.
 */
export interface GraphTheme {
  /** Unique key — used as the `theme` attribute value and the dropdown option value. */
  name: string;
  /** Human-readable label for the dropdown. Defaults to a title-cased `name`. */
  label?: string;
  /**
   * `--cnd-*` custom-property values layered over the light baseline (the
   * stylesheet literals). An absent/empty map IS the light theme. Slots are
   * applied to the host element and read by the shadow stylesheet via `var()`.
   */
  slots?: Record<string, string>;
  /**
   * Tuning for the default (phyllotactic) node palette — e.g. a dark canvas
   * passes `{ lightness: 74 }` so the algorithm-assigned type colors stay
   * legible. Absent = leave the canonical light palette untouched.
   */
  nodeColors?: NodeColorParams;
}

// HTMLElement doesn't exist in Node; fall back to a plain class so the module
// can be imported headless. Instantiating the element still requires a DOM.
const HTMLElementBase = (typeof HTMLElement !== 'undefined'
  ? HTMLElement
  : (class {} as unknown as typeof HTMLElement));

export class WebColaCnDGraph extends HTMLElementBase {
  private svg!: any;
  private container!: any;
  private currentLayout!: WebColaLayout;
  private colaLayout!: D3Layout;
  /** Signature of the warnings currently on screen. See renderLayoutWarnings. */
  private currentWarningSignature: string | null = null;
  /**
   * Signature of the warning set the user dismissed, or null if they haven't.
   * Compared against each render's signature so a dismissal survives re-renders
   * of the *same* warnings but does not suppress new ones.
   */
  private dismissedWarningSignature: string | null = null;
  private readonly lineFunction: d3.Line<{ x: number; y: number }>;
  /**
   * Snapshot of the seed positions the most-recent policy resolved
   * before the solver ran. Captured for the seed-vs-output
   * decomposition in the appropriateness experiment. `null` when no
   * policy was applied this render (e.g., first frame, no prior).
   */
  private lastSeedState: LayoutState | null = null;

  /**
   * Configuration constants for SVG
   */

  private static readonly DEFAULT_SVG_WIDTH = 800;
  private static readonly DEFAULT_SVG_HEIGHT = 600;

  /**
   * Configuration constants for node visualization
   */
  private static readonly SMALL_IMG_SCALE_FACTOR = 0.3;
  private static readonly NODE_BORDER_RADIUS = 6;
  private static readonly NODE_STROKE_WIDTH = 1.5;

  /**
   * Default canvas color (tufte-css `#fffff8` warm white). Used when the host
   * element has no `background` attribute. The canvas color drives the
   * container background, node fills, edge-label halos, and PNG export
   * background so they all stay in sync.
   */
  private static readonly DEFAULT_CANVAS_BG = '#fffff8';

  /**
   * Returns the active canvas color, resolved highest-priority-first: an
   * explicit `--cnd-canvas-bg` theme override, then the legacy `background`
   * attribute (sugar for the canvas slot — composes with either base theme),
   * then the base theme's canvas (dark canvas, or the warm-white default).
   * Drives node fills, the edge-label halo, and PNG export so they stay in sync.
   */
  private getCanvasBackground(): string {
    const override = this.themeOverrides['--cnd-canvas-bg'];
    if (override) return override;
    const bg = this.getAttribute('background');
    if (bg) return bg;
    return this.activeSlots()['--cnd-canvas-bg'] ?? WebColaCnDGraph.DEFAULT_CANVAS_BG;
  }

  /**
   * Name of the active theme (a key into {@link themeRegistry}). `'light'`
   * (default) preserves the historical warm-white look exactly. Switching theme
   * sets `--cnd-*` custom properties on the host; the shadow stylesheet reads
   * them via `var(--cnd-*, <light-literal>)`, so an unthemed graph is
   * pixel-identical to before. Data-driven colors (`d.color`/`d.highlight`) are
   * never overridden — only static fallbacks and algorithm defaults are themed.
   */
  private currentThemeName: string = 'light';

  /**
   * Registry of available themes, keyed by name. Seeded with the built-in
   * `light` and `dark`; hosts add more via {@link registerTheme} /
   * {@link registerThemes}, which surface in the Mode dropdown. Per-instance, so
   * different graphs can offer different theme sets.
   */
  private themeRegistry: Map<string, GraphTheme> = new Map<string, GraphTheme>([
    ['light', { name: 'light', label: 'Light', slots: {}, nodeColors: {} }],
    ['dark', { name: 'dark', label: 'Dark', slots: WebColaCnDGraph.DARK_THEME, nodeColors: { lightness: 74 } }]
  ]);

  /**
   * Per-slot color overrides layered on top of the active theme (e.g.
   * `{ '--cnd-canvas-bg': '#101010' }`). Set via the second argument to
   * {@link setTheme}. The `background` attribute is sugar for `--cnd-canvas-bg`.
   */
  private themeOverrides: Record<string, string> = {};

  /**
   * Per-call node-color overrides layered on top of the active theme's
   * `nodeColors` (e.g. `{ lightness: 80 }`). Set via the third argument to
   * {@link setTheme}.
   */
  private nodeColorOverrides: NodeColorParams = {};

  /** The active theme object (falls back to `light` if the name is unknown). */
  private getActiveTheme(): GraphTheme {
    return this.themeRegistry.get(this.currentThemeName) ?? this.themeRegistry.get('light')!;
  }

  /** The active theme's `--cnd-*` slot values (empty for the light baseline). */
  private activeSlots(): Record<string, string> {
    return this.getActiveTheme().slots ?? {};
  }

  /**
   * Every `--cnd-*` slot key any registered theme can set — the set to clear
   * before applying a theme so a switch never leaves stale custom properties.
   */
  private allSlotKeys(): Set<string> {
    const keys = new Set<string>(Object.keys(WebColaCnDGraph.DARK_THEME));
    for (const theme of this.themeRegistry.values()) {
      if (theme.slots) for (const key of Object.keys(theme.slots)) keys.add(key);
    }
    return keys;
  }

  /**
   * Register one or more custom themes (or override a built-in by reusing its
   * name). Registered themes appear in the Mode dropdown immediately. Each theme
   * needs a unique `name`; see {@link GraphTheme}.
   */
  public registerThemes(themes: GraphTheme[]): void {
    for (const theme of themes) {
      if (!theme || !theme.name) {
        console.warn('registerThemes: each theme needs a non-empty `name`; skipping', theme);
        continue;
      }
      this.themeRegistry.set(theme.name, { ...theme });
    }
    this.updateThemeDropdown();
  }

  /** Register a single custom theme. See {@link registerThemes}. */
  public registerTheme(theme: GraphTheme): void {
    this.registerThemes([theme]);
  }

  /** Names of all registered themes, in registration order (built-ins first). */
  public getThemeNames(): string[] {
    return [...this.themeRegistry.keys()];
  }

  /** Title-case a theme name for display when it has no explicit `label`. */
  private titleCaseThemeName(name: string): string {
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  /**
   * Rebuild the Mode dropdown's options from the registry (only when the set of
   * themes changed) and sync its value to the active theme. No-op before the
   * toolbar exists. Called on init, on {@link setTheme}, and on registration.
   */
  private updateThemeDropdown(): void {
    const select = this.shadowRoot?.querySelector('#theme-mode') as HTMLSelectElement | null;
    if (!select) return;
    const names = this.getThemeNames();
    const signature = names.join('');
    if (select.getAttribute('data-themes') !== signature) {
      select.textContent = '';
      for (const name of names) {
        const theme = this.themeRegistry.get(name)!;
        const option = document.createElement('option');
        option.value = name;
        option.textContent = theme.label ?? this.titleCaseThemeName(name);
        select.appendChild(option);
      }
      select.setAttribute('data-themes', signature);
    }
    select.value = this.currentThemeName;
  }

  /**
   * Dark-theme custom-property values. Light mode simply removes these
   * properties, falling back to the literals baked into the stylesheet. A few
   * entries (canvas-bg, edge-color, node-border, label-text) are also read
   * directly from JS for colors applied as SVG presentation attributes, where
   * a CSS `var()` cannot reach.
   */
  private static readonly DARK_THEME: Record<string, string> = {
    '--cnd-canvas-bg': '#1e1e1e',
    '--cnd-label-text': '#e6e6e6',
    '--cnd-edge-color': '#7d828b',
    '--cnd-node-border': '#8b919b',
    '--cnd-edge-highlight': '#f0f0f0',
    '--cnd-inferred-highlight': '#9aa0a8',
    '--cnd-group-fill': 'rgba(140, 140, 140, 0.12)',
    '--cnd-group-stroke': '#7a7f87',
    '--cnd-loading-bg': 'rgba(30, 32, 37, 0.95)',
    '--cnd-loading-text': '#c9cdd4',
    '--cnd-loading-dot': '#5dade2',
    '--cnd-panel-bg': '#23262d',
    '--cnd-panel-text': '#e6e6e6',
    '--cnd-panel-text-muted': '#a7adb8',
    '--cnd-panel-border': '#3a3f49',
    '--cnd-toolbar-bg': 'rgba(30, 32, 37, 0.95)',
    '--cnd-control-bg': '#2b2f37',
    '--cnd-control-bg-hover': '#343a44',
    '--cnd-control-border': '#3a3f49'
  };

  /**
   * Set the graph's theme by name. `'light'` (default) and `'dark'` are built
   * in; any name passed to {@link registerThemes} also works. An unknown name
   * falls back to `'light'`. The theme is the single source of truth for the
   * graph's colors and subsumes the legacy `background` attribute (just the
   * canvas slot). Optional `overrides` layer per-slot `--cnd-*` values on top,
   * e.g. `setTheme('dark', { '--cnd-canvas-bg': '#101010' })`; optional
   * `nodeColors` retunes the node palette, e.g. `setTheme('dark', undefined, { lightness: 80 })`.
   *
   * Everything is a pure display concern: `--cnd-*` custom properties are set on
   * the host, and data-driven node/edge colors are re-tinted in place against an
   * already-rendered graph (no layout regeneration), so switching theme on a live
   * graph updates it instantly without reshuffling.
   */
  public setTheme(
    name: string,
    overrides?: Record<string, string>,
    nodeColors?: NodeColorParams
  ): void {
    this.currentThemeName = this.themeRegistry.has(name) ? name : 'light';
    this.themeOverrides = overrides ? { ...overrides } : {};
    this.nodeColorOverrides = nodeColors ? { ...nodeColors } : {};
    this.applyTheme();
    // Re-tint data-driven colors on the live DOM (no-op before first render).
    this.repaintThemedColors();
    // Keep the Mode dropdown in sync with the active theme.
    this.updateThemeDropdown();
    // Reflect for declarative hosts / introspection (guarded against the
    // attributeChangedCallback re-entering setTheme).
    if (this.getAttribute('theme') !== this.currentThemeName) {
      this.setAttribute('theme', this.currentThemeName);
    }
  }

  /**
   * Apply the active theme's slots, then the `background` attribute (canvas
   * sugar), then explicit per-slot overrides (highest priority) as `--cnd-*`
   * custom properties on the host. Clears every known slot first so switching
   * themes never leaves stale properties behind (and `light` reverts to the
   * stylesheet literals).
   */
  private applyTheme(): void {
    for (const key of this.allSlotKeys()) {
      this.style.removeProperty(key);
    }
    for (const [key, value] of Object.entries(this.activeSlots())) {
      this.style.setProperty(key, value);
    }
    const bg = this.getAttribute('background');
    if (bg) this.style.setProperty('--cnd-canvas-bg', bg);
    for (const [key, value] of Object.entries(this.themeOverrides)) {
      if (value) this.style.setProperty(key, value);
    }
  }

  /**
   * The active theme's node-color tuning (its `nodeColors`, e.g. light `{}` /
   * dark `{ lightness: 74 }`) with any per-call `nodeColors` override from
   * {@link setTheme} layered on top. Consumed by {@link themedNodeColor}.
   */
  private resolvedNodeColorParams(): NodeColorParams {
    return {
      ...this.getActiveTheme().nodeColors,
      ...this.nodeColorOverrides
    };
  }

  /**
   * Whether the renderer may re-tune a node's color for the active theme, based
   * on its {@link ColorSource}. Only the default palette is themeable today;
   * this is the single decision point, so a new ColorSource variant opts in here
   * (and defaults to non-themeable until it does). Absent source is treated as
   * the default palette for backward compatibility.
   */
  private isThemeableNodeColor(source: ColorSource | undefined): boolean {
    return (source ?? ColorSource.DefaultPalette) === ColorSource.DefaultPalette;
  }

  /**
   * Resolve a node's effective color for the active theme. An algorithm-assigned
   * color (a themeable {@link ColorSource}) is re-tinted to the theme's node
   * lightness so the canonical palette adapts to the canvas; a user `color`
   * directive, or the implicit black fallback, is passed through to
   * {@link themedDataColor} unchanged. With no themed lightness (light theme)
   * this is identity — the historical look is preserved exactly.
   */
  private themedNodeColor(
    d: any,
    darkVar: keyof typeof WebColaCnDGraph.DARK_THEME,
    lightFallback: string | null
  ): string | null {
    const lightness = this.resolvedNodeColorParams().lightness;
    const isBlackish =
      !d.color || d.color === 'black' || d.color === '#000' || d.color === '#000000';
    const base =
      lightness !== undefined && this.isThemeableNodeColor(d.colorSource) && !isBlackish
        ? setLabLightness(d.color, lightness)
        : d.color;
    return this.themedDataColor(base, darkVar, lightFallback);
  }

  /** Node border stroke (themed). Shared by initial render and live repaint. */
  private nodeBorderColor(d: any): string | null {
    return this.themedNodeColor(d, '--cnd-node-border', 'black');
  }

  /** Most-specific-type label fill (themed). */
  private nodeTypeLabelColor(d: any): string | null {
    return this.themedNodeColor(d, '--cnd-label-text', 'black');
  }

  /**
   * Node rectangle fill. Hidden nodes, and nodes whose icon occupies the box
   * (`placement: full`), stay transparent — real `transparent`, not the canvas
   * color, so a group hull shows through behind the icon. An explicit
   * `atomStyle.fillStyle.color` (d.fillColor) is an opt-in real fill and wins
   * even under a full-bleed icon. Otherwise Tufte: the fill matches the canvas
   * so only stroke + label distinguish a node.
   *
   * Note this keys off placement, not label visibility — a labelled atom with a
   * faded full-bleed icon (the watermark idiom) gets the same see-through box as
   * an unlabelled glyph.
   */
  private nodeFillColor(d: any): string {
    if (this.isHiddenNode(d)) return 'transparent';
    if (d.fillColor) return d.fillColor;
    if (d.icon && !this.isBadgeIcon(d)) return 'transparent';
    return this.getCanvasBackground();
  }

  /** True when the node's icon draws as a small corner marker rather than filling the box. */
  private isBadgeIcon(d: any): boolean {
    return d.iconPlacement === 'badge';
  }

  /** Icon width: the full box, or a fraction of it for a badge. */
  private iconWidth(d: any): number {
    const vw = d.visualWidth ?? d.width;
    return this.isBadgeIcon(d) ? vw * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR : vw;
  }

  /** Icon height: the full box, or a fraction of it for a badge. */
  private iconHeight(d: any): number {
    const vh = d.visualHeight ?? d.height;
    return this.isBadgeIcon(d) ? vh * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR : vh;
  }

  /**
   * Icon left edge. A badge sits against the box's top-right corner; a full icon
   * spans the box from `fullX` (defaulting to the box's own left edge — callers
   * that track WebCola bounds pass those instead).
   */
  private iconX(d: any, fullX?: number): number {
    const vw = d.visualWidth ?? d.width;
    if (this.isBadgeIcon(d)) return d.x + vw / 2 - (vw * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR);
    return fullX ?? d.x - vw / 2;
  }

  /** Icon top edge — the box's top edge in both placements (see {@link iconX} for `fullY`). */
  private iconY(d: any, fullY?: number): number {
    const vh = d.visualHeight ?? d.height;
    if (this.isBadgeIcon(d)) return d.y - vh / 2;
    return fullY ?? d.y - vh / 2;
  }

  /** Edge stroke (themed): preserves chosen colors, themes the implicit default. */
  private edgeStrokeColor(d: any): string | null {
    return this.isAlignmentEdge(d) ? 'none' : this.themedDataColor(d.color, '--cnd-edge-color', null);
  }

  /**
   * Node border stroke width. An explicit `atomStyle.borderStyle.width`
   * (d.borderWidth) wins; otherwise null so the constant default (NODE_STROKE_WIDTH)
   * applies. Consumed via `.style()` (not `.attr()`) so it beats any node-rect
   * CSS class rule — the same reason edge stroke-width uses `.style()`.
   */
  private nodeStrokeWidth(d: any): string | null {
    return d.borderWidth != null ? `${d.borderWidth}px` : null;
  }

  /** Node main-label fill from `atomStyle.textStyle.color`; null = inherit the default label color. */
  private nodeLabelColor(d: any): string | null {
    return d.textStyle?.color ?? null;
  }

  /** Group label fill from the group directive's `textStyle.color`; null = keep the default (#333). */
  private groupLabelColor(d: any): string | null {
    return d.labelTextStyle?.color ?? null;
  }

  /**
   * Re-apply the data-driven themed colors (node border/fill, type-label fill,
   * edge stroke) to the live DOM. The `--cnd-*` slots auto-update via CSS
   * `var()`, but per-element colors are SVG presentation attributes that a host
   * theme switch must repaint. No-op before the first render.
   */
  private repaintThemedColors(): void {
    if (this.svgNodes) {
      this.svgNodes.select('rect')
        .attr('stroke', (d: any) => this.nodeBorderColor(d))
        .attr('fill', (d: any) => this.nodeFillColor(d));
      this.svgNodes.selectAll('text.mostSpecificTypeLabel')
        .style('fill', (d: any) => this.nodeTypeLabelColor(d));
    }
    if (this.svgLinkGroups) {
      this.svgLinkGroups.selectAll('path.link, path.inferredLink, path.alignmentLink')
        .attr('stroke', (d: any) => this.edgeStrokeColor(d));
    }
  }

  static get observedAttributes(): string[] {
    return ['theme', 'background'];
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    if (name === 'theme') {
      const requested = newValue || 'light';
      if (requested !== this.currentThemeName) {
        this.setTheme(requested, this.themeOverrides, this.nodeColorOverrides);
      }
    } else if (name === 'background') {
      // `background` is canvas sugar; re-apply so it composes with the base
      // theme in either mode (and so clearing it reverts to the theme canvas).
      this.applyTheme();
    }
  }

  /**
   * Resolve a data-driven stroke/fill color that may be the *implicit* black
   * default (Alloy/Forge assign `"black"` to uncolored edges, node borders, and
   * type labels). A genuinely chosen non-black color is preserved under every
   * theme; the implicit black/empty default is replaced with the theme's slot
   * value when the active theme defines that slot, so edges, arrowheads (which
   * inherit via `context-stroke`), and borders stay legible on a themed canvas.
   * Under the light baseline (no slot) the literal fallback is used — unchanged.
   */
  private themedDataColor(
    color: string | null | undefined,
    slotKey: keyof typeof WebColaCnDGraph.DARK_THEME,
    lightFallback: string | null
  ): string | null {
    const isImplicitDefault =
      !color || color === 'black' || color === '#000' || color === '#000000';
    const themed = this.themeOverrides[slotKey] ?? this.activeSlots()[slotKey];
    if (themed !== undefined) {
      return isImplicitDefault ? themed : color!;
    }
    return color || lightFallback;
  }

  /**
   * Default font stack. Atkinson Hyperlegible (Braille Institute, OFL) is
   * designed for low-vision and dyslexic readers — distinguishable letterforms
   * (a/g/q/d, 0/O, 1/l/I) without looking childlike. Falls back to system-ui
   * if the host hasn't loaded the web font.
   */
  private static readonly DEFAULT_FONT_FAMILY = "'Atkinson Hyperlegible', system-ui, -apple-system, sans-serif";

  /**
   * Returns the active font stack: the host's `font-family` attribute if
   * set, otherwise the Atkinson Hyperlegible default.
   */
  private getFontFamily(): string {
    return this.getAttribute('font-family') ?? WebColaCnDGraph.DEFAULT_FONT_FAMILY;
  }

  /**
   * Returns the @import statement(s) needed to load the default font from
   * Google Fonts. Skipped when the host has set a custom `font-family`, since
   * users with their own font shouldn't pay for an unused download.
   */
  private getFontImports(): string {
    if (this.hasAttribute('font-family')) return '';
    return "@import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap');";
  }

  /**
   * Text-sizing constants. Node-label sizes (main + secondary) live in
   * `src/layout/text-extent.ts` so the layout estimator and the renderer
   * agree by construction. The constants below are for non-node text:
   * group labels (DEFAULT/MIN/MAX_FONT_SIZE) and the renderer's inset for
   * text inside node rects (TEXT_PADDING).
   */
  private static readonly DEFAULT_FONT_SIZE = 10;
  private static readonly MIN_FONT_SIZE = 6;
  private static readonly MAX_FONT_SIZE = 16;
  private static readonly TEXT_PADDING = 8;

  /**
   * Configuration constants for screenshot export
   */
  private static readonly SCREENSHOT_SCALE = 3; // 3x for high-def PNG export

  /**
   * Configuration constants for group visualization
   */
  private static readonly DISCONNECTED_NODE_PREFIX = "_d_";
  private static readonly GROUP_BORDER_RADIUS = 8;
  private static readonly GROUP_FILL_OPACITY = 0.10;
  private static readonly GROUP_STROKE_OPACITY = 0.4;
  private static readonly GROUP_STROKE_WIDTH = 1.5;
  private static readonly GROUP_LABEL_PADDING = 20;
  private static readonly GROUP_LABEL_PILL_PADDING_X = 8;
  private static readonly GROUP_LABEL_PILL_PADDING_Y = 3;
  private static readonly GROUP_LABEL_PILL_OFFSET_Y = 4;
  private static readonly DEFAULT_GROUP_COMPACTNESS = 1e-5;
  private static readonly SELF_LOOP_CURVATURE_SCALE = 0.2;
  private static readonly VIEWBOX_PADDING = 10;

  /**
   * Configuration constants for WebCola layout iterations
   * Reduced from previous values (10, 100, 1000, 5) to improve performance
   * and prevent browser timeouts on large graphs
   */
  private static readonly INITIAL_UNCONSTRAINED_ITERATIONS = 10;
  private static readonly INITIAL_USER_CONSTRAINT_ITERATIONS = 50;
  private static readonly INITIAL_ALL_CONSTRAINTS_ITERATIONS = 200;
  private static readonly GRID_SNAP_ITERATIONS = 1; // Reduced from 5 for performance, but kept at 1 for alignment
  /**
   * Cap on the synchronous alpha-decay ticks driven after layout.start()
   * returns (see renderLayout). start() has already run every constraint
   * phase; the decay ticks only polish stress below the convergence
   * threshold, which normally takes a handful of iterations. A stress
   * plateau that never crosses the threshold hits this cap and is forced
   * to a clean 'end' instead of iterating forever.
   */
  private static readonly MAX_SYNC_DECAY_TICKS = 500;
  private static readonly LOADING_INDICATOR_DELAY_MS = 180;

  /**
   * Base morph transition timing (at speed = 1.0 ≈ 1.1 s total).
   * Exit duration is longer so departing elements have time to fade gracefully.
   * Enter starts after a short delay so exits are underway first.
   * Actual durations are `base × morphSpeed`; default speed is 0.2.
   */
  private static readonly MORPH_BASE_EXIT_DURATION_MS = 1100;
  private static readonly MORPH_BASE_ENTER_DURATION_MS = 1000;
  private static readonly MORPH_BASE_ENTER_DELAY_MS = 200;

  /**
   * Counter for edge routing iterations (for performance tracking)
   */
  private edgeRouteIdx = 0;

  /**
   * Cache for edge routing optimizations
   */
  private edgeRoutingCache: {
    edgesBetweenNodes: Map<string, EdgeWithMetadata[]>;
    alignmentEdges: Set<string>;
    nodeEdgesBySide: Map<string, { top: any[]; bottom: any[]; left: any[]; right: any[] }>;
  } = {
    edgesBetweenNodes: new Map(),
    alignmentEdges: new Set(),
    nodeEdgesBySide: new Map()
  };

  /**
   * Stores computed route arrays between route computation and SVG rendering,
   * enabling post-routing optimization (e.g. crossing reduction).
   */
  private computedRoutes: Map<string, Array<{ x: number; y: number }>> = new Map();

  /**
   * Per-routeEdges cache of inflated obstacle rectangles (one per node, keyed by
   * id) for the taut router. Built once at the top of routeEdges — positions are
   * frozen by then — so each edge's buildRouterObstacles is an O(N) filter rather
   * than recomputing getRenderedBounds for every node on every edge (O(E·N)).
   * Null outside a routeEdges pass.
   */
  private routerObstacleCache: Array<{ id: string; minX: number; minY: number; maxX: number; maxY: number }> | null = null;

  /**
   * Guard flag to prevent re-entrance during grid routing operations.
   * This prevents infinite loops when gridify is called while already in progress.
   */
  private isGridifyingInProgress: boolean = false;

  // We use these to store state and references.
  private svgNodes : any;
  private svgLinkGroups : any;
  private svgGroups : any;
  private svgGroupLabels: any;
  private svgGroupLabelBgs: any;
  private zoomBehavior: any;
  private storedTransform: any;
  
  /**
   * Line function for grid edges (sharp right angles, no curves)
   */
  private readonly gridLineFunction: d3.Line<{ x: number; y: number }>;
  
  /**
   * Tracks whether the user has manually interacted with zoom/pan.
   * When true, we don't auto-fit the viewport to preserve user's view.
   */
  private userHasManuallyZoomed: boolean = false;
  
  /**
   * Tracks whether this is the initial render (first layout).
   * We always fit viewport on initial render.
   */
  private isInitialRender: boolean = true;
  private loadingShowTimer: number | null = null;
  
  /**
   * Stores the starting coordinates when a node begins dragging so
   * drag end events can report both the previous and new positions.
   */
  private dragStartPositions: Map<string, { x: number; y: number }> = new Map();

  /**
   * Input mode state management for edge creation and modification
   */
  private isInputModeActive: boolean = false;
  private inputModeEnabled: boolean = true;
  private inputModeListenersAttached: boolean = false;
  private readonly handleInputModeKeydown = (event: KeyboardEvent): void => {
    if ((event.metaKey || event.ctrlKey) && !this.isInputModeActive) {
      this.activateInputMode();
    }
  };
  private readonly handleInputModeKeyup = (event: KeyboardEvent): void => {
    if (!event.metaKey && !event.ctrlKey && this.isInputModeActive) {
      this.deactivateInputMode();
    }
  };
  private readonly handleInputModeBlur = (): void => {
    if (this.isInputModeActive) {
      this.deactivateInputMode();
    }
  };
  private edgeCreationState: {
    isCreating: boolean;
    sourceNode: NodeWithMetadata | null;
    temporaryEdge: any;
  } = {
    isCreating: false,
    sourceNode: null,
    temporaryEdge: null
  };

  /**
   * Edge endpoint dragging state for moving edges between nodes
   */
  private edgeDragState: {
    isDragging: boolean;
    edge: EdgeWithMetadata | null;
    endpoint: 'source' | 'target' | null;
    dragMarker: any;
  } = {
    isDragging: false,
    edge: null,
    endpoint: null,
    dragMarker: null
  };

  /**
   * Temporary canvas for text measurement
   */
  private textMeasurementCanvas: HTMLCanvasElement | null = null;

  private get root(): ShadowRoot {
    if (!this.shadowRoot) {
      throw new Error('ShadowRoot not initialized');
    }
    return this.shadowRoot;
  }

  constructor(isInputAllowed: boolean = false) {
    super();

    this.attachShadow({ mode: 'open' });
    this.initializeDOM();
    this.initializeD3();
    this.setupLayoutWarningsToggle();

    // TODO: I'd like to make this better.
    this.lineFunction = d3.line()
      .x((d: any) => d.x)
      .y((d: any) => d.y)
      .curve(d3.curveBasis);

    // Grid line function uses linear interpolation for sharp right-angle turns
    this.gridLineFunction = d3.line()
      .x((d: any) => d.x)
      .y((d: any) => d.y)
      .curve(d3.curveLinear);

    // Initialize input mode keyboard event handlers
    this.inputModeEnabled = isInputAllowed;
    this.initializeInputModeHandlers();
  }

  /**
   * Access the layoutFormat attribute
   */
  private get layoutFormat(): string | null {
    return this.getAttribute('layoutFormat');
  }

  /**
   * Resolves the layoutFormat attribute to a registered routing mode id.
   * Taut is the default: unset, 'default', and 'taut' all resolve to 'taut'.
   * Anything not in the registry warns once and falls back to taut — with a
   * dedicated removal message for 'legacy' (the removed multi-router curved
   * path).
   */
  private get routingMode(): string {
    const format = this.layoutFormat;
    if (!format || format === 'default') return 'taut';
    if (getRoutingMode(format)) return format;
    if (!this.warnedRoutingModeFallbacks.has(format)) {
      this.warnedRoutingModeFallbacks.add(format);
      console.warn(format === 'legacy'
        ? '[spytial] layoutFormat="legacy" has been removed; edges now route as "taut". ' +
          'Remove the layoutFormat attribute (taut is the default) to silence this warning.'
        : `[spytial] Unknown layoutFormat "${format}" — no such routing mode is registered. ` +
          'Falling back to "taut". (Opt-in routers must be imported before they can be selected.)'
      );
    }
    return 'taut';
  }

  /** Warn-once-per-value guard for routing-mode fallbacks (legacy/unknown). */
  private warnedRoutingModeFallbacks = new Set<string>();

  /** True when the resolved routing mode uses the bespoke grid (orthogonal) pipeline. */
  private get useGridPipeline(): boolean {
    return getRoutingMode(this.routingMode)?.pipeline === 'grid';
  }

  /**
   * Router instance for the standard pipeline, cached per registry
   * DEFINITION (not per mode id): re-registering an id replaces its
   * definition object, so upgrade-in-place reaches elements that already
   * routed in that mode on their next pass.
   */
  private activeRouter: SpytialEdgeRouter | null = null;
  private activeRouterDef: RoutingModeDefinition | null = null;

  private get edgeRouter(): SpytialEdgeRouter {
    const def = getRoutingMode(this.routingMode) ?? null;
    if (!this.activeRouter || this.activeRouterDef !== def) {
      this.activeRouter = def?.createRouter?.() ?? new TautRouter();
      this.activeRouterDef = def;
    }
    return this.activeRouter;
  }

  /**
   * The RouterHost handed to pluggable routers: everything Spytial-specific
   * (ports, obstacles, parallel-edge fanning, edge selection) stays behind
   * these callbacks. Built once — every member delegates to `this`, and
   * `computedRoutes` is a stable Map instance — and reused across passes.
   */
  private cachedRouterHost: RouterHost | null = null;

  private routerHost(): RouterHost {
    return this.cachedRouterHost ??= {
      portAttachment: (edge, end) => portAttachment(edge, end),
      obstaclesFor: (edge) => this.buildRouterObstacles(edge),
      obstacles: () => {
        if (!this.routerObstacleCache) this.buildRouterObstacleCache();
        return this.routerObstacleCache!;
      },
      fanParallel: (edge, route, scale) => this.handleMultipleEdgeRouting(edge, route, scale),
      routerEdges: () =>
        ((this.currentLayout?.links ?? []) as any[]).filter(e => this.isRouterEdge(e)),
      routes: this.computedRoutes,
    };
  }

  /**
   * Access transition mode for layout swaps.
   * Supported values:
   * - "morph": exiting elements fade out, entering fade in, continuing slide
   * - "replace": clear and redraw immediately (legacy behavior)
   *
   * Defaults to 'morph' when the attribute is absent.
   */
  private get transitionMode(): WebColaRenderTransitionMode {
    const attrMode = this.getAttribute('transition-mode');
    return attrMode === 'replace' ? 'replace' : 'morph';
  }

  /**
   * Morph speed multiplier (0 – 1).
   *  • 0   = instant (no animation, equivalent to 'replace')
   *  • 0.2 = default (~220 ms exit, ~200 ms enter)
   *  • 1   = slow / dramatic (~1.1 s exit, ~1 s enter)
   *
   * Read from the `morph-speed` attribute; defaults to 0.2.
   */
  private get morphSpeed(): number {
    const raw = parseFloat(this.getAttribute('morph-speed') ?? '');
    if (Number.isFinite(raw)) return Math.max(0, Math.min(raw, 1));
    return 0.2;
  }

  /** Convenience helpers that apply the speed multiplier to base timings. */
  private get morphExitDurationMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_EXIT_DURATION_MS * this.morphSpeed);
  }
  private get morphEnterDurationMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_ENTER_DURATION_MS * this.morphSpeed);
  }
  private get morphEnterDelayMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_ENTER_DELAY_MS * this.morphSpeed);
  }
  /** Duration for the slide animation of continuing nodes (same scale as enter). */
  private get morphSlideDurationMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_ENTER_DURATION_MS * this.morphSpeed);
  }

  /**
   * Determines if an edge is used for alignment purposes.
   * Alignment edges are identified by IDs starting with "_alignment_".
   * Uses cached set for O(1) lookup performance.
   * 
   * @param edge - Edge object to check
   * @returns True if the edge is an alignment constraint edge
   * 
   * @example
   * ```typescript
   * const alignEdge = { id: "_alignment_nodes_1_2" };
   * const isAlign = this.isAlignmentEdge(alignEdge); // returns true
   * ```
   */
  private isAlignmentEdge(edge: { id: string }): boolean {
    // Use cache if available (during routing), otherwise fall back to string check
    if (this.edgeRoutingCache.alignmentEdges.size > 0) {
      return this.edgeRoutingCache.alignmentEdges.has(edge.id);
    }
    return edge.id.startsWith("_alignment_");
  }

  /**
   * Determines if a node is hidden based on naming convention.
   * Hidden nodes are identified by names starting with underscore.
   * 
   * @param node - Node object to check
   * @returns True if the node should be hidden from display
   */
  private isHiddenNode(node: { name?: string; id?: string }): boolean {
    // Check name first, fall back to id if name is not available
    const identifier = node.name || node.id;
    return identifier ? identifier.startsWith("_") : false;
  }

  /**
   * Determines if a node is an error node.
   * @param node - Node object to check
   * @returns True if the node is in the set of conflicting constraints
   */
  private isErrorNode(node: {name: string, id: string}): boolean {
    // Check if this node appears in any constraint that's in conflictingConstraints
    const conflictingNodes = this.currentLayout.conflictingNodes;

    // Check if this node appear in overlapping nodes
    const overlappingNodes = this.currentLayout.overlappingNodes;

    if (conflictingNodes.length > 0 && overlappingNodes.length > 0) {
      const conflictingNodeIds = conflictingNodes.map(n => n.id);
      const overlappingNodeIds = overlappingNodes.map(n => n.id);
      throw new Error(`Layout cannot have both conflictingConstraints (${conflictingNodeIds}) and overlappingNodes ${overlappingNodeIds}`);
    }

    const errorNodes = [...conflictingNodes, ...overlappingNodes];

    return errorNodes.some((errorNode: LayoutNode) => errorNode.id === node.id); // NOTE: `id` should be unique
  }

  /**
   * Determines if a node is hidden-but-shown — hidden by a hideAtom directive that a layout
   * constraint conflicts with, so the counterfactual diagram draws it anyway. These are
   * marked distinctly (dashed outline) so the user can see which atom caused the conflict.
   * @param node - Node object to check
   * @returns True if the node is in the layout's set of re-introduced nodes
   */
  private isReintroducedNode(node: { id: string }): boolean {
    const reintroduced = this.currentLayout?.reintroducedNodes;
    if (!reintroduced || reintroduced.length === 0) return false;
    return reintroduced.some((n: LayoutNode) => n.id === node.id);
  }

  /**
   * Check if a node is considered "small" and needs enhanced visibility
   * Accounts for current zoom level to determine visual size on screen
   * @param node - Node object with dimensions
   * @returns True if the node appears smaller than the threshold on screen
   */
  private isSmallNode(node: any): boolean {
    const minVisualSize = 30; // Minimum visual size threshold in screen pixels
    
    // Get current zoom scale
    let zoomScale = 1;
    if (this.svg && this.svg.node()) {
      try {
        const transform = d3.zoomTransform(this.svg.node());
        zoomScale = transform.k;
      } catch (e) {
        // Fallback to scale 1 if transform is not available
        zoomScale = 1;
      }
    }
    
    // Calculate visual size (coordinate size * zoom scale)
    const visualWidth = ((node as any).visualWidth ?? node.width ?? 0) * zoomScale;
    const visualHeight = ((node as any).visualHeight ?? node.height ?? 0) * zoomScale;
    
    return visualWidth < minVisualSize || visualHeight < minVisualSize;
  }

  /**
   * Update node classes based on current zoom level
   * Called when zoom changes to ensure small error nodes get proper styling
   */
  private updateSmallNodeClasses(): void {
    if (!this.container) return;
    
    // Update all error nodes to check if they should have small-error-node class
    this.container.selectAll('.error-node').each((d: any, i: number, nodes: ArrayLike<SVGGElement>) => {
      const nodeElement = d3.select(nodes[i]);
      const isSmall = this.isSmallNode(d);
      
      if (isSmall) {
        // Add small-error-node class if not present
        if (!nodeElement.classed('small-error-node')) {
          nodeElement.classed('small-error-node', true);
        }
      } else {
        // Remove small-error-node class if present
        if (nodeElement.classed('small-error-node')) {
          nodeElement.classed('small-error-node', false);
        }
      }
    });
  }

  /**
   * Gets the current zoom scale factor
   * @returns Current zoom scale (k value from transform)
   */
  private getCurrentZoomScale(): number {
    if (this.svg && this.svg.node()) {
      try {
        const transform = d3.zoomTransform(this.svg.node());
        return transform.k;
      } catch (e) {
        return 1;
      }
    }
    return 1;
  }

  private isErrorGroup(_group: {name: string}): boolean {
    // Overlapping groups are now supported and no longer treated as errors.
    return false;
  }

  /**
   * Determines if an edge represents an inferred relationship.
   * Inferred edges are identified by specific properties or naming conventions.
   * 
   * @param edge - Edge object to check
   * @returns True if the edge is an inferred relationship
   * 
   * @example
   * ```typescript
   * const inferredEdge = { isInferred: true };
   * const isInferred = this.isInferredEdge(inferredEdge); // returns true
   * ```
   */
  private isInferredEdge(edge: { id?: string }): boolean {
    const helperPrefix = "_inferred_";

    // Check if the edge contains the helper prefix
    return edge.id ? edge.id.includes(helperPrefix) : false;
  }

  /**
   * Determines if a group represents disconnected nodes.
   * Disconnected node groups are identified by names starting with "_d_".
   * 
   * @param group - Group object to check
   * @returns True if the group contains disconnected nodes
   * 
   * @example
   * ```typescript
   * const disconnectedGroup = { name: "_d_isolated_nodes" };
   * const isDisconnected = this.isDisconnectedGroup(disconnectedGroup); // returns true
   * ```
   */
  private isDisconnectedGroup(group: { name: string }): boolean {
    return group.name.startsWith(WebColaCnDGraph.DISCONNECTED_NODE_PREFIX);
  }

  /**
   * Computes adaptive link length based on actual node dimensions, edge labels, and graph density
   */
  private computeAdaptiveLinkLength(nodes: any[], scaleFactor: number, links?: any[]): number {
    if (!nodes || nodes.length === 0) {
      return 150; // fallback
    }

    // Calculate average node dimensions using visual width/height (not inflated collision bounds)
    let totalWidth = 0;
    let totalHeight = 0;
    let validNodes = 0;

    nodes.forEach(node => {
      if (node && !this.isHiddenNode(node)) {
        totalWidth += (node.visualWidth ?? node.width ?? 100);
        totalHeight += (node.visualHeight ?? node.height ?? 60);
        validNodes++;
      }
    });

    if (validNodes === 0) {
      return 150; // fallback
    }

    const avgWidth = totalWidth / validNodes;
    const avgHeight = totalHeight / validNodes;
    const avgNodeSize = Math.max(avgWidth, avgHeight);

    // Calculate maximum edge label width if links are provided
    let maxLabelWidth = 0;
    if (links && links.length > 0) {
      const fontSize = 12; // Default edge label font size
      links.forEach(link => {
        if (link && link.label) {
          const labelWidth = this.measureTextWidth(link.label, fontSize);
          maxLabelWidth = Math.max(maxLabelWidth, labelWidth);
        }
      });
    }

    // Marker size (arrowhead)
    const markerSize = 15; // Width of the marker as defined in SVG defs

    // Base link length should account for:
    // 1. Average node size
    // 2. Edge label text width
    // 3. Marker (arrowhead) size
    // 4. Additional separation buffer
    const baseSeparation = 50; // minimum separation between nodes
    const labelAndMarkerSpace = maxLabelWidth + markerSize + 20; // 20px buffer
    let baseLinkLength = Math.max(avgNodeSize + baseSeparation + labelAndMarkerSpace, 120);

    // Apply density factor - more nodes = slightly tighter spacing to fit better
    const densityFactor = Math.max(0.7, 1 - Math.log10(validNodes) * 0.1);
    baseLinkLength *= densityFactor;

    // Apply scale factor
    const adjustedScaleFactor = scaleFactor / 5;
    const scaledLinkLength = baseLinkLength / adjustedScaleFactor;

    // Ensure reasonable bounds - prevent tiny edges and excessive spacing
    return Math.max(60, Math.min(scaledLinkLength, 350));
  }

  private getScaledDetails(constraints: any[], scaleFactor: number = DEFAULT_SCALE_FACTOR, nodes?: any[], groups?: any[], links?: any[]) {
    const adjustedScaleFactor = scaleFactor / 5;

    // Calculate adaptive group compactness based on graph structure
    let groupCompactness = this.calculateAdaptiveGroupCompactness(groups || [], nodes?.length || 0, adjustedScaleFactor);

    // Use adaptive link length calculation if nodes are available
    let linkLength: number;
    if (nodes && nodes.length > 0) {
      linkLength = this.computeAdaptiveLinkLength(nodes, scaleFactor, links);
    } else {
      // Fallback to original calculation
      const min_sep = 150;
      const default_node_width = 100;
      linkLength = (min_sep + default_node_width) / adjustedScaleFactor;
    }

    /*
    For each constraint, if it is a separation constraint, adjust the distance by the scale factor.
    */
    function getScaledConstraints(constraints: any[]): any[] {
      return constraints.map(constraint => {
        if (constraint.type === "separation" && typeof constraint.gap === "number") {
          const oldgap = constraint.gap;
          const newgap = oldgap / adjustedScaleFactor; // or * scaleFactor, depending on your UI logic
          //console.log(`Scaling constraint gap from ${oldgap} to ${newgap} with scale factor ${adjustedScaleFactor}`);

          return {
            ...constraint,
            gap: newgap
          };
        }
        return constraint;
      });
    }

    return {
      scaledConstraints: getScaledConstraints(constraints),
      linkLength: linkLength,
      groupCompactness: groupCompactness
    }
  }

  /**
   * Calculate adaptive group compactness based on graph characteristics.
   * 
   * This helps reduce jitter in layouts with complex group structures by:
   * - Increasing compactness for deeply nested groups (stronger group boundaries)
   * - Increasing compactness when there are many groups relative to nodes
   * - Using default compactness for simple group structures
   * 
   * Higher compactness values create stronger attraction between group boundaries,
   * which helps groups stabilize faster and reduces oscillation.
   * 
   * @param groups - Array of group definitions
   * @param nodeCount - Total number of nodes in the graph
   * @param scaleFactor - Current scale factor adjustment
   * @returns Calculated group compactness value
   */
  private calculateAdaptiveGroupCompactness(groups: any[], nodeCount: number, scaleFactor: number): number {
    const DEFAULT_GROUP_COMPACTNESS = WebColaCnDGraph.DEFAULT_GROUP_COMPACTNESS * scaleFactor;
    
    if (!groups || groups.length === 0) {
      return DEFAULT_GROUP_COMPACTNESS;
    }
    
    // Calculate maximum nesting depth
    const maxDepth = this.calculateMaxGroupDepth(groups);
    
    // Calculate group-to-node ratio
    const groupRatio = groups.length / Math.max(nodeCount, 1);
    
    // Base compactness
    let compactness = DEFAULT_GROUP_COMPACTNESS;
    
    // Increase compactness for deeply nested groups (depth > 2)
    // Deeper nesting requires stronger group boundaries to prevent jitter
    if (maxDepth > 2) {
      compactness *= 10; // 10x stronger for deeply nested groups
    } else if (maxDepth > 1) {
      compactness *= 5; // 5x stronger for moderately nested groups
    }
    
    // Increase compactness when there are many groups relative to nodes
    // Many groups can create conflicting constraints that cause oscillation
    if (groupRatio > 0.3) {
      compactness *= 2; // Double strength for high group density
    }
    
    return compactness;
  }

  /**
   * Convert screen viewport bounds to layout-coordinate bounds using zoom transform.
   */
  private getViewportBoundsInLayoutSpace(transform: TransformInfo): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | undefined {
    const svgContainer = this.shadowRoot?.querySelector('#svg-container') as HTMLElement | null;
    const viewportWidth = svgContainer?.clientWidth || WebColaCnDGraph.DEFAULT_SVG_WIDTH;
    const viewportHeight = svgContainer?.clientHeight || WebColaCnDGraph.DEFAULT_SVG_HEIGHT;

    if (viewportWidth <= 0 || viewportHeight <= 0) {
      return undefined;
    }

    const k = transform.k;
    if (!Number.isFinite(k) || k === 0) {
      return undefined;
    }

    const tx = Number.isFinite(transform.x) ? transform.x : 0;
    const ty = Number.isFinite(transform.y) ? transform.y : 0;

    return {
      minX: (0 - tx) / k,
      maxX: (viewportWidth - tx) / k,
      minY: (0 - ty) / k,
      maxY: (viewportHeight - ty) / k,
    };
  }

  /**
   * Returns true when a transform can be safely reapplied for viewport continuity.
   */
  private hasValidTransform(transform?: TransformInfo): boolean {
    if (!transform) return false;
    return Number.isFinite(transform.k) && transform.k > 0 &&
      Number.isFinite(transform.x) && Number.isFinite(transform.y);
  }

  /**
   * Applies viewport continuity rules before a new render.
   * Temporal transitions should preserve the prior transform even when
   * there are few/no reusable node positions.
   */
  private applyViewportRenderPolicy(hasPriorPositions: boolean, hasPriorTransform: boolean): void {
    if (hasPriorTransform) {
      // Prevent automatic fit from overriding temporal viewport continuity.
      this.isInitialRender = false;
      this.userHasManuallyZoomed = true;
      return;
    }

    if (!hasPriorPositions) {
      this.isInitialRender = true;
      this.userHasManuallyZoomed = false;
    }
  }

  /**
   * Builds the raw prior state used by sequence policies.
   * For temporal transitions we anchor the transform to the live viewport at
   * render-start time when a previous layout is currently displayed.
   */
  private buildPolicyRawState(options?: WebColaLayoutOptions): LayoutState {
    const liveStateAtChange = this.getLayoutState();
    const priorState = options?.priorPositions ?? liveStateAtChange;
    const hasPolicyContext = !!(options?.policy && options.prevInstance && options.currInstance);
    const hasLiveLayout = !!(this.currentLayout?.nodes && this.currentLayout.nodes.length > 0);

    if (
      hasPolicyContext &&
      hasLiveLayout &&
      this.hasValidTransform(liveStateAtChange.transform)
    ) {
      return {
        positions: priorState.positions,
        transform: liveStateAtChange.transform,
      };
    }

    return priorState;
  }

  /**
   * Calculate the maximum nesting depth of groups.
   * Depth 1 = groups with only leaf nodes
   * Depth 2 = groups containing other groups
   * And so on...
   * 
   * @param groups - Array of group definitions
   * @returns Maximum nesting depth
   */
  private calculateMaxGroupDepth(groups: any[]): number {
    if (!groups || groups.length === 0) return 0;
    
    let maxDepth = 1;
    
    for (const group of groups) {
      if (group.groups && Array.isArray(group.groups) && group.groups.length > 0) {
        // This group contains subgroups
        // Safely map indices to groups, filtering out invalid indices
        const validSubgroups = group.groups
          .filter((idx: number) => typeof idx === 'number' && idx >= 0 && idx < groups.length)
          .map((idx: number) => groups[idx])
          .filter((g: any) => g != null);
        
        if (validSubgroups.length > 0) {
          const subgroupDepth = 1 + this.calculateMaxGroupDepth(validSubgroups);
          maxDepth = Math.max(maxDepth, subgroupDepth);
        }
      }
    }
    
    return maxDepth;
  }

  /**
   * Initialize the Shadow DOM structure
   */
  private initializeDOM(): void {
    this.root.innerHTML = `
      <style>
      ${this.getCSS()}
      </style>
      <div id="graph-shell">
      <div id="graph-toolbar">
        <div id="zoom-controls">
          <button id="zoom-in" title="Zoom In" aria-label="Zoom in">+</button>
          <button id="zoom-out" title="Zoom Out" aria-label="Zoom out">−</button>
          <!-- U+29C8, a square inside a square: the content framed by the view. Not the
               outward arrows or corner brackets, which mean "full screen" everywhere else. -->
          <button id="zoom-fit" title="Fit to View" aria-label="Fit graph to view">&#x29C8;</button>
        </div>
        <div id="routing-control">
          <label for="routing-mode">Routing:</label>
          <!-- Options are built from the registry in updateRoutingModeDropdown(). -->
          <select id="routing-mode" title="Edge routing mode"></select>
        </div>
        <div id="mode-control">
          <label for="theme-mode">Mode:</label>
          <select id="theme-mode" title="Color theme"></select>
        </div>
        <div id="screenshot-control">
          <button id="screenshot-btn" title="Download high-res PNG screenshot" aria-label="Screenshot graph">⬇</button>
        </div>
      </div>
      <div id="svg-container">
      <div id="loading" role="status" aria-live="polite" aria-atomic="true">
        <span class="loading-dot" aria-hidden="true"></span>
        <span id="loading-progress">Computing layout...</span>
      </div>
      <div id="empty-state" hidden></div>
      <div id="layout-warnings" hidden>
        <div id="layout-warnings-bar">
          <button id="layout-warnings-badge" type="button" aria-expanded="false" aria-controls="layout-warnings-panel">
            <span aria-hidden="true">&#9888;</span>
            <span id="layout-warnings-count"></span>
            <span id="layout-warnings-caret" aria-hidden="true">&#9656;</span>
          </button>
          <button id="layout-warnings-dismiss" type="button" title="Dismiss" aria-label="Dismiss spec warnings">&#215;</button>
        </div>
        <div id="layout-warnings-panel" role="region" aria-label="Spec warnings" hidden></div>
      </div>
      <svg id="svg">
        <defs>
        <marker id="end-arrow" markerWidth="12" markerHeight="8" refX="12" refY="4" orient="auto" markerUnits="userSpaceOnUse">
          <polygon points="0 0, 12 4, 0 8, 3 4" fill="context-stroke" />
        </marker>
        <marker id="start-arrow" markerWidth="12" markerHeight="8" refX="2" refY="4" orient="auto" markerUnits="userSpaceOnUse">
          <polygon points="12 0, 0 4, 12 8, 9 4" fill="context-stroke" />
        </marker>
        </defs>
        <g class="zoomable"></g>
      </svg>
      </div>
      </div>
      <div id="error" style="display: none; color: red;"></div>
    `;
  }

  /**
   * Initialize D3 selections and zoom behavior
   */
  private initializeD3(): void {
    this.svg = d3.select(this.root.querySelector('#svg'));
    this.container = this.svg.select('.zoomable');

    if(d3.zoom) {

    // Set up zoom behavior (D3 v4 API - matches your working pattern)
    this.zoomBehavior = d3.zoom()
      .scaleExtent([0.01, 20])
      .on('start', () => {
        // Only mark as user interaction if it's from mouse/touch (not programmatic)
        // d3.event.sourceEvent is null for programmatic zooms
        if (d3.event.sourceEvent) {
          this.userHasManuallyZoomed = true;
        }
      })
      .on('zoom', () => {
        this.container.attr('transform', d3.event.transform);
        // Update zoom control states when zoom changes
        this.updateZoomControlStates();
        // Update small node classes based on new zoom level
        this.updateSmallNodeClasses();
      });

    this.svg.call(this.zoomBehavior);
    
    // Set up zoom control event listeners
    this.initializeZoomControls();
    }
    else {
      console.warn('D3 zoom behavior not available. Ensure D3 v4+ is loaded.');
    }
  }

  /**
   * Initialize zoom control event listeners
   */
  private initializeZoomControls(): void {
    const zoomInButton = this.root.querySelector('#zoom-in') as HTMLButtonElement;
    const zoomOutButton = this.root.querySelector('#zoom-out') as HTMLButtonElement;
    const zoomFitButton = this.root.querySelector('#zoom-fit') as HTMLButtonElement;

    if (zoomInButton) {
      zoomInButton.addEventListener('click', () => {
        this.userHasManuallyZoomed = true; // Mark as user interaction
        this.zoomIn();
      });
    }

    if (zoomOutButton) {
      zoomOutButton.addEventListener('click', () => {
        this.userHasManuallyZoomed = true; // Mark as user interaction
        this.zoomOut();
      });
    }
    
    if (zoomFitButton) {
      zoomFitButton.addEventListener('click', () => {
        this.resetViewToFitContent();
      });
    }

    // Set up routing mode dropdown
    const routingModeSelect = this.root.querySelector('#routing-mode') as HTMLSelectElement;
    if (routingModeSelect) {
      // Fills the options and sets the initial value from the attribute
      // (unset/'default' resolve to taut).
      this.updateRoutingModeDropdown();

      routingModeSelect.addEventListener('change', () => {
        this.handleRoutingModeChange(routingModeSelect.value);
      });
    }

    // Set up theme (Mode) dropdown — populate from the registry, sync to active.
    const themeModeSelect = this.root.querySelector('#theme-mode') as HTMLSelectElement;
    if (themeModeSelect) {
      this.updateThemeDropdown();
      themeModeSelect.addEventListener('change', () => {
        this.setTheme(themeModeSelect.value);
        this.dispatchEvent(new CustomEvent('theme-changed', {
          detail: { theme: themeModeSelect.value },
          bubbles: true
        }));
      });
    }

    // Set up screenshot button
    const screenshotButton = this.root.querySelector('#screenshot-btn') as HTMLButtonElement;
    if (screenshotButton) {
      screenshotButton.addEventListener('click', () => {
        this.takeScreenshot();
      });
    }

    // Initial state update
    this.updateZoomControlStates();
  }

  /**
   * Handle routing mode change from dropdown
   */
  private handleRoutingModeChange(mode: string): void {
    // Update the layoutFormat attribute
    this.setAttribute('layoutFormat', mode);
    
    // Trigger re-routing if layout is already rendered
    if (this.currentLayout && this.colaLayout) {
      if (this.useGridPipeline) {
        // Apply grid routing
        this.gridify(10, 25, 10);
      } else {
        // Apply standard-pipeline routing
        this.routeEdges();
      }
      
      // Dispatch event for external listeners
      this.dispatchEvent(new CustomEvent('routing-mode-changed', {
        detail: { mode }
      }));
    }
  }

  /**
   * Rebuild the routing dropdown from the registry and select the current
   * layoutFormat. Options are built with DOM APIs, never interpolated into
   * markup: ids and labels come from registerRoutingMode(), which consumers
   * call with values of their own choosing.
   *
   * Runs after every layout, so a router that registers late (the libavoid
   * entry loads its WASM asynchronously) appears in the list without a
   * re-render, and a replaced mode picks up its new label.
   */
  private updateRoutingModeDropdown(): void {
    const select = this.root?.querySelector('#routing-mode') as HTMLSelectElement | null;
    if (!select) return;
    const modes = listRoutingModes();
    const signature = JSON.stringify(modes.map(m => [m.id, m.label]));
    if (select.getAttribute('data-modes') !== signature) {
      select.textContent = '';
      for (const mode of modes) {
        const option = document.createElement('option');
        option.value = mode.id;
        option.textContent = mode.label;
        select.appendChild(option);
      }
      select.setAttribute('data-modes', signature);
    }
    select.value = this.routingMode;
  }

  /**
   * Initialize keyboard event handlers for input mode activation
   */
  private initializeInputModeHandlers(): void {
    if (this.inputModeEnabled) {
      this.attachInputModeListeners();
    }
  }

  private attachInputModeListeners(): void {
    if (this.inputModeListenersAttached) {
      return;
    }
    document.addEventListener('keydown', this.handleInputModeKeydown);
    document.addEventListener('keyup', this.handleInputModeKeyup);
    window.addEventListener('blur', this.handleInputModeBlur);
    this.inputModeListenersAttached = true;
  }

  private detachInputModeListeners(): void {
    if (!this.inputModeListenersAttached) {
      return;
    }
    document.removeEventListener('keydown', this.handleInputModeKeydown);
    document.removeEventListener('keyup', this.handleInputModeKeyup);
    window.removeEventListener('blur', this.handleInputModeBlur);
    this.inputModeListenersAttached = false;
  }

  /**
   * Activate input mode for edge creation and modification
   */
  private activateInputMode(): void {
    this.isInputModeActive = true;
    
    // Add input-mode class to SVG for styling
    if (this.svg) {
      this.svg.classed('input-mode', true);
    }

    // Disable node dragging and zoom/translate
    this.disableNodeDragging();
    this.disableZoom();

    // Update edge endpoint markers visibility
    this.updateEdgeEndpointMarkers();

    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('input-mode-activated', {
      detail: { active: true }
    }));
  }

  /**
   * Deactivate input mode and restore normal behavior
   */
  private deactivateInputMode(): void {
    this.isInputModeActive = false;
    
    // Remove input-mode class from SVG
    if (this.svg) {
      this.svg.classed('input-mode', false);
    }

    // Clean up any temporary edge creation state
    this.cleanupEdgeCreation();

    // Re-enable node dragging and zoom/translate
    this.enableNodeDragging();
    this.enableZoom();

    // Update edge endpoint markers visibility
    this.updateEdgeEndpointMarkers();

    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('input-mode-deactivated', {
      detail: { active: false }
    }));
  }

  /**
   * Disable node dragging when in input mode
   */
  private disableNodeDragging(): void {
    if (this.svgNodes && this.colaLayout) {
      this.svgNodes.on('.drag', null);
    }
  }

  /**
   * Re-enable node dragging when exiting input mode
   */
  private enableNodeDragging(): void {
    if (this.svgNodes && this.colaLayout && this.colaLayout.drag) {
      const nodeDrag = this.colaLayout.drag();
      this.setupNodeDragHandlers(nodeDrag);
      this.svgNodes.call(nodeDrag);
    }
  }

  /**
   * Disable zoom/translate functionality when in input mode
   */
  private disableZoom(): void {
    if (this.svg && this.zoomBehavior) {
      // Store current transform before disabling
      this.storedTransform = d3.zoomTransform(this.svg.node());
      // Disable zoom events but preserve the behavior
      this.svg.on('.zoom', null);
    }
  }

  /**
   * Re-enable zoom/translate functionality when exiting input mode
   */
  private enableZoom(): void {
    if (this.svg && this.zoomBehavior) {
      // Re-enable zoom behavior
      this.svg.call(this.zoomBehavior);
      // Restore the previous transform if we had one
      if (this.storedTransform) {
        this.svg.call(this.zoomBehavior.transform, this.storedTransform);
      }
    }
  }

  /**
   * Zoom in by a fixed scale factor
   */
  private zoomIn(): void {
    if (this.svg && this.zoomBehavior) {
      this.svg.transition().duration(200).call(
        this.zoomBehavior.scaleBy, 1.5
      );
    }
  }

  /**
   * Zoom out by a fixed scale factor
   */
  private zoomOut(): void {
    if (this.svg && this.zoomBehavior) {
      this.svg.transition().duration(200).call(
        this.zoomBehavior.scaleBy, 1 / 1.5
      );
    }
  }

  /**
   * Update zoom control button states based on current zoom level
   */
  private updateZoomControlStates(): void {
    if (!this.svg || !this.zoomBehavior) return;

    const currentTransform = d3.zoomTransform(this.svg.node());
    const currentScale = currentTransform.k;
    const [minScale, maxScale] = this.zoomBehavior.scaleExtent();

    const zoomInButton = this.root.querySelector('#zoom-in') as HTMLButtonElement;
    const zoomOutButton = this.root.querySelector('#zoom-out') as HTMLButtonElement;

    if (zoomInButton) {
      zoomInButton.disabled = currentScale >= maxScale;
    }
    
    if (zoomOutButton) {
      zoomOutButton.disabled = currentScale <= minScale;
    }
  }

  /**
   * Clean up temporary edge creation state
   */
  private cleanupEdgeCreation(): void {
    // Remove temporary edge if it exists
    if (this.edgeCreationState.temporaryEdge) {
      this.edgeCreationState.temporaryEdge.remove();
    }

    // Reset edge creation state
    this.edgeCreationState = {
      isCreating: false,
      sourceNode: null,
      temporaryEdge: null
    };
  }

  /**
   * Setup drag handlers for nodes
   */
  private setupNodeDragHandlers(nodeDrag: any): void {
    nodeDrag
      .on('start.cnd', (d: any) => {
        // Mark that user has interacted with the layout - prevents auto-fitting
        this.userHasManuallyZoomed = true;
        
        const start = { x: d.x, y: d.y };
        this.dragStartPositions.set(d.id, start);
        this.dispatchEvent(
          new CustomEvent('node-drag-start', {
            detail: { id: d.id, position: start }
          })
        );
      })
      .on('end.cnd', (d: any) => {
        const start = this.dragStartPositions.get(d.id);
        this.dragStartPositions.delete(d.id);
        const detail = {
          id: d.id,
          previous: start,
          current: { x: d.x, y: d.y }
        };
        this.dispatchEvent(new CustomEvent('node-drag-end', { detail }));
      });
  }

  /**
   * Start edge creation from a source node
   */
  private startEdgeCreation(sourceNode: NodeWithMetadata): void {
    if (!this.isInputModeActive) return;

    // Clean up any existing edge creation
    this.cleanupEdgeCreation();

    // Set edge creation state
    this.edgeCreationState.isCreating = true;
    this.edgeCreationState.sourceNode = sourceNode;

    // Create temporary edge line
    this.edgeCreationState.temporaryEdge = this.container
      .append('line')
      .attr('class', 'temporary-edge')
      .attr('x1', sourceNode.x)
      .attr('y1', sourceNode.y)
      .attr('x2', sourceNode.x)
      .attr('y2', sourceNode.y)
      .attr('stroke', '#007bff')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .attr('opacity', 0.7);

    // Add mousemove listener for temporary edge visualization
    this.svg.on('mousemove.edgecreation', () => {
      if (this.edgeCreationState.isCreating && this.edgeCreationState.temporaryEdge) {
        const [mouseX, mouseY] = d3.mouse(this.container.node());
        this.edgeCreationState.temporaryEdge
          .attr('x2', mouseX)
          .attr('y2', mouseY);
      }
    });
  }

  /**
   * Finish edge creation by connecting to a target node
   */
  private async finishEdgeCreation(targetNode: NodeWithMetadata): Promise<void> {
    if (!this.isInputModeActive || !this.edgeCreationState.isCreating || !this.edgeCreationState.sourceNode) {
      return;
    }

    const sourceNode = this.edgeCreationState.sourceNode;

    // Confirm self-loop edges
    if (sourceNode.id === targetNode.id) {
      const confirmSelfLoop = await this.showConfirmDialog(
        `Are you sure you want to create a self-loop edge on "${sourceNode.label || sourceNode.id}"?`
      );
      if (!confirmSelfLoop) {
        this.cleanupEdgeCreation();
        return;
      }
    }

    // Clean up temporary edge visualization
    this.svg.on('mousemove.edgecreation', null);

    // Show edge label input dialog
    await this.showEdgeLabelInput(sourceNode, targetNode);
  }

  /**
   * Show edge label input dialog and create the edge
   */
  private async showEdgeLabelInput(sourceNode: NodeWithMetadata, targetNode: NodeWithMetadata): Promise<void> {
    const label = await this.showPromptDialog(
      `Enter label for edge from "${sourceNode.label || sourceNode.id}" to "${targetNode.label || targetNode.id}":`,
      ''
    );
    
    if (label !== null) { // User didn't cancel
      await this.createNewEdge(sourceNode, targetNode, label || '');
    }

    // Clean up edge creation state
    this.cleanupEdgeCreation();
  }

  /**
   * Create a new edge between two nodes
   */
  private async createNewEdge(sourceNode: NodeWithMetadata, targetNode: NodeWithMetadata, label: string): Promise<void> {
    if (!this.currentLayout) return;

    // Find node indices in the current layout
    const sourceIndex = this.currentLayout.nodes.findIndex(node => node.id === sourceNode.id);
    const targetIndex = this.currentLayout.nodes.findIndex(node => node.id === targetNode.id);

    if (sourceIndex === -1 || targetIndex === -1) {
      console.error('Could not find node indices for edge creation');
      return;
    }

    // Generate unique edge ID
    const edgeId = `edge_${sourceNode.id}_${targetNode.id}_${Date.now()}`;

    // Create new edge object.
    //
    // Store the NODE OBJECTS, not the indices. WebCola swaps indices for nodes
    // only inside Layout.start(), and this edge joins a layout that has already
    // started — rerenderGraph() deliberately never calls start() again. An index
    // written here would therefore never be resolved, and every reader
    // (routing, rendering, hit-testing) would treat the number as a node.
    // `currentLayout.nodes` is the same array WebCola resolved against, so these
    // are the identical objects it would have installed.
    const newEdge: EdgeWithMetadata = {
      id: edgeId,
      source: this.currentLayout.nodes[sourceIndex],
      target: this.currentLayout.nodes[targetIndex],
      label: label,
      relName: label,
      color: '#333',
      isUserCreated: true
    };

    // Add edge to current layout
    this.currentLayout.links.push(newEdge);

    // Update external state with the new edge
    await this.updateExternalStateForNewEdge(sourceNode, targetNode, label);

    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('edge-created', {
      detail: { 
        edge: newEdge,
        sourceNode: sourceNode,
        targetNode: targetNode
      }
    }));

    // Re-render the graph to show the new edge
    this.rerenderGraph();
  }

  /**
   * Update external state for a new edge through the external state management system
   * @param sourceNode - Source node of the edge
   * @param targetNode - Target node of the edge 
   * @param relationName - Name/label of the relation
   */
  private async updateExternalStateForNewEdge(sourceNode: NodeWithMetadata, targetNode: NodeWithMetadata, relationName: string): Promise<void> {
    if (!relationName.trim()) {
      return;
    }

    try {
      // Create a tuple representing the edge/relation
      const tuple: ITuple = {
        atoms: [sourceNode.id, targetNode.id],
        // Provisional: a layout node knows only its most specific type, and this class
        // has no data instance to check it against. Whichever data instance receives
        // the tuple settles its column types against the relation's declared signature
        // before storing it (see settleTupleTypes), so these values are never what
        // lands in a relation. A listener keeping its OWN store must settle them.
        types: [sourceNode.mostSpecificType || 'untyped', targetNode.mostSpecificType || 'untyped']
      };

      console.log(`Dispatching edge creation request: ${relationName}(${sourceNode.id}, ${targetNode.id})`);
      
      // Dispatch edge creation event for React components to handle
      const edgeCreationEvent = new CustomEvent('edge-creation-requested', {
        detail: {
          relationId: relationName,
          sourceNodeId: sourceNode.id,
          targetNodeId: targetNode.id,
          tuple: tuple
        },
        bubbles: true
      });
      this.dispatchEvent(edgeCreationEvent);
    } catch (error) {
      console.error('Failed to update external state for new edge:', error);
    }
  }

  /**
   * Whether opposite-direction tuples with the same label collapse into one
   * double-headed edge. True for the read-only base; the editable
   * StructuredInputGraph overrides this to false so each direction stays an
   * independent, individually-editable arrow.
   */
  protected shouldCollapseSymmetricEdges(): boolean {
    return true;
  }

  /**
   * Re-render the graph with current layout data.
   *
   * Applies current node positions immediately without restarting the
   * solver.  When a StructuredInputGraph event handler is active, its
   * enforceConstraintsAndRegenerate() will do a full layout solve
   * afterward via renderLayout().  Restarting the old solver here would
   * leave rAF tick callbacks that race with the new solver and corrupt
   * positions / cause visual flicker.
   */
  protected rerenderGraph(): void {
    if (!this.currentLayout || !this.colaLayout) return;

    // Update links in the layout
    this.colaLayout.links(this.currentLayout.links);

    // Re-render links
    this.container.selectAll('.link-group').remove();
    this.renderLinks(this.currentLayout.links, this.colaLayout);

    // Position elements at their current coordinates so the user gets
    // immediate visual feedback.  Do NOT call this.colaLayout.start() —
    // that schedules rAF tick callbacks on the OLD solver which race
    // with the new solver that renderLayout() creates moments later.
    this.updatePositions();
  }

  /**
   * Edit the label of an existing edge
   */
  private async editEdgeLabel(edgeData: EdgeWithMetadata): Promise<void> {
    if (!this.isInputModeActive) return;

    // Use relName for data-instance lookups; fall back to label for display.
    const currentRelName = edgeData.relName || edgeData.label || '';
    const displayLabel = edgeData.label || edgeData.relName || '';
    const result = await this.showEdgeEditDialog(`Edit edge label:`, displayLabel);

    // Handle deletion request
    if (result === 'DELETE') {
      await this.deleteEdge(edgeData);
      return;
    }

    // Handle label change
    if (result !== null && result !== displayLabel) {
      const newLabel = result;

      // Get source and target nodes for data instance update
      const sourceNode = this.getNodeFromEdge(edgeData, 'source');
      const targetNode = this.getNodeFromEdge(edgeData, 'target');

      // Update external state using relation name (not display label)
      await this.updateExternalStateForEdgeModification(sourceNode, targetNode, currentRelName, newLabel);

      // Update edge data
      edgeData.label = newLabel;
      edgeData.relName = newLabel;

      // Dispatch event for external listeners
      this.dispatchEvent(new CustomEvent('edge-modified', {
        detail: { 
          edge: edgeData,
          oldLabel: displayLabel,
          newLabel: newLabel
        }
      }));

      // Re-render to show updated label
      this.rerenderGraph();
    }
  }

  /**
   * Get node from edge data based on source or target
   * @param edgeData - Edge data
   * @param position - 'source' or 'target'
   * @returns Node data or null
   */
  private getNodeFromEdge(edgeData: EdgeWithMetadata, position: 'source' | 'target'): NodeWithMetadata | null {
    if (!this.currentLayout) return null;
    
    // Endpoints are indices before WebCola's first tick and node objects after,
    // so accept either. See EdgeWithMetadata in webcolatranslator.
    const endpoint = edgeData[position] as NodeWithMetadata | number;
    const nodeIndex = typeof endpoint === 'number' ? endpoint : endpoint?.index;
    if (nodeIndex === undefined) return null;
    return this.currentLayout.nodes[nodeIndex] || null;
  }

  /**
   * Update external state for an edge modification through the external state management system
   * @param sourceNode - Source node of the edge
   * @param targetNode - Target node of the edge 
   * @param oldRelationName - Old relation name/label
   * @param newRelationName - New relation name/label
   */
  private async updateExternalStateForEdgeModification(
    sourceNode: NodeWithMetadata | null, 
    targetNode: NodeWithMetadata | null, 
    oldRelationName: string, 
    newRelationName: string
  ): Promise<void> {
    if (!sourceNode || !targetNode) {
      return;
    }

    try {
      // Create tuple for the relation
      const tuple: ITuple = {
        atoms: [sourceNode.id, targetNode.id],
        // Provisional: a layout node knows only its most specific type, and this class
        // has no data instance to check it against. Whichever data instance receives
        // the tuple settles its column types against the relation's declared signature
        // before storing it (see settleTupleTypes), so these values are never what
        // lands in a relation. A listener keeping its OWN store must settle them.
        types: [sourceNode.mostSpecificType || 'untyped', targetNode.mostSpecificType || 'untyped']
      };

      console.log(`Dispatching edge modification request: ${oldRelationName} -> ${newRelationName}`);

      // Dispatch edge modification event for React components to handle
      const edgeModificationEvent = new CustomEvent('edge-modification-requested', {
        detail: {
          oldRelationId: oldRelationName,
          newRelationId: newRelationName,
          sourceNodeId: sourceNode.id,
          targetNodeId: targetNode.id,
          tuple: tuple
        },
        bubbles: true
      });
      this.dispatchEvent(edgeModificationEvent);
    } catch (error) {
      console.error('Failed to update external state for edge modification:', error);
    }
  }

  /**
   * Resolve the effective transition mode for a given render call.
   *
   * Priority order:
   * 1. Explicit `options.transitionMode`
   * 2. Element-level `transition-mode` attribute
   * 3. Auto-detect: if a sequence policy is provided, default to 'morph'; else 'replace'
   */
  private resolveTransitionMode(options?: WebColaLayoutOptions): WebColaRenderTransitionMode {
    if (options?.transitionMode === 'replace') return 'replace';
    if (options?.transitionMode === 'morph') return 'morph';

    const attrMode = this.getAttribute('transition-mode');
    if (attrMode === 'replace') return 'replace';
    if (attrMode === 'morph') return 'morph';

    // Auto: morph when a sequence policy is provided, replace otherwise.
    return options?.policy ? 'morph' : 'replace';
  }

  /**
   * Render layout using WebCola constraint solver.
   *
   * @param instanceLayout - The layout instance to render
   * @param options - Optional; pass a `policy` with `prevInstance` / `currInstance`
   *   for sequence continuity.  Omit entirely for a fresh layout.
   *
   * @example
   * ```typescript
   * // Fresh
   * await graph.renderLayout(layout);
   *
   * // Policy-driven (graph captures its own state)
   * await graph.renderLayout(nextLayout, {
   *   policy: stability,
   *   prevInstance: prev,
   *   currInstance: curr,
   * });
   *
   * // Policy-driven with explicit prior positions
   * await graph.renderLayout(nextLayout, {
   *   policy: stability,
   *   prevInstance: prev,
   *   currInstance: curr,
   *   priorPositions: graph.getLayoutState(),
   * });
   * ```
   */
  public async renderLayout(instanceLayout: InstanceLayout, options?: WebColaLayoutOptions): Promise<void> {

    if (! isInstanceLayout(instanceLayout)) {
      throw new Error('Invalid instance layout provided. Expected an InstanceLayout instance.');
    }

    // Tear down any in-flight render FIRST — before any state is read or
    // mutated for the new one. Without this, the old solver's d3 timer keeps
    // ticking against the component while the new render replaces
    // currentLayout/svgNodes underneath it — its stale tick and end closures
    // then race the new render's (the wedged-overlay / never-routed-edges /
    // never-revealed-morph failures).
    this.teardownInflightRender();

    // Surface the advisory warnings this layout carries. Done before the solve
    // rather than after because the badge describes the *spec*, not the geometry,
    // so it stays accurate even if this render is later superseded or fails —
    // but strictly after teardown, which must observe the prior render's state
    // untouched.
    this.renderLayoutWarnings(instanceLayout.warnings ?? []);

    // Claim the render generation. A superseded render can be torn down at
    // entry only if its solver already exists — a competitor arriving while
    // this render is still awaiting translation has nothing to stop yet, so
    // each render also re-checks its claim after the await (and in its
    // tick/end closures) and abandons itself once superseded.
    const generation = ++this.renderGeneration;

    const transitionMode = this.resolveTransitionMode(options);
    const shouldMorph = transitionMode === 'morph';
    const shouldShowLoadingOverlay = transitionMode === 'replace';

    // ── Resolve effective prior state via policy ────────────────────────
    // If a policy + instance pair is provided, run the policy to decide
    // what prior positions (if any) reach the solver.
    let resolvedState: LayoutState | undefined;
    let useReducedIterations = false;

    if (options?.policy && options.prevInstance && options.currInstance) {
      const rawState = this.buildPolicyRawState(options);
      const viewportBounds = this.getViewportBoundsInLayoutSpace(rawState.transform);
      const result = options.policy.apply({
        priorState: rawState,
        prevInstance: options.prevInstance,
        currInstance: options.currInstance,
        spec: { constraints: { orientation: { relative: [], cyclic: [] }, alignment: [], grouping: { groups: [], subgroups: [] } }, directives: { sizes: [], hiddenAtoms: [], icons: [], projections: [], edgeStyles: [] } } as any,
        viewportBounds,
      });
      resolvedState = result.effectivePriorState;
      useReducedIterations = result.useReducedIterations;
      // Capture by reference only — production users who never call
      // getLastSeedState pay nothing. The policy's effectivePriorState
      // is a freshly constructed object, so retaining the reference is
      // safe; the deep-clone for immutability happens lazily in
      // getLastSeedState below.
      this.lastSeedState = resolvedState ?? null;
    } else if (options?.priorPositions) {
      // Direct prior-positions path (no policy): warm-start the solver from the
      // caller-supplied positions and use reduced iterations so existing nodes
      // barely drift. This makes incremental re-renders (e.g. StructuredInputGraph
      // adding an atom) feel stable — nodes stay where the user left them while
      // the solver handles the new element and re-satisfies constraints.
      resolvedState = options.priorPositions;
      useReducedIterations = true;
    } else if (shouldMorph && this.currentLayout?.nodes?.length) {
      // Default continuity path: when callers re-render without explicitly passing
      // prior state, warm-start from the current on-screen layout so transitions
      // can morph instead of popping to a fresh solve.
      const liveState = this.getLayoutState();
      if (liveState.positions.length > 0) {
        resolvedState = liveState;
        useReducedIterations = true;
      }
    }

    const hasPriorPositions = !!(resolvedState && resolvedState.positions.length > 0);
    const hasPriorTransform = this.hasValidTransform(resolvedState?.transform);
    // Build the options the translator will see.
    // lockUnconstrainedNodes is gated on useReducedIterations so that only
    // stability-mode layouts lock nodes; morph transitions keep nodes free.
    const translatorOptions: WebColaLayoutOptions = hasPriorPositions
      ? {
          priorPositions: resolvedState,
          lockUnconstrainedNodes: useReducedIterations,
          collapseSymmetricEdges: this.shouldCollapseSymmetricEdges()
        }
      : { collapseSymmetricEdges: this.shouldCollapseSymmetricEdges() };

    this.applyViewportRenderPolicy(hasPriorPositions, hasPriorTransform);
    
    // Handle zoom transform based on whether we have prior transform to restore
    if (this.svg && this.zoomBehavior && d3) {
      try {
        if (hasPriorTransform) {
          // Restore the prior transform to maintain visual continuity
          const priorTransform = resolvedState!.transform;
          const transform = d3.zoomIdentity
            .translate(priorTransform.x, priorTransform.y)
            .scale(priorTransform.k);
          this.svg.call(this.zoomBehavior.transform, transform);
        } else if (!hasPriorPositions) {
          // Reset zoom transform to identity for a fresh start (will be adjusted by fitViewportToContent)
          const identity = d3.zoomIdentity;
          this.svg.call(this.zoomBehavior.transform, identity);
        }
      } catch (error) {
        console.warn('Failed to set zoom transform:', error);
      }
    }

    try {

      // Ensure D3 and container are properly initialized
      if (!this.container || !this.svg) {

        this.initializeD3();
      }
      
      // Double-check that container is now available
      if (!this.container) {
        throw new Error('Failed to initialize D3 container. SVG elements may not be available.');
      }

      if (shouldShowLoadingOverlay) {
        this.showLoading();
        this.updateLoadingProgress('Translating layout...');
      } else {
        // Ensure any stale loading state from a prior render is fully cleared.
        this.hideLoading();
      }

      // Get actual container dimensions for responsive layout
      const svgContainer = this.root.querySelector('#svg-container') as HTMLElement;
      const containerRect = svgContainer.getBoundingClientRect();
      const containerWidth = containerRect.width || 800; // fallback to default
      const containerHeight = containerRect.height || 600; // fallback to default

      // Translate to WebCola format with actual container dimensions
      const translator = new WebColaTranslator();
      const webcolaLayout = await translator.translate(instanceLayout, containerWidth, containerHeight, translatorOptions);

      // Superseded while awaiting translation? The newer render owns the
      // element now — abandon before creating a solver it can't tear down.
      if (generation !== this.renderGeneration) return;

      // Nothing to draw. Handled here, before a solver exists, because WebCola
      // cannot be handed an empty node set at all — see renderEmptyLayout.
      if (webcolaLayout.nodes.length === 0) {
        this.renderEmptyLayout(instanceLayout, webcolaLayout);
        return;
      }

      if (shouldShowLoadingOverlay) {
        this.updateLoadingProgress(`Computing layout for ${webcolaLayout.nodes.length} nodes...`);
      }

      // Adaptive iteration counts based on graph size for better performance
      // For small graphs, use default values. For large graphs, reduce iterations.
      const nodeCount = webcolaLayout.nodes.length;
      let unconstrainedIters = WebColaCnDGraph.INITIAL_UNCONSTRAINED_ITERATIONS;
      let userConstraintIters = WebColaCnDGraph.INITIAL_USER_CONSTRAINT_ITERATIONS;
      let allConstraintIters = WebColaCnDGraph.INITIAL_ALL_CONSTRAINTS_ITERATIONS;
      
      // When prior state is provided and the policy requests reduced iterations,
      // minimize solver iterations to preserve node positions.
      // WebCola's unconstrained phase allows nodes to move freely from their initial positions,
      // so minimizing this phase helps preserve the provided positions.
      //
      // The policy controls this via useReducedIterations — a policy can return
      // positions while requesting normal iterations (useReducedIterations: false)
      // to let the solver fully re-converge.
      if (hasPriorPositions && useReducedIterations) {
        // Use minimal iterations to preserve prior positions:
        // - 0 unconstrained: don't let nodes drift from prior positions
        // - 10 user constraint: apply position constraints quickly
        // - 20 all constraints: final constraint satisfaction with overlap avoidance
        unconstrainedIters = 0;
        userConstraintIters = Math.min(10, userConstraintIters);
        allConstraintIters = Math.min(20, allConstraintIters);
        
      }
      
      if (nodeCount > 100) {
        // For large graphs (>100 nodes), reduce iterations more aggressively
        unconstrainedIters = Math.max(hasPriorPositions ? 0 : 5, Math.floor(unconstrainedIters * 0.5));
        userConstraintIters = Math.max(25, Math.floor(userConstraintIters * 0.5));
        allConstraintIters = Math.max(100, Math.floor(allConstraintIters * 0.5));
      } else if (nodeCount > 50) {
        // For medium graphs (>50 nodes), reduce iterations moderately
        unconstrainedIters = Math.max(hasPriorPositions ? 0 : 8, Math.floor(unconstrainedIters * 0.8));
        userConstraintIters = Math.max(40, Math.floor(userConstraintIters * 0.8));
        allConstraintIters = Math.max(150, Math.floor(allConstraintIters * 0.75));
      }


      // Get scaled constraints, link length, and adaptive group compactness
      const { scaledConstraints, linkLength, groupCompactness } = this.getScaledDetails(
        webcolaLayout.constraints, 
        DEFAULT_SCALE_FACTOR, 
        webcolaLayout.nodes,
        webcolaLayout.groups,
        webcolaLayout.links
      );

      if (shouldShowLoadingOverlay) {
        this.updateLoadingProgress('Applying constraints and initializing...');
      }

      // Use a higher convergence threshold when prior state exists.
      // This allows the layout to converge faster, preserving prior positions better.
      const convergenceThreshold = hasPriorPositions ? 0.1 : 1e-3;

      // Create WebCola layout using d3adaptor
      const layout: D3Layout = requireCola().d3adaptor(d3)
        .linkDistance(linkLength)
        .convergenceThreshold(convergenceThreshold)
        .avoidOverlaps(true)
        .handleDisconnected(true)
        .nodes(webcolaLayout.nodes)
        .links(webcolaLayout.links)
        .constraints(scaledConstraints)
        .groups(webcolaLayout.groups)
        .groupCompactness(groupCompactness)
        .size([webcolaLayout.FIG_WIDTH, webcolaLayout.FIG_HEIGHT]);

      // ── Morph transition: element-level enter / exit / continue ──────
      // Instead of cross-fading the entire graph (which makes continuing
      // elements blink), we diff the old and new layouts by ID and:
      //   • Exiting elements  → clone to a snapshot layer, fade out in place
      //   • Continuing elements → stay visible; slide from old pos to new pos
      //   • Entering elements  → created normally, then faded in from opacity 0
      if (shouldMorph && this.currentLayout?.nodes?.length) {
        // Capture old positions BEFORE anything is cleared — needed for the
        // slide animation that runs after the solver converges.
        this.morphOldPositions = new Map();
        for (const n of this.currentLayout.nodes) {
          if (n.x != null && n.y != null) {
            this.morphOldPositions.set((n as any).id, { x: n.x, y: n.y });
          }
        }
        this.applyMorphExitSnapshot(webcolaLayout);
      } else {
        this.morphOldPositions = null;
      }

      // Store current layout
      this.currentLayout = webcolaLayout;
      this.colaLayout = layout;

      // For morph transitions: snapshot the entire old graph as a visual
      // overlay BEFORE clearing.  The overlay stays visible while the
      // solver runs behind the scenes, then gets removed when the morph
      // animation starts.
      if (shouldMorph && this.container) {
        this.snapshotOldGraph();
      }

      // Clear the active layer (snapshot layer is separate and keeps visible)
      this.container.selectAll('*').remove();

      this.renderGroups(webcolaLayout.groups, layout);
      this.renderLinks(webcolaLayout.links, layout);
      this.renderNodes(webcolaLayout.nodes, layout);

      // Hide the new elements while the solver runs — the old-graph
      // snapshot keeps the display stable.
      if (shouldMorph && this.container) {
        this.container.attr('opacity', 0);
      }

      // Track iteration progress
      let tickCount = 0;
      const totalIterations = unconstrainedIters + userConstraintIters + allConstraintIters;
      // Suppress intermediate position rendering during initial layout solve.
      // Once the solver converges (end handler), this flag is cleared so that
      // subsequent tick events (e.g. from drag → layout.resume()) still render.
      let isInitialSolve = true;

      // Start the layout with specific iteration counts and proper event handling
      layout
        .on('tick', () => {
          // Superseded render: a newer renderLayout owns the element. The
          // entry teardown detaches these handlers when the solver already
          // exists, but a solver started after the competitor's teardown ran
          // (i.e. this render was past its await) only dies via this check.
          if (generation !== this.renderGeneration) return;
          // A tick queued before clear()/dispose() can land after the
          // selections were nulled — bail out instead of crashing.
          if (!this.currentLayout || !this.svgNodes) return;

          if (isInitialSolve) {
            tickCount++;
            if (tickCount % 20 === 0) {
              const progress = Math.min(95, Math.round((tickCount / totalIterations) * 100));
              if (shouldShowLoadingOverlay) {
                this.updateLoadingProgress(`Computing layout... ${progress}%`);
              }
            }
            // Skip DOM updates during initial solve — the end handler
            // applies final positions once.
            return;
          }

          // Drag-triggered ticks: render position updates normally.
          // The grid pipeline uses the orthogonal updater; every other mode
          // uses the standard one.
          if (this.useGridPipeline) {
            this.gridUpdatePositions();
          } else {
            this.updatePositions();
          }
        })
        .on('end', () => {
          // Superseded render — same checkpoint as the tick handler above.
          if (generation !== this.renderGeneration) return;
          // Teardown race: bail if clear()/dispose() nulled the selections.
          if (!this.currentLayout || !this.svgNodes) return;

          isInitialSolve = false;
          if (shouldShowLoadingOverlay) {
            this.updateLoadingProgress('Finalizing...');
          }

          if (shouldMorph && this.morphOldPositions && this.morphOldPositions.size > 0) {
            // Unhide the real container — solver has converged, nodes
            // are at final positions.
            if (this.container) {
              this.container.attr('opacity', 1);
            }

            // Remove the old-graph overlay and start exit animations.
            this.startMorphExitAnimation();

            // Apply final positions to the real DOM, then hide entering
            // elements before the slide starts.
            if (this.useGridPipeline) {
              this.gridUpdatePositions();
            } else {
              this.updatePositions();
            }
            this.hideEnteringElements();

            // Animate continuing nodes from old → new positions.
            this.animateMorphSlide();
          } else {
            // Non-morph: unhide container, apply final positions, route.
            if (this.container) {
              this.container.attr('opacity', 1);
            }
            if (this.useGridPipeline) {
              this.gridUpdatePositions();
              this.gridify(10, 25, 10);
            } else {
              this.updatePositions();
              this.routeEdges();
            }
          }

          // Dispatch relations-available event after layout is complete
          this.dispatchRelationsAvailableEvent();

          // Dispatch layout-complete event with final node positions
          // This is useful for capturing positions for temporal consistency
          this.dispatchEvent(new CustomEvent('layout-complete', {
            detail: {
              nodePositions: this.getNodePositions()
            }
          }));

          // Update routing dropdown to match current layout format
          this.updateRoutingModeDropdown();

          if (shouldShowLoadingOverlay) {
            this.hideLoading();
          }
        });

      // Start the layout with error handling for D3/WebCola compatibility issues.
      // keepRunning=false: start() runs every constraint phase synchronously and
      // would then hand the remaining alpha-decay ticks — and the 'end' dispatch
      // that triggers the ONLY paint of the initial solve — to a d3.timer.
      // d3.timer is requestAnimationFrame-driven, and rAF never fires in a
      // hidden/occluded tab or embedded pane, so the solve converges without
      // ever painting. We drive those remaining ticks synchronously below
      // instead; interaction-driven solves (drag → resume()) still use cola's
      // own timer, which is fine because the user is present for those.
      try {
        layout.start(
          unconstrainedIters,
          userConstraintIters,
          allConstraintIters,
          WebColaCnDGraph.GRID_SNAP_ITERATIONS,
          false
        );
      } catch (layoutError) {
        try {
          layout.start(0, 0, 0, 0, false);
        } catch (fallbackError) {
          // Rethrown so the outer catch reports it the same way as any other
          // render failure — one message, on screen and on the event.
          throw new Error(`WebCola layout failed to start: ${(fallbackError as Error).message}`);
        }
        // Reported only now that the retry has succeeded, because only now is
        // it true that a diagram gets drawn. The retry runs zero iterations of
        // every phase, so it is drawn at its seed positions and never actually
        // solved — which looks like a layout rather than like a failure. This
        // was a console.warn the host had no way to see.
        this.emitLayoutError({
          message: 'The layout solver could not start. The diagram is drawn, but its positions are unsolved.',
          phase: 'solver',
          fatal: false,
          cause: layoutError,
        });
      }

      // Drive the remaining ticks to convergence synchronously. Each tick()
      // dispatches through the handlers above (progress only, while
      // isInitialSolve suppresses DOM updates) and the converging tick
      // dispatches 'end', which performs the initial paint — so the paint can
      // never be starved of animation frames. tick() is typed protected,
      // hence the cast.
      let syncTicks = 0;
      while (!(layout as any).tick() && ++syncTicks < WebColaCnDGraph.MAX_SYNC_DECAY_TICKS);
      if (syncTicks >= WebColaCnDGraph.MAX_SYNC_DECAY_TICKS) {
        // Stress plateaued above the convergence threshold — force alpha to 0
        // so this last tick takes the convergence branch and dispatches 'end'.
        layout.stop();
        (layout as any).tick();
      }

    } catch (error) {
      this.showError(
        `Layout rendering failed: ${(error as Error).message}`,
        { phase: 'render', cause: error }
      );
    }
  }

  /**
   * Draw a layout that has no nodes: clear the canvas and say why it is empty.
   *
   * An empty layout is ordinary input, not a failure. The instance may have no
   * atoms (an empty or unsatisfiable model, or a datum the host failed to
   * parse), or every atom may be hidden. Either way there is nothing to solve.
   *
   * It has to be caught before the solver, because WebCola cannot take an empty
   * node set: `Layout.start` reads `this._nodes[0].width` to size its grid-snap
   * phase and throws. The catch there retried with `start(0, 0, 0, 0, false)`,
   * which converged and fired `end`, and the paint that followed reached for
   * selections this render never created. Integrators saw that cascade as three
   * console messages ending in "Layout rendering failed: this.svgGroups is
   * undefined" — which says nothing about the empty instance behind it.
   */
  private renderEmptyLayout(instanceLayout: InstanceLayout, webcolaLayout: WebColaLayout): void {
    this.currentLayout = webcolaLayout;
    this.colaLayout = null as any;
    this.morphOldPositions = null;

    this.container.selectAll('*').remove();
    // A superseded morph can leave the container hidden; nothing would ever
    // unhide it on this path.
    this.container.attr('opacity', 1);

    // Bind empty selections rather than nulling. Public methods read these
    // between renders — highlightRelation and clearHighlightRelation guard on
    // `currentLayout.links`, which is a truthy empty array here, and then use
    // the selection — so null is a crash where an empty selection is a no-op.
    // The container was just emptied, so each of these selects nothing.
    this.svgNodes = this.container.selectAll('.node');
    this.svgLinkGroups = this.container.selectAll('.link-group');
    this.svgGroups = this.container.selectAll('.group');
    this.svgGroupLabels = this.container.selectAll('.groupLabel');
    this.svgGroupLabelBgs = this.container.selectAll('.groupLabelBg');

    this.hideLoading();
    // Only the hideAtom case can be named with confidence: `hiddenAtoms`
    // records atoms removed by a hideAtom *selector* and nothing else, so the
    // legacy hideDisconnected / hideDisconnectedBuiltIns flags leave it empty.
    // Blaming the datum there would be the same misdirection this note exists
    // to replace, so the general message names both possibilities instead.
    this.showEmptyState(
      instanceLayout.hiddenAtoms && instanceLayout.hiddenAtoms.length > 0
        ? 'Nothing to draw: every atom in this instance is hidden.'
        : 'Nothing to draw: no atoms reached this diagram — the instance is empty, or the spec hides them all.'
    );

    // Hosts wait on these to know the render finished. An empty diagram is a
    // finished render, so they still fire.
    this.dispatchRelationsAvailableEvent();
    this.dispatchEvent(new CustomEvent('layout-complete', {
      detail: { nodePositions: [] }
    }));
    this.updateRoutingModeDropdown();
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  Morph transition helpers
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * IDs that were present in the previous layout but absent from the current
   * one.  Populated by `applyMorphExitSnapshot` and consumed by
   * `applyMorphEnterTransition` so it knows what counts as "entering".
   */
  private morphEnteringNodeIds: Set<string> = new Set();
  private morphEnteringEdgeIds: Set<string> = new Set();

  /**
   * Old positions of nodes captured before the morph, keyed by node ID.
   * Used by `animateMorphSlide` to slide continuing nodes from their
   * previous position to their new solver-converged position.
   */
  private morphOldPositions: Map<string, { x: number; y: number }> | null = null;

  /** Active d3.timer for the slide animation — kept so `clear()` can stop it. */
  private morphSlideTimer: any = null;

  /**
   * Monotonic render claim. Each renderLayout() call takes the next value;
   * a render whose value no longer matches has been superseded and must
   * abandon itself at its next checkpoint (after awaits, in tick/end
   * closures) instead of mutating state the newer render now owns.
   */
  private renderGeneration = 0;

  /**
   * Create a snapshot layer containing **only** the elements that are being
   * removed (exiting) between the old and new layouts, then fade them out.
   *
   * Elements that continue in the new layout are NOT included in the
   * snapshot — they stay fully visible in the active container and slide
   * to their new positions via the solver.
   */

  /**
   * Clone the entire current graph (`.zoomable` container) into a
   * `.morph-old-graph` overlay.  This dumb visual copy stays on screen
   * while the solver runs behind the scenes so the user sees the old
   * graph until the morph animation starts.
   */
  private snapshotOldGraph(): void {
    if (!this.svg || !this.container) return;

    // Remove any prior snapshot.
    this.svg.selectAll('.morph-old-graph').interrupt().remove();

    const containerNode = this.container.node() as SVGGElement | null;
    if (!containerNode) return;

    const clone = containerNode.cloneNode(true) as SVGGElement;
    clone.setAttribute('class', 'morph-old-graph');
    clone.style.pointerEvents = 'none';

    const svgNode = this.svg.node() as SVGSVGElement | null;
    if (svgNode) {
      svgNode.appendChild(clone);
    }
  }

  private applyMorphExitSnapshot(newLayout: WebColaLayout): void {
    if (!this.svg || !this.container) return;

    // Remove any leftover exit layer from a prior morph.
    this.svg.selectAll('.morph-exit-layer').interrupt().remove();

    const oldNodes = this.currentLayout?.nodes || [];
    const oldLinks = (this.currentLayout as any)?.links || [];
    const newNodeIds = new Set(newLayout.nodes.map((n: any) => n.id));
    const newEdgeIds = new Set(newLayout.links.map((e: any) => e.id));
    const oldNodeIds = new Set(oldNodes.map((n: any) => n.id));
    const oldEdgeIds = new Set(oldLinks.map((e: any) => e.id));

    // Compute entering IDs (used later by applyMorphEnterTransition)
    this.morphEnteringNodeIds = new Set(
      [...newNodeIds].filter(id => !oldNodeIds.has(id))
    );
    this.morphEnteringEdgeIds = new Set(
      [...newEdgeIds].filter(id => !oldEdgeIds.has(id))
    );

    // Compute exiting IDs
    const exitingNodeIds = new Set(
      [...oldNodeIds].filter(id => !newNodeIds.has(id))
    );
    const exitingEdgeIds = new Set(
      [...oldEdgeIds].filter(id => !newEdgeIds.has(id))
    );

    // Nothing to fade out → skip
    if (exitingNodeIds.size === 0 && exitingEdgeIds.size === 0) return;

    const svgNode = this.svg.node() as SVGSVGElement | null;
    if (!svgNode) return;

    // Build a lightweight snapshot group containing only the exiting elements.
    const snapshotGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    snapshotGroup.setAttribute('class', 'morph-exit-layer');
    snapshotGroup.style.pointerEvents = 'none';

    // Preserve the current zoom/pan transform on the snapshot so it aligns
    // visually with the container.
    const containerTransform = this.container.attr('transform');
    if (containerTransform) {
      snapshotGroup.setAttribute('transform', containerTransform);
    }

    // Clone exiting nodes
    if (this.svgNodes) {
      this.svgNodes.each(function(this: SVGGElement, d: any) {
        if (exitingNodeIds.has(d.id)) {
          snapshotGroup.appendChild(this.cloneNode(true));
        }
      });
    }

    // Clone exiting edges (edge exits if its own ID is gone, OR if either
    // endpoint node is gone — because a dangling edge is meaningless).
    if (this.svgLinkGroups) {
      this.svgLinkGroups.each(function(this: SVGGElement, d: any) {
        const edgeId: string = d.id;
        const srcId: string = d.source?.id ?? '';
        const tgtId: string = d.target?.id ?? '';
        if (
          exitingEdgeIds.has(edgeId) ||
          exitingNodeIds.has(srcId) ||
          exitingNodeIds.has(tgtId)
        ) {
          snapshotGroup.appendChild(this.cloneNode(true));
        }
      });
    }

    // Clone exiting group rects
    if (this.svgGroups) {
      this.svgGroups.each(function(this: SVGRectElement, d: any) {
        if (d.id && !newLayout.groups.some((g: any) => g.id === d.id)) {
          snapshotGroup.appendChild(this.cloneNode(true));
        }
      });
    }

    if (snapshotGroup.childElementCount === 0) return;

    svgNode.appendChild(snapshotGroup);

    // Don't animate yet — the snapshot stays visible at full opacity
    // while the solver runs so the user still sees the old graph.
    // Call `startMorphExitAnimation()` from the layout end handler
    // once the solver has converged.
  }

  /**
   * Begin the exit animation on the morph-exit-layer snapshot.
   * Called from the layout 'end' handler after the solver converges,
   * so the old graph remains visible during the solve phase.
   */
  private startMorphExitAnimation(): void {
    if (!this.svg) return;

    // Remove the full old-graph snapshot immediately — the real
    // container is now unhidden and positioned at final coordinates.
    this.svg.selectAll('.morph-old-graph').remove();

    const snapshotSel = this.svg.select('.morph-exit-layer');
    if (snapshotSel.empty()) return;

    const exitDuration = this.morphExitDurationMs;

    // Animate exiting edge paths with stroke-dashoffset (draw-out effect).
    snapshotSel.selectAll('g.link-group, g.inferredLinkGroup, g.alignmentLinkGroup')
      .each(function(this: SVGGElement) {
        const pathEl = this.querySelector('path[data-link-id]') as SVGPathElement | null;
        if (!pathEl) return;
        const totalLength = pathEl.getTotalLength();
        if (!totalLength || totalLength <= 0) return;

        pathEl.setAttribute('stroke-dasharray', String(totalLength));
        pathEl.setAttribute('stroke-dashoffset', '0');

        d3.select(this).selectAll('.linklabel, .arrowhead').attr('opacity', 0);

        d3.select(pathEl)
          .transition()
          .duration(exitDuration)
          .ease(d3.easeCubicIn)
          .attr('stroke-dashoffset', totalLength);
      });

    // Fade out exiting nodes and groups normally.
    snapshotSel.selectAll('g.node, rect.group')
      .transition()
      .duration(exitDuration)
      .ease(d3.easeCubicOut)
      .attr('opacity', 0);

    // Remove the snapshot layer once the transition finishes.
    snapshotSel
      .transition()
      .duration(exitDuration)
      .on('end', function(this: SVGGElement) {
        d3.select(this).remove();
      });
  }

  /**
   * Fade in only the **entering** elements (nodes/edges that did not exist
   * in the previous layout).  Continuing elements stay at full opacity so
   * there is no visual blink — they simply slide to their new position.
   */

  /**
   * Immediately hide all entering nodes and edges (opacity 0) so they are
   * invisible during the morph slide.  `applyMorphEnterTransition` is
   * called later to reveal them with animation.
   */
  private hideEnteringElements(): void {
    const enterNodeIds = this.morphEnteringNodeIds;
    const enterEdgeIds = this.morphEnteringEdgeIds;
    if (enterNodeIds.size === 0 && enterEdgeIds.size === 0) return;

    if (this.svgNodes && enterNodeIds.size > 0) {
      this.svgNodes
        .filter((d: any) => enterNodeIds.has(d.id))
        .attr('opacity', 0);
    }

    if (this.svgLinkGroups && (enterEdgeIds.size > 0 || enterNodeIds.size > 0)) {
      this.svgLinkGroups
        .filter((d: any) => {
          if (enterEdgeIds.has(d.id)) return true;
          const srcId: string = d.source?.id ?? '';
          const tgtId: string = d.target?.id ?? '';
          return enterNodeIds.has(srcId) || enterNodeIds.has(tgtId);
        })
        .attr('opacity', 0);
    }
  }

  private applyMorphEnterTransition(): void {
    const enterNodeIds = this.morphEnteringNodeIds;
    const enterEdgeIds = this.morphEnteringEdgeIds;

    // If there is no diff info (first render, or replace mode), treat
    // everything as entering for a gentle fade-in.
    const hasEntryInfo = enterNodeIds.size > 0 || enterEdgeIds.size > 0;

    if (!hasEntryInfo && this.morphEnteringNodeIds.size === 0 && this.morphEnteringEdgeIds.size === 0) {
      // First render with morph — nothing was "old" so nothing enters;
      // just show everything immediately.
      return;
    }

    // Fade in entering nodes
    if (this.svgNodes && enterNodeIds.size > 0) {
      this.svgNodes
        .filter((d: any) => enterNodeIds.has(d.id))
        .attr('opacity', 0)
        .transition()
        .delay(this.morphEnterDelayMs)
        .duration(this.morphEnterDurationMs)
        .ease(d3.easeCubicOut)
        .attr('opacity', 1);
    }

    // Draw in entering edges — an edge enters if its own ID is new, OR
    // if either endpoint is an entering node.
    // Uses stroke-dashoffset animation so the path visually "draws" from
    // the continuing endpoint toward the entering node.
    if (this.svgLinkGroups && (enterEdgeIds.size > 0 || enterNodeIds.size > 0)) {
      this.svgLinkGroups
        .filter((d: any) => {
          if (enterEdgeIds.has(d.id)) return true;
          const srcId: string = d.source?.id ?? '';
          const tgtId: string = d.target?.id ?? '';
          return enterNodeIds.has(srcId) || enterNodeIds.has(tgtId);
        })
        .each(function(this: SVGGElement) {
          const pathEl = this.querySelector('path[data-link-id]') as SVGPathElement | null;
          if (!pathEl) return;
          const totalLength = pathEl.getTotalLength();
          if (!totalLength || totalLength <= 0) return;

          d3.select(pathEl)
            .attr('stroke-dasharray', totalLength)
            .attr('stroke-dashoffset', totalLength);

          // Also hide the link label and arrowheads during draw-in
          d3.select(this).selectAll('.linklabel, .arrowhead')
            .attr('opacity', 0);
        })
        .attr('opacity', 1) // edge group visible — dash hides the stroke
        .transition()
        .delay(this.morphEnterDelayMs)
        .duration(this.morphEnterDurationMs)
        .ease(d3.easeCubicOut)
        .tween('draw-in', function(this: SVGGElement) {
          const pathEl = this.querySelector('path[data-link-id]') as SVGPathElement | null;
          if (!pathEl) return () => {};
          const totalLength = pathEl.getTotalLength();
          if (!totalLength || totalLength <= 0) return () => {};
          const interpolate = d3.interpolateNumber(totalLength, 0);
          return (t: number) => {
            pathEl.setAttribute('stroke-dashoffset', String(interpolate(t)));
          };
        })
        .on('end', function(this: SVGGElement) {
          // Clean up dash attributes and show labels/arrows
          const pathEl = this.querySelector('path[data-link-id]');
          if (pathEl) {
            pathEl.removeAttribute('stroke-dasharray');
            pathEl.removeAttribute('stroke-dashoffset');
          }
          d3.select(this).selectAll('.linklabel, .arrowhead')
            .attr('opacity', 1);
        });
    }

    // Fade in entering groups
    if (this.svgGroups && this.svgGroupLabels) {
      // Groups whose ID was not in the old layout (approximation: if morphEnteringNodeIds
      // contains ANY leaf of the group, the group is visually new).
      // For simplicity we let groups appear instantly — they're background rects
      // and the visual weight is in the nodes/edges.
    }

    // Clear the diff sets so they don't leak into the next render.
    this.morphEnteringNodeIds = new Set();
    this.morphEnteringEdgeIds = new Set();
  }

  /**
   * Animate continuing nodes from their old positions to their new
   * solver-converged positions.
   *
   * Called in the layout 'end' handler after the solver has written final
   * coordinates into `d.x / d.y`.  We:
   *  1. Capture each continuing node's final position.
   *  2. Override `d.x / d.y` to the OLD position and call `updatePositions()`
   *     so the node visually starts where it used to be.
   *  3. Run a `d3.timer` that interpolates `d.x / d.y` from old → final and
   *     calls `updatePositions()` every frame, producing a smooth slide.
   *  4. When done, restore exact final positions and re-run `routeEdges()`
   *     for clean edge paths.
   */
  private animateMorphSlide(): void {
    const oldPositions = this.morphOldPositions;
    if (!oldPositions || oldPositions.size === 0 || !this.currentLayout?.nodes) {
      this.morphOldPositions = null;
      return;
    }

    const nodes = this.currentLayout.nodes as any[];
    const duration = this.morphSlideDurationMs;

    // If speed is effectively 0, skip animation entirely.
    if (duration <= 0) {
      this.morphOldPositions = null;
      return;
    }

    // Identify continuing nodes (present in both old and new)
    const continuingNodes: Array<{
      node: any;
      oldX: number; oldY: number;
      finalX: number; finalY: number;
    }> = [];

    for (const node of nodes) {
      const old = oldPositions.get(node.id);
      if (old) {
        continuingNodes.push({
          node,
          oldX: old.x, oldY: old.y,
          finalX: node.x, finalY: node.y,
        });
      }
    }

    if (continuingNodes.length === 0) {
      this.morphOldPositions = null;
      return;
    }

    // Snap continuing nodes back to their OLD positions for the first frame.
    for (const c of continuingNodes) {
      c.node.x = c.oldX;
      c.node.y = c.oldY;
    }
    this.updatePositions();

    // Cubic ease-out for a natural deceleration feel.
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const t0 = performance.now();

    // Stop any previous slide timer (e.g. if the user navigates quickly).
    if (this.morphSlideTimer) {
      this.morphSlideTimer.stop();
      this.morphSlideTimer = null;
    }

    this.morphSlideTimer = d3.timer(() => {
      const elapsed = performance.now() - t0;
      const progress = Math.min(1, elapsed / duration);
      const eased = ease(progress);

      for (const c of continuingNodes) {
        c.node.x = c.oldX + (c.finalX - c.oldX) * eased;
        c.node.y = c.oldY + (c.finalY - c.oldY) * eased;
      }

      this.updatePositions();

      if (progress >= 1) {
        this.morphSlideTimer.stop();
        this.morphSlideTimer = null;

        // Ensure exact final positions.
        for (const c of continuingNodes) {
          c.node.x = c.finalX;
          c.node.y = c.finalY;
        }
        this.updatePositions();

        // Re-run advanced edge routing now that nodes are at final positions.
        // The grid pipeline re-routes via gridify elsewhere; every other mode
        // re-routes here.
        if (!this.useGridPipeline) {
          this.routeEdges();
        }

        // Now that the slide is done, reveal entering nodes/edges.
        this.applyMorphEnterTransition();

        this.morphOldPositions = null;
      }
    });
  }

  /**
   * Configure how subsequent render calls transition between frames.
   */
  public setTransitionMode(mode: WebColaRenderTransitionMode): void {
    this.setAttribute('transition-mode', mode);
  }

  /**
   * Set the morph animation speed.
   * @param speed 0 (instant) – 1 (full duration, default)
   */
  public setMorphSpeed(speed: number): void {
    this.setAttribute('morph-speed', String(Math.max(0, Math.min(speed, 1))));
  }

  /**
   * Tears down everything still running from a previous render: the WebCola
   * solve (whose d3 timer would otherwise keep firing stale tick/end closures
   * against state the next render replaces), the morph slide timer, any
   * lingering morph-exit snapshot layer, and the entering-element bookkeeping.
   *
   * Called at the top of renderLayout() — so overlapping renders can't race —
   * and from clear()/dispose(), which add their own state nulling on top.
   * Safe to call at any point in the component lifecycle, including before
   * the first render (every step is guarded).
   */
  private teardownInflightRender(): void {
    if (this.colaLayout) {
      try {
        (this.colaLayout as any).stop?.();
      } catch {
        // Solver may already be stopped; nothing to clean up.
      }
      // stop() doesn't cancel an already-queued tick, so detach the handlers
      // or a stale tick/end would fire against the next render's state.
      (this.colaLayout as any).on?.('tick', null);
      (this.colaLayout as any).on?.('end', null);
    }
    if (this.morphSlideTimer) {
      this.morphSlideTimer.stop();
      this.morphSlideTimer = null;
    }
    // A superseded morph can leave its old-graph snapshot fading on screen.
    // Morph renders replace it themselves, but a 'replace' render would not —
    // remove it here so it can't linger above the new graph.
    if (this.svg) {
      this.svg.selectAll('.morph-exit-layer').interrupt().remove();
    }
    this.morphEnteringNodeIds = new Set();
    this.morphEnteringEdgeIds = new Set();

    // Hide any loading overlay the superseded render raised. A 'replace' render
    // shows it and relies on its own 'end' handler to hide it — but that handler
    // was just detached (or will bail on the generation guard), and a morph
    // render that supersedes it never touches the overlay. Without this the
    // overlay wedges at its last message ("Finalizing…") forever.
    this.hideLoading();

    // Same reasoning for the empty-diagram note and the error box: the render
    // that raised them is over, so neither may sit on top of what is drawn next.
    this.hideEmptyState();
    this.hideError();
  }

  /**
   * Clear the current graph visualization and reset internal state.
   * This is useful when switching between temporal states to ensure a clean slate.
   */
  public clear(): void {
    // Stop the solver, morph timers/layers, and stale handlers.
    this.teardownInflightRender();

    // Clear the SVG container
    if (this.container) {
      this.container.selectAll('*').remove();
    }
    this.morphOldPositions = null;

    // Reset internal state
    this.currentLayout = null as any;
    this.colaLayout = null as any;
    this.svgNodes = null;
    this.svgGroups = null;

    // Clear caches
    this.edgeRoutingCache.edgesBetweenNodes.clear();
    this.edgeRoutingCache.alignmentEdges.clear();
    this.dragStartPositions.clear();
    this.hideLoading();
  }

  /**
   * Get the current positions of all nodes in the layout.
   * Useful for reading coordinates after rendering or drag events.
   * 
   * Note: These positions are in the layout coordinate space, not screen space.
   * For preserving visual continuity across renders, use `getLayoutState()` instead.
   */
  public getNodePositions(): Array<{ id: string; x: number; y: number }> {
    if (!this.currentLayout?.nodes) {
      return [];
    }
    return this.currentLayout.nodes.map((n: any) => ({
      id: n.id,
      x: n.x,
      y: n.y
    }));
  }

  /**
   * Get the current zoom/pan transform state.
   * 
   * @returns The current transform with k (scale), x (translateX), and y (translateY)
   */
  public getCurrentTransform(): TransformInfo {
    if (this.svg && this.svg.node()) {
      try {
        const transform = d3.zoomTransform(this.svg.node());
        return {
          k: transform.k,
          x: transform.x,
          y: transform.y
        };
      } catch (e) {
        // Return identity transform if we can't get the current one
        return { k: 1, x: 0, y: 0 };
      }
    }
    return { k: 1, x: 0, y: 0 };
  }

  /**
   * Get the complete layout state for preservation across renders.
   *
   * When using the policy-driven `renderLayout` API, you generally don't
   * need to call this — the graph captures its own state automatically.
   * Use this only when you need explicit control over prior positions.
   *
   * @returns Complete layout state including positions and transform
   *
   * @example
   * ```typescript
   * // Explicit capture (optional — graph auto-captures when policy is used)
   * const state = graph.getLayoutState();
   * await graph.renderLayout(newLayout, {
   *   policy: stability,
   *   prevInstance: prev,
   *   currInstance: curr,
   *   priorPositions: state,
   * });
   * ```
   */
  public getLayoutState(): LayoutState {
    return {
      positions: this.getNodePositions(),
      transform: this.getCurrentTransform()
    };
  }

  /**
   * Return the seed positions the most-recent policy produced before
   * the solver ran, or `null` if no policy was applied during the last
   * render.
   *
   * Used by the appropriateness-experiment harness to score the policy
   * seed against the prior frame independently of solver output. The
   * gap between seed-scored and output-scored metrics attributes the
   * effect: small gap = policy is the lever; large gap = solver
   * dominated.
   *
   * The stored value is captured by reference during rendering (zero
   * production cost — callers who never invoke this method pay
   * nothing). The deep-clone happens here so the returned snapshot is
   * immutable from the consumer's perspective.
   */
  public getLastSeedState(): LayoutState | null {
    if (!this.lastSeedState) return null;
    return {
      positions: this.lastSeedState.positions.map(p => ({ ...p })),
      transform: { ...this.lastSeedState.transform },
    };
  }

  /**
   * Add a control element to the graph toolbar
   * @param element - The HTML element to add to the toolbar
   */
  public addToolbarControl(element: any): void {
    const toolbar = this.shadowRoot?.querySelector('#graph-toolbar');
    if (toolbar) {
      toolbar.appendChild(element);
    }
  }

  /**
   * Get the graph toolbar element for more advanced control manipulation
   * @returns The toolbar element or null if not found
   */
  public getToolbar(): any {
    return this.shadowRoot?.querySelector('#graph-toolbar') || null;
  }

  /**
   * Render groups using D3 data binding
   */
  private renderGroups(groups: any[], layout: D3Layout): void {
    const nodes = this.currentLayout?.nodes ?? [];

    // A group draws around its member nodes, so groups without nodes cannot be
    // drawn. Drop them — but still bind, because every later paint reads
    // this.svgGroups and returning early would leave it undefined. That was the
    // first step of the "this.svgGroups is undefined" failure integrators saw
    // on an empty instance.
    if (nodes.length === 0 && groups.length > 0) {
      console.warn("Cannot render groups: nodes not available");
      groups = [];
    }

    this.svgGroups = this.setupGroups(groups, nodes, layout);
  }

  /**
   * Renders link elements with proper grouping, styling, and labels.
   * Creates link groups for ALL edges, including alignment edges (which are rendered
   * fully transparent so WebCola can use them as layout constraints without them
   * being visually present). Non-alignment edges receive normal styling and labels.
   * 
   * @param links - Array of edge objects to render
   * @param layout - WebCola layout instance (unused but maintained for API consistency)
   * @returns D3 selection of created link groups
   * 
   * @example
   * ```typescript
   * const linkSelection = this.setupLinks(this.currentLayout.links, layout);
   * ```
   */
  private setupLinks(
    links: Array<EdgeWithMetadata>,
    _layout: D3Layout
  ) {
    // Create link groups for each edge
    const linkGroups = this.container
      .selectAll(".link-group")
      .data(links)
      .enter()
      .append("g")
      .attr("class", "link-group");

    // Add path elements to each link group with dynamic styling
    this.setupLinkPaths(linkGroups);

    // Add labels to non-alignment links
    this.setupLinkLabels(linkGroups);

    // Add draggable endpoint markers for input mode
    this.setupEdgeEndpointMarkers(linkGroups);

    return linkGroups;
  }

  /**
   * Creates path elements for links with appropriate CSS classes and styling.
   * Assigns different classes based on edge type for targeted styling.
   * 
   * @param linkGroups - D3 selection of link group elements
   */
  private setupLinkPaths(
    linkGroups: d3.Selection<SVGGElement, any, any, unknown>
  ): void {
    linkGroups
      .append("path")
      .attr("class", (d: any) => {
        if (this.isAlignmentEdge(d)) return "alignmentLink";
        if (this.isInferredEdge(d)) return "inferredLink";
        return "link";
      })
      .attr("data-link-id", (d: any) => d.id || "")
      // A genuinely chosen edge color is preserved; the implicit "black"/empty
      // default is themed in dark mode so the edge and its (context-stroke)
      // arrowhead stay visible on the dark canvas. Light mode is unchanged.
      .attr("stroke", (d: any) => this.edgeStrokeColor(d))
      .attr("fill", "none")
      .attr("opacity", (d: any) => this.isAlignmentEdge(d) ? 0 : null)
      // Use .style() for stroke-width so inline styles override CSS class rules (.link, .inferredLink)
      .style("stroke-width", (d: any) => this.isAlignmentEdge(d) ? "0" : d.weight != null ? `${d.weight}px` : null)
      .attr("stroke-dasharray", (d: any) => this.isAlignmentEdge(d) ? null : this.getEdgeDasharray(d.style))
      .attr("marker-end", (d: any) => {
        if (this.isAlignmentEdge(d)) return "none";
        return "url(#end-arrow)";
      })
      .attr("marker-start", (d: any) => {
        // Add marker-start for bidirectional edges
        if (this.isAlignmentEdge(d) || !d.bidirectional) return "none";
        return  "url(#start-arrow)";
      })
      .on('click.inputmode', (d: any) => {
        if (this.isInputModeActive && !this.isAlignmentEdge(d)) {
          d3.event.stopPropagation();
          // Handle async operation without blocking the event
          this.editEdgeLabel(d).catch(error => {
            console.error('Error editing edge label:', error);
          });
        }
      })
      .style('cursor', () => {
        return this.isInputModeActive ? 'pointer' : 'default';
      });

    // Highlight underlay: a wider, translucent path drawn beneath the main edge.
    // Only created for edges that actually set `highlight`, so non-highlighted
    // edges pay zero extra cost. Inserted as the first child of the link-group
    // so SVG painter's order renders it underneath the main edge.
    linkGroups
      .filter((d: any) => !!d.highlight && !this.isAlignmentEdge(d))
      .insert("path", ":first-child")
      .attr("class", "highlight-underlay")
      .attr("stroke", (d: any) => d.highlight)
      .attr("fill", "none")
      .attr("stroke-linecap", "round")
      .style("stroke-width", (d: any) => {
        const base = d.weight != null ? d.weight : 2;
        return `${base + 8}px`;
      })
      .attr("opacity", 0.45)
      .style("pointer-events", "none");
  }

  /** Edge-label font size from its textStyle tier, or null → CSS default (.linklabel). */
  private edgeLabelFontSize(d: any): string | null {
    return d?.textStyle?.size ? `${resolveAttrFontSize(d.textStyle.size)}px` : null;
  }

  /** Edge-label fill from its textStyle color, or null → CSS default (.linklabel). */
  private edgeLabelFill(d: any): string | null {
    return d?.textStyle?.color ?? null;
  }

  private getEdgeDasharray(style?: string): string | null {
    if (!style) {
      return null;
    }

    switch (style.toLowerCase()) {
      case 'dotted':
        return '1,4';
      case 'dashed':
        return '6,4';
      case 'solid':
        return null;
      default:
        return null;
    }
  }

  /**
   * Adds text labels to link groups for non-alignment edges.
   * Labels are positioned at the midpoint of each link path.
   * Respects the showLabel property - if explicitly set to false, no label is rendered.
   * Inferred edges always show their labels.
   * 
   * @param linkGroups - D3 selection of link group elements
   */
  private setupLinkLabels(
    linkGroups: d3.Selection<SVGGElement, any, any, unknown>
  ): void {
    linkGroups
      .filter((d: any) => !this.isAlignmentEdge(d) && (this.isInferredEdge(d) || d.showLabel !== false))
      .append("text")
      .attr("class", "linklabel")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-family", this.getFontFamily())
      // Edge-label styling from the edge's textStyle block. Use .style() (not
      // .attr) so the inline value overrides the .linklabel CSS rule — a
      // presentation attribute would lose to it. null → the CSS default applies.
      .style("font-size", (d: any) => this.edgeLabelFontSize(d))
      .style("fill", (d: any) => this.edgeLabelFill(d))
      .attr("pointer-events", "none")
      .text((d: any) => d.label || d.relName || "");
  }

  /**
   * Adds draggable endpoint markers to edges for input mode.
   * These markers allow users to drag edge endpoints to reconnect edges.
   * 
   * @param linkGroups - D3 selection of link group elements
   */
  private setupEdgeEndpointMarkers(
    linkGroups: d3.Selection<SVGGElement, any, any, unknown>
  ): void {
    const self = this;

    // Build one draggable endpoint handle. The handle's SHAPE — not just its
    // color — encodes the endpoint's role, so it reads under color-vision
    // deficiency and never collides with the app's primary-action blue:
    //   • directed edge  → hollow ring at the source (origin), filled diamond
    //                       at the target (matches the arrowhead it sits on);
    //   • symmetric edge → both ends are filled diamonds, because a
    //                       bidirectional edge has no privileged direction.
    // Each handle is tinted with its own edge's rendered color so it's obvious
    // which edge a knob belongs to in a dense graph.
    const addHandle = (role: 'source' | 'target'): void => {
      const handles = linkGroups
        .filter((d: any) => !this.isAlignmentEdge(d))
        .append('g')
        .attr('class', `edge-endpoint-marker ${role}-marker`)
        .attr('opacity', 0)              // hidden until input mode
        .style('pointer-events', 'none');

      handles.each(function (d: any) {
        const g = d3.select(this);

        // Source of truth for the tint: the already-rendered edge path. Falls
        // back to the computed color, then a neutral slate.
        let color = self.edgeStrokeColor(d) || '#475569';
        const path = (this.parentNode as Element | null)
          ?.querySelector('path[data-link-id]') as SVGPathElement | null;
        if (path) {
          const stroke = path.getAttribute('stroke');
          if (stroke && stroke !== 'none') color = stroke;
        }

        // A bidirectional edge's two ends are interchangeable → both are heads.
        const isHead = !!d.bidirectional || role === 'target';
        if (isHead) {
          // Filled diamond (rotated square) centered on the origin; the parent
          // <g>'s translate does the positioning.
          g.append('rect')
            .attr('class', 'endpoint-shape')
            .attr('x', -6).attr('y', -6)
            .attr('width', 12).attr('height', 12)
            .attr('rx', 2)
            .attr('transform', 'rotate(45)')
            .attr('fill', color)
            .attr('stroke', 'white')
            .attr('stroke-width', 2);
        } else {
          // Hollow ring = the source/origin end of a directed edge.
          g.append('circle')
            .attr('class', 'endpoint-shape')
            .attr('r', 6)
            .attr('fill', 'white')
            .attr('stroke', color)
            .attr('stroke-width', 2.5);
        }

        // Native hover tooltip — the cheapest path to making the gesture
        // discoverable instead of tribal knowledge.
        const tip = d.bidirectional
          ? 'Symmetric edge — drag either end to move it · drop on empty space to delete'
          : role === 'source'
            ? 'Source end — drag to reconnect · drop on empty space to delete'
            : 'Target end — drag to reconnect · drop on empty space to delete';
        g.append('title').text(tip);
      });

      handles.call(
        d3.drag()
          .on('start', (d: EdgeWithMetadata) => this.startEdgeEndpointDrag(d, role))
          .on('drag', (d: EdgeWithMetadata) => this.dragEdgeEndpoint(d, role))
          .on('end', (d: EdgeWithMetadata) => this.endEdgeEndpointDrag(d, role))
      );
    };

    addHandle('target');
    addHandle('source');
  }

  /**
   * Start dragging an edge endpoint
   */
  private startEdgeEndpointDrag(edgeData: EdgeWithMetadata, endpoint: 'source' | 'target'): void {
    d3.event.sourceEvent.stopPropagation();
    
    this.edgeDragState.isDragging = true;
    this.edgeDragState.edge = edgeData;
    this.edgeDragState.endpoint = endpoint;
    
    console.log(`🔵 Started dragging ${endpoint} endpoint of edge:`, edgeData.id);
  }

  /**
   * Drag an edge endpoint - update visual feedback
   */
  private dragEdgeEndpoint(edgeData: EdgeWithMetadata, endpoint: 'source' | 'target'): void {
    if (!this.edgeDragState.isDragging) return;

    const [mouseX, mouseY] = d3.mouse(this.container.node());

    // Move the dragged handle group to follow the cursor.
    const markerClass = endpoint === 'target' ? '.target-marker' : '.source-marker';
    this.container
      .selectAll('.link-group')
      .filter((d: any) => d.id === edgeData.id)
      .select(markerClass)
      .attr('transform', `translate(${mouseX}, ${mouseY})`);
  }

  /**
   * End dragging an edge endpoint - reconnect or delete edge
   */
  private async endEdgeEndpointDrag(edgeData: EdgeWithMetadata, endpoint: 'source' | 'target'): Promise<void> {
    if (!this.edgeDragState.isDragging) return;

    const [mouseX, mouseY] = d3.mouse(this.container.node());
    
    // Find the node under the cursor
    const targetNode = this.findNodeAtPosition(mouseX, mouseY);
    
    if (targetNode) {
      console.log(`🔗 Reconnecting ${endpoint} to node:`, targetNode.id);
      await this.reconnectEdge(edgeData, endpoint, targetNode);
    } else {
      console.log(`🗑️ No node found - deleting edge:`, edgeData.id);
      await this.deleteEdge(edgeData);
    }
    
    // Clean up drag state
    this.edgeDragState = {
      isDragging: false,
      edge: null,
      endpoint: null,
      dragMarker: null
    };
    
    // Re-render to show changes
    this.rerenderGraph();
  }

  /**
   * Find a node at the given position
   */
  private findNodeAtPosition(x: number, y: number): NodeWithMetadata | null {
    if (!this.currentLayout?.nodes) return null;
    
    // Check each node to see if the position is within its bounds
    for (const node of this.currentLayout.nodes) {
      const halfWidth = ((node as any).visualWidth ?? node.width ?? 0) / 2;
      const halfHeight = ((node as any).visualHeight ?? node.height ?? 0) / 2;
      
      if (x >= node.x - halfWidth && x <= node.x + halfWidth &&
          y >= node.y - halfHeight && y <= node.y + halfHeight) {
        return node;
      }
    }
    
    return null;
  }

  /**
   * Reconnect an edge to a new node
   */
  private async reconnectEdge(
    edgeData: EdgeWithMetadata,
    endpoint: 'source' | 'target',
    newNode: NodeWithMetadata
  ): Promise<void> {
    const oldSourceNode = this.getNodeFromEdge(edgeData, 'source');
    const oldTargetNode = this.getNodeFromEdge(edgeData, 'target');
    
    if (!oldSourceNode || !oldTargetNode) {
      console.error('Could not find source or target node');
      return;
    }

    // Determine new source and target
    let newSourceNode: NodeWithMetadata;
    let newTargetNode: NodeWithMetadata;
    
    if (endpoint === 'source') {
      newSourceNode = newNode;
      newTargetNode = oldTargetNode;
    } else {
      newSourceNode = oldSourceNode;
      newTargetNode = newNode;
    }

    // Don't allow reconnecting if it results in the same edge
    if (newSourceNode.id === oldSourceNode.id && newTargetNode.id === oldTargetNode.id) {
      console.log('⏭️ Edge already connected to this node, no change needed');
      return;
    }

    // Use relName (the data-instance relation key) rather than label (the display
    // string, which may include n-ary suffixes like "[Person1]").
    const relationName = edgeData.relName || edgeData.label || '';

    if (!relationName.trim()) {
      console.warn('Edge has no relation name, cannot reconnect');
      return;
    }

    // Create tuples for old and new edges
    const oldTuple: ITuple = {
      atoms: [oldSourceNode.id, oldTargetNode.id],
      // Provisional: a layout node knows only its most specific type, and this class
      // has no data instance to check it against. Whichever data instance receives
      // the tuple settles its column types against the relation's declared signature
      // before storing it (see settleTupleTypes), so these values are never what
      // lands in a relation. A listener keeping its OWN store must settle them.
      types: [oldSourceNode.mostSpecificType || 'untyped', oldTargetNode.mostSpecificType || 'untyped']
    };

    const newTuple: ITuple = {
      atoms: [newSourceNode.id, newTargetNode.id],
      // Provisional: a layout node knows only its most specific type, and this class
      // has no data instance to check it against. Whichever data instance receives
      // the tuple settles its column types against the relation's declared signature
      // before storing it (see settleTupleTypes), so these values are never what
      // lands in a relation. A listener keeping its OWN store must settle them.
      types: [newSourceNode.mostSpecificType || 'untyped', newTargetNode.mostSpecificType || 'untyped']
    };

    console.log(`Reconnecting edge: ${relationName} from ${oldSourceNode.id}->${oldTargetNode.id} to ${newSourceNode.id}->${newTargetNode.id}`);

    // Dispatch edge reconnection event
    const edgeReconnectionEvent = new CustomEvent('edge-reconnection-requested', {
      detail: {
        relationId: relationName,
        oldTuple: oldTuple,
        newTuple: newTuple,
        oldSourceNodeId: oldSourceNode.id,
        oldTargetNodeId: oldTargetNode.id,
        newSourceNodeId: newSourceNode.id,
        newTargetNodeId: newTargetNode.id
      },
      bubbles: true
    });
    this.dispatchEvent(edgeReconnectionEvent);

    // Update the edge data in the current layout
    const sourceIndex = this.currentLayout.nodes.findIndex(n => n.id === newSourceNode.id);
    const targetIndex = this.currentLayout.nodes.findIndex(n => n.id === newTargetNode.id);
    
    // Store the NODE OBJECTS. These endpoints already HOLD resolved nodes — the
    // layout has started — so writing indices back would un-resolve them, and
    // nothing would ever resolve them again (see createNewEdge above).
    if (sourceIndex !== -1 && targetIndex !== -1) {
      edgeData.source = this.currentLayout.nodes[sourceIndex];
      edgeData.target = this.currentLayout.nodes[targetIndex];
    }
  }

  /**
   * Delete an edge from the graph
   */
  private async deleteEdge(edgeData: EdgeWithMetadata): Promise<void> {
    const sourceNode = this.getNodeFromEdge(edgeData, 'source');
    const targetNode = this.getNodeFromEdge(edgeData, 'target');

    if (!sourceNode || !targetNode) {
      console.error('Could not find source or target node for edge deletion');
      return;
    }

    // Use relName (the data-instance relation key) rather than label (display string).
    const relationName = edgeData.relName || edgeData.label || '';

    if (!relationName.trim()) {
      console.warn('Edge has no relation name, cannot delete from data instance');
      // Still remove from visualization
      this.removeEdgeFromLayout(edgeData);
      return;
    }

    // For group edges, collect tuples for ALL member nodes in the group.
    // A group edge from KeyNode to GroupA (containing N1, N2, N3) visually
    // represents the tuples (KeyNode,N1), (KeyNode,N2), (KeyNode,N3).
    const tuples: ITuple[] = [];

    if (edgeData.groupId && edgeData.keyNodeId && this.currentLayout) {
      const group = (this.currentLayout.groups || []).find((g: any) => g.id === edgeData.groupId);
      if (group) {
        const allGroups = this.currentLayout.groups || [];
        const memberIndices = this.collectGroupNodeIndices(group, allGroups);
        const keyNode = this.currentLayout.nodes.find((n: NodeWithMetadata) => n.id === edgeData.keyNodeId);

        for (const idx of memberIndices) {
          const memberNode = this.currentLayout.nodes[idx];
          if (memberNode && keyNode) {
            tuples.push({
              atoms: [keyNode.id, memberNode.id],
              // Provisional: a layout node knows only its most specific type, and this class
              // has no data instance to check it against. Whichever data instance receives
              // the tuple settles its column types against the relation's declared signature
              // before storing it (see settleTupleTypes), so these values are never what
              // lands in a relation. A listener keeping its OWN store must settle them.
              types: [keyNode.mostSpecificType || 'untyped', memberNode.mostSpecificType || 'untyped']
            });
          }
        }
      }
    }

    // Fallback: if no group tuples were found, use the direct source/target
    if (tuples.length === 0) {
      tuples.push({
        atoms: [sourceNode.id, targetNode.id],
        // Provisional: a layout node knows only its most specific type, and this class
        // has no data instance to check it against. Whichever data instance receives
        // the tuple settles its column types against the relation's declared signature
        // before storing it (see settleTupleTypes), so these values are never what
        // lands in a relation. A listener keeping its OWN store must settle them.
        types: [sourceNode.mostSpecificType || 'untyped', targetNode.mostSpecificType || 'untyped']
      });
    }

    console.log(`🗑️ Deleting edge: ${relationName} (${tuples.length} tuple(s))`);

    // Dispatch edge deletion event (using modification with empty new name)
    const edgeDeletionEvent = new CustomEvent('edge-modification-requested', {
      detail: {
        oldRelationId: relationName,
        newRelationId: '', // Empty string signals deletion
        sourceNodeId: sourceNode.id,
        targetNodeId: targetNode.id,
        tuples: tuples
      },
      bubbles: true
    });
    this.dispatchEvent(edgeDeletionEvent);

    // Remove from current layout
    this.removeEdgeFromLayout(edgeData);
  }

  /**
   * Remove an edge from the current layout
   */
  private removeEdgeFromLayout(edgeData: EdgeWithMetadata): void {
    if (!this.currentLayout?.links) return;
    
    const index = this.currentLayout.links.findIndex(link => link.id === edgeData.id);
    if (index !== -1) {
      this.currentLayout.links.splice(index, 1);
      console.log(`✅ Edge removed from layout: ${edgeData.id}`);
    }
  }

  /**
   * Sets up SVG group elements with rectangles and labels for WebCola layout.
   * Creates visual containers for node groups with proper styling and interactivity.
   * Handles both regular groups and disconnected node groups with different styling.
   * 
   * @param groups - Array of group objects to render
   * @param nodes - Array of nodes for color lookup via keyNode
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created group rectangles
   * 
   * @example
   * ```typescript
   * const groupSelection = this.setupGroups(this.currentLayout.groups, this.currentLayout.nodes, layout);
   * 
   * 
   * 
   * TODO: Could the issue be that groups are NODES and not indices?
   * 
   * Why are we returning anything here?
   * ```
   */
  private setupGroups(
    groups: any[], 
    nodes: Array<NodeWithMetadata>, 
    layout: D3Layout
  ) {
    // Create group rectangles with dynamic styling
    const groupRects = this.setupGroupRectangles(groups, nodes, layout);

    // Add labels to groups that should display them
    this.svgGroupLabels = this.setupGroupLabels(groups, layout);

    return groupRects;
  }

  /**
   * Creates rectangle elements for groups with proper styling and drag behavior.
   * Handles different group types with appropriate visual styling.
   * 
   * @param groups - Array of group objects to render
   * @param nodes - Array of nodes for color lookup via keyNode
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created group rectangles
   */
  private setupGroupRectangles(
    groups: any[], 
    nodes: Array<NodeWithMetadata>, 
    layout: D3Layout
  ): d3.Selection<SVGRectElement, any, any, unknown> {
    const groupRects = this.container
      .selectAll(".group")
      .data(groups)
      .enter()
      .append("rect")
      .attr("class", (d: any) => {
        if (this.isDisconnectedGroup(d))
          return "disconnectedNode"
        else if (this.isErrorGroup(d)) {
          return "error-group";
        } else {
          return "group";
        }
      })
      .attr("rx", WebColaCnDGraph.GROUP_BORDER_RADIUS)
      .attr("ry", WebColaCnDGraph.GROUP_BORDER_RADIUS)
      .style("fill", (d: any) => {
        // Disconnected groups are transparent
        if (this.isDisconnectedGroup(d)) {
          return "transparent";
        }

        // Use key node color for regular groups
        const targetNode = nodes[d.keyNode];
        return targetNode?.color || "#cccccc";
      })
      .attr("fill-opacity", WebColaCnDGraph.GROUP_FILL_OPACITY)
      .attr("stroke", (d: any) => {
        if (this.isDisconnectedGroup(d)) {
          return "none";
        }
        const targetNode = nodes[d.keyNode];
        return targetNode?.color || "#999999";
      })
      .attr("stroke-width", (d: any) => {
        if (this.isDisconnectedGroup(d) || this.isErrorGroup(d)) return 1;
        return WebColaCnDGraph.GROUP_STROKE_WIDTH;
      })
      .attr("stroke-opacity", (d: any) => {
        if (this.isDisconnectedGroup(d) || this.isErrorGroup(d)) return 1;
        return WebColaCnDGraph.GROUP_STROKE_OPACITY;
      })
      .call((layout as any).drag);


    return groupRects;
  }

  private resolveGroupLeafToNodeIndex(leaf: unknown): number | null {
    if (typeof leaf === 'number' && Number.isInteger(leaf) && leaf >= 0) {
      return leaf;
    }

    if (leaf && typeof leaf === 'object') {
      const nodeLike = leaf as { id?: string; index?: number };
      if (typeof nodeLike.index === 'number' && Number.isInteger(nodeLike.index) && nodeLike.index >= 0) {
        return nodeLike.index;
      }

      if (typeof nodeLike.id === 'string' && this.currentLayout?.nodes) {
        const index = this.currentLayout.nodes.findIndex((node: NodeWithMetadata) => node.id === nodeLike.id);
        return index >= 0 ? index : null;
      }
    }

    return null;
  }

  private collectGroupNodeIndices(group: any, allGroups: any[]): number[] {
    const nodeIndices = new Set<number>();
    const visitedGroupIndices = new Set<number>();

    const collect = (grp: any, groupIndex?: number): void => {
      if (!grp) {
        return;
      }

      if (typeof groupIndex === 'number') {
        if (visitedGroupIndices.has(groupIndex)) {
          return;
        }
        visitedGroupIndices.add(groupIndex);
      }

      if (Array.isArray(grp.leaves)) {
        grp.leaves.forEach((leaf: unknown) => {
          const index = this.resolveGroupLeafToNodeIndex(leaf);
          if (index !== null) {
            nodeIndices.add(index);
          }
        });
      }

      if (!Array.isArray(grp.groups)) {
        return;
      }

      grp.groups.forEach((subgroupIndex: unknown) => {
        if (
          typeof subgroupIndex === 'number'
          && Number.isInteger(subgroupIndex)
          && subgroupIndex >= 0
          && subgroupIndex < allGroups.length
        ) {
          collect(allGroups[subgroupIndex], subgroupIndex);
        }
      });
    };

    const rootIndex = allGroups.indexOf(group);
    collect(group, rootIndex >= 0 ? rootIndex : undefined);

    return Array.from(nodeIndices);
  }

  private getNodeMainLabelFontSize(_node: any): number {
    return MAIN_LABEL_FONT_SIZE;
  }

  private calculateGroupLabelFontSize(group: any, allGroups: any[]): number {
    if (!this.currentLayout?.nodes?.length) {
      return WebColaCnDGraph.DEFAULT_FONT_SIZE;
    }

    const nodeIndices = this.collectGroupNodeIndices(group, allGroups);
    if (nodeIndices.length === 0) {
      return WebColaCnDGraph.DEFAULT_FONT_SIZE;
    }

    const fontSizes = nodeIndices
      .map((index: number) => this.currentLayout.nodes[index])
      .filter((node: NodeWithMetadata | undefined): node is NodeWithMetadata => Boolean(node))
      .map((node: NodeWithMetadata) => this.getNodeMainLabelFontSize(node));

    if (fontSizes.length === 0) {
      return WebColaCnDGraph.DEFAULT_FONT_SIZE;
    }

    const averageFontSize = fontSizes.reduce((sum: number, size: number) => sum + size, 0) / fontSizes.length;
    const clampedFontSize = Math.max(
      WebColaCnDGraph.MIN_FONT_SIZE,
      Math.min(averageFontSize, WebColaCnDGraph.MAX_FONT_SIZE)
    );
    return Math.round(clampedFontSize * 10) / 10;
  }

  /**
   * Adds text labels to groups that should display them.
   * Labels are positioned at the top of each group with proper spacing.
   * 
   * @param groups - Array of group objects to render
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created group labels
   */
  private setupGroupLabels(
    groups: any[],
    layout: D3Layout
  ): d3.Selection<SVGTextElement, any, any, unknown> {
    const allNodes = this.currentLayout?.nodes || [];

    // Pill backings sit between the group rect and the text. Append before
    // the text so document order keeps text on top.
    this.svgGroupLabelBgs = this.container
      .selectAll(".groupLabelBg")
      .data(groups)
      .enter()
      .append("rect")
      .attr("class", "groupLabelBg")
      .attr("rx", 6)
      .attr("ry", 6)
      .attr("fill", this.getCanvasBackground())
      .attr("fill-opacity", 0.92)
      .attr("stroke", (d: any) => {
        const targetNode = allNodes[d.keyNode];
        return targetNode?.color || "#999999";
      })
      .attr("stroke-width", 1)
      .attr("stroke-opacity", 0.3)
      .attr("pointer-events", "none")
      .style("display", "none");

    return this.container
      .selectAll(".groupLabel")
      .data(groups)
      .enter()
      .append("text")
      .attr("class", "groupLabel")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "hanging")
      .attr("font-family", this.getFontFamily())
      .attr("font-size", (d: any) => {
        const computedFontSize = this.calculateGroupLabelFontSize(d, groups);
        d._groupLabelFontSize = computedFontSize;
        return `${computedFontSize}px`;
      })
      .attr("font-weight", "bold")
      .attr("fill", "#333")
      // Group directive's textStyle.color override via .style() (beats the attr default); null = keep #333.
      .style("fill", (d: any) => this.groupLabelColor(d))
      .attr("pointer-events", "none")
      .text((d: any) => {
        const shouldShowGroupLabel = d.showLabel || false;

        if (shouldShowGroupLabel) {
          // Ensure adequate padding for label display
          if (d.padding) {
            d.padding = Math.max(d.padding, WebColaCnDGraph.GROUP_LABEL_PADDING);
          }
          return d.name || "";
        }

        return "";
      }).call((layout as any).drag);
  }

  /**
   * Render links using D3 data binding with enhanced grouping and labeling
   */
  private renderLinks(links: Array<EdgeWithMetadata>, layout: D3Layout): void {
    // Clear the stale alignment-edge cache so isAlignmentEdge() falls back to the
    // reliable id-prefix check during rendering. On re-renders (e.g. stability policy)
    // the cache still holds IDs from the previous render; new alignment edges with
    // different IDs are not found in it and would be treated as visible real edges.
    // buildEdgeRoutingCaches() will repopulate the cache during routeEdges() as usual.
    this.edgeRoutingCache.alignmentEdges.clear();
    this.svgLinkGroups = this.setupLinks(links, layout);
  }

  /**
   * Sets up SVG node elements with rectangles, icons, and labels for WebCola layout.
   * Creates a complete node visualization with proper centering, styling, and interactivity.
   * 
   * @param nodes - Array of nodes with metadata to visualize
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created node groups
   */
  private setupNodes(nodes: Array<NodeWithMetadata>, layout: D3Layout): d3.Selection<SVGGElement, any, any, unknown> {
    // Create node groups with drag behavior
    const nodeDrag = layout.drag();
    this.setupNodeDragHandlers(nodeDrag);

    const nodeSelection = this.container
      .selectAll(".node")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", (d: any) => {
        let baseClass = this.isErrorNode(d) ? "error-node" : "node";
        if (this.isErrorNode(d) && this.isSmallNode(d)) {
          baseClass += " small-error-node";
        }
        if (this.isReintroducedNode(d)) {
          baseClass += " reintroduced-node";
        }
        return baseClass;
      })
      .call(nodeDrag)
      .on('mousedown.inputmode', (d: any) => {
        if (this.isInputModeActive) {
          d3.event.stopPropagation();
          this.startEdgeCreation(d);
        }
      })
      .on('mouseup.inputmode', (d: any) => {
        if (this.isInputModeActive && this.edgeCreationState.isCreating) {
          d3.event.stopPropagation();
          // Handle async operation without blocking the event
          this.finishEdgeCreation(d).catch(error => {
            console.error('Error finishing edge creation:', error);
          });
        }
      })
    // Show tooltip with node ID only when not in input mode
      .on('mouseover', function(this: SVGGElement, d: any) {
          d3.select(this)
            .append('title')
            .attr('class', 'node-tooltip')
            .text(`ID: ${d.id}`);
        
      })
      .on('mouseout', function(this: SVGGElement) {
        d3.select(this).select('title.node-tooltip').remove();
      });

    // Add rectangle backgrounds for nodes
    this.setupNodeRectangles(nodeSelection);

    // Add icons for nodes that have them
    this.setupNodeIcons(nodeSelection);

    // Add most specific type labels
    this.setupMostSpecificTypeLabels(nodeSelection);

    // Add main node labels with attributes
    this.setupNodeLabels(nodeSelection);

    return nodeSelection;
  }

  /**
   * Creates rectangle backgrounds for nodes with proper centering and styling.
   * Handles hidden nodes and icon-only nodes with transparent fills.
   * 
   * @param nodeSelection - D3 selection of node groups
   */
  private setupNodeRectangles(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    nodeSelection
      .append("rect")
      .attr("width", (d: any) => d.visualWidth ?? d.width)
      .attr("height", (d: any) => d.visualHeight ?? d.height)
      .attr("x", (d: any) => -((d.visualWidth ?? d.width)) / 2) // Center on node's x position
      .attr("y", (d: any) => -((d.visualHeight ?? d.height)) / 2) // Center on node's y position
      // A chosen node color is preserved; algorithm-assigned colors are re-tinted
      // for the themed canvas and the implicit black default is themed. See
      // nodeBorderColor / themedNodeColor.
      .attr("stroke", (d: any) => this.nodeBorderColor(d))
      .attr("rx", WebColaCnDGraph.NODE_BORDER_RADIUS)
      .attr("ry", WebColaCnDGraph.NODE_BORDER_RADIUS)
      .attr("stroke-width", WebColaCnDGraph.NODE_STROKE_WIDTH)
      // atomStyle.borderStyle.width override via .style() so it beats CSS rules; null = keep the default above.
      .style("stroke-width", (d: any) => this.nodeStrokeWidth(d))
      .attr("fill", (d: any) => this.nodeFillColor(d));
  }

  /**
   * Adds icons to nodes that have icon properties.
   * Geometry comes from `atomStyle.iconStyle.placement` (full vs badge) and alpha
   * from its `opacity`. Includes error handling for failed icon loads.
   *
   * Two element kinds come out of this, both classed `.node-icon` so the tick
   * paths can position either: an inlined `<svg>` for icons this package ships
   * (see {@link buildIconElement}) and an `<image>` for author-supplied URLs,
   * which cannot be inlined. A nested `<svg>` takes the same x/y/width/height as
   * `<image>` and defaults to the same `xMidYMid meet` fit, so the geometry the
   * `icon*` helpers feed it is unchanged.
   *
   * @param nodeSelection - D3 selection of node groups
   */
  private setupNodeIcons(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    const icons = nodeSelection
      .filter((d: any) => d.icon) // Only nodes with icons
      .append((d: any) => this.buildIconElement(d.icon))
      .attr("class", "node-icon")
      .attr("width", (d: any) => this.iconWidth(d))
      .attr("height", (d: any) => this.iconHeight(d))
      .attr("x", (d: any) => this.iconX(d))
      .attr("y", (d: any) => this.iconY(d))
      // Set on the icon itself, so the morph animation's group-level opacity
      // composes with it rather than being overwritten.
      .attr("opacity", (d: any) => d.iconOpacity ?? null);

    // Bound to the icon element, not to the <title> appended below: chaining
    // `.on` after `.append("title")` would attach the handler to the title
    // element, which never emits `error`, leaving broken icons silently blank.
    //
    // Only an <image> can fail this way; an inlined <svg> fetches nothing. The
    // fallback is inlined markup (not a packaged asset path, which would resolve
    // against the host app and 404), so the <image> is *replaced* rather than
    // re-pointed — which also gets the fallback's own `currentColor` themed. The
    // replacement carries the geometry over so the tick paths keep positioning
    // it, and the datum so the title and the message below can read it; the tick
    // paths re-propagate the node's datum themselves on every `select`.
    //
    // Plain DOM rather than d3 here: the listener's arguments differ between d3
    // versions, but `__data__` (what `.datum()` writes, and what `.append()`
    // already copied from the node) does not.
    const self = this;
    icons.on("error", function (this: SVGElement) {
      const d = (this as any).__data__;
      const replacement = self.buildIconElement(FALLBACK_ICON);
      for (const attr of ["class", "width", "height", "x", "y", "opacity"]) {
        const value = this.getAttribute(attr);
        if (value !== null) replacement.setAttribute(attr, value);
      }
      (replacement as any).__data__ = d;
      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      title.textContent = self.iconTitle(d);
      replacement.appendChild(title);
      this.replaceWith(replacement);
      console.error(`Failed to load icon for node ${d.id}: ${d.icon}`);
    });

    icons
      .append("title")
      .text((d: any) => this.iconTitle(d));
  }

  /** Tooltip text for a node's icon. */
  private iconTitle(d: any): string {
    return d.label || d.name || d.id || "Node";
  }

  /**
   * The element that draws `icon`: an inlined `<svg>` when this package authored
   * the markup, otherwise an `<image>` pointing at the reference.
   *
   * Inlining is what makes a bundled icon themeable. Its glyph is drawn with
   * `currentColor`, and inside an SVG loaded through `<image>` that resolves
   * against the *referenced* document — whose initial `color` is black — so the
   * glyph tracked the browser's color scheme rather than the diagram's theme and
   * was invisible on a dark canvas. Inlined, `color` cascades from the host in
   * the normal way (see the `.node-icon` rule in {@link getCSS}).
   *
   * Only markup from {@link getInlinableIconSvg} is ever inlined — it returns
   * `undefined` for anything this package did not author — so an author-supplied
   * path can never be injected as markup.
   */
  private buildIconElement(icon: string): SVGElement {
    const markup = getInlinableIconSvg(icon);
    if (markup) return WebColaCnDGraph.inlineIconFromMarkup(markup);
    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', icon);
    return image;
  }

  /**
   * Parsed-once templates for the inlinable icons, keyed by markup. Parsing is
   * cheap but repeated per node, and the same handful of glyphs recurs across a
   * whole diagram.
   */
  private static readonly inlineIconTemplates = new Map<string, SVGElement>();

  /** A fresh copy of `markup` as a live SVG element, via a cached template. */
  private static inlineIconFromMarkup(markup: string): SVGElement {
    let template = WebColaCnDGraph.inlineIconTemplates.get(markup);
    if (!template) {
      template = new DOMParser()
        .parseFromString(markup, 'image/svg+xml')
        .documentElement as unknown as SVGElement;
      WebColaCnDGraph.inlineIconTemplates.set(markup, template);
    }
    return document.importNode(template, true) as SVGElement;
  }

  /**
   * Adds most specific type labels to nodes with proper styling.
   * 
   * @param nodeSelection - D3 selection of node groups
   */
  private setupMostSpecificTypeLabels(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    nodeSelection
      .append("text")
      .attr("class", "mostSpecificTypeLabel")
      .style("fill", (d: any) => this.nodeTypeLabelColor(d))
      .text((d: any) => d.mostSpecificType || "");
  }

  /**
   * Gets a canvas context for text measurement
   */
  private getTextMeasurementContext(): CanvasRenderingContext2D {
    if (!this.textMeasurementCanvas) {
      this.textMeasurementCanvas = document.createElement('canvas');
    }
    return this.textMeasurementCanvas.getContext('2d')!;
  }

  /**
   * Measures the width of text at a given font size
   */
  private measureTextWidth(text: string, fontSize: number, fontFamily?: string): number {
    const context = this.getTextMeasurementContext();
    context.font = `${fontSize}px ${fontFamily ?? this.getFontFamily()}`;
    return context.measureText(text).width;
  }



  /**
   * Creates main node labels with attributes using fixed font sizes.
   *
   * Box dimensions are decided upstream by `LayoutInstance.getNodeSizeMap`
   * via `estimateLabelBox`, which sizes each box to fit the same fixed font
   * sizes used here. No per-node font negotiation; if a user pins a too-small
   * `size` directive their text overflows (their explicit choice).
   */
  private setupNodeLabels(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    const secondaryLineHeight = SECONDARY_FONT_SIZE * LABEL_LINE_HEIGHT_RATIO;

    nodeSelection
      .append("text")
      .attr("class", "label")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-family", this.getFontFamily())
      .attr("fill", "black")
      .each((d: any, i: number, nodes: ArrayLike<SVGTextElement>) => {
        if (this.isHiddenNode(d)) {
          return;
        }

        if (!d.showLabels) {
          return;
        }

        const textElement = d3.select(nodes[i]);

        const displayLabel = d.label || d.name || d.id || "Node";
        const attributeEntries = Object.entries(d.attributes || {})
          .sort(([a], [b]) => a.localeCompare(b));
        const labelEntries = Object.entries(d.labels || {});

        // Per-attribute text style (the shared `textStyle` block: size + color).
        // Size (small/normal/large tiers → px) drives each line's own line-height,
        // so the secondary block is no longer a uniform grid; we track heights per
        // line to place baselines. Color is applied as the tspan fill below.
        const attributeStyles = d.attributeTextStyles || {};
        const attributeFontSizes = attributeEntries.map(
          ([key]) => resolveAttrFontSize(attributeStyles[key]?.size)
        );
        // Secondary line-heights in DOM order: Skolem labels first, then attributes.
        const secondaryHeights = [
          ...labelEntries.map(() => secondaryLineHeight),
          ...attributeFontSizes.map((fs) => fs * LABEL_LINE_HEIGHT_RATIO),
        ];
        const secondaryBlockHeight = secondaryHeights.reduce((a, b) => a + b, 0);

        // Center the whole block on the node: shift the main label up by half
        // the secondary block's height so the visual centroid sits at d.y.
        const verticalOffset = secondaryHeights.length > 0 ? -secondaryBlockHeight * 0.5 : 0;

        // Per-tspan dy advances (index 0 = main label, then one per secondary
        // line). SVG `dy` is relative to the previous baseline, and tspans are
        // center-aligned (dominant-baseline: middle), so the gap between two
        // adjacent secondary lines must be the average of their line-heights —
        // otherwise a `large` line followed by a smaller one advances by only
        // the small height and the glyphs crowd/overlap. The main→first-secondary
        // advance stays the first line's own height, which keeps the all-normal
        // case identical (uniform heights make the average a no-op) and matches
        // the summed-height box sizing (adjacent averages telescope to Σ heights).
        const tspanDys = [verticalOffset];
        for (let s = 0; s < secondaryHeights.length; s++) {
          tspanDys.push(
            s === 0 ? secondaryHeights[0] : (secondaryHeights[s - 1] + secondaryHeights[s]) / 2
          );
        }
        // Cached so the post-layout reposition passes re-apply identical offsets;
        // also the single source of truth for the dy set on each tspan below.
        d._labelTspanDys = tspanDys;
        d._labelVerticalOffset = verticalOffset;
        d._labelLineHeight = secondaryLineHeight;

        textElement.attr("font-size", `${MAIN_LABEL_FONT_SIZE}px`);

        textElement
          .append("tspan")
          .attr("x", 0)
          .attr("dy", `${verticalOffset}px`)
          .attr("class", "main-label-tspan")
          .style("font-weight", "bold")
          .style("font-size", `${MAIN_LABEL_FONT_SIZE}px`)
          // atomStyle.textStyle.color for the node's own label; null = inherit the default black.
          .style("fill", this.nodeLabelColor(d))
          .text(displayLabel);

        // Skolem-style labels: italic, comma-separated values per key. These are
        // the first secondary lines (DOM order), so tspanDys indices 1..N.
        labelEntries.forEach(([, values], idx) => {
          const labelText = Array.isArray(values) ? values.join(', ') : String(values);
          textElement
            .append("tspan")
            .attr("x", 0)
            .attr("dy", `${tspanDys[1 + idx]}px`)
            .style("font-size", `${SECONDARY_FONT_SIZE}px`)
            // No explicit fill: inherit the themed parent label fill (the
            // `.label` CSS rule), so dark mode lightens skolem labels too.
            .style("font-style", "italic")
            .text(labelText);
        });

        // Attribute lines: "key: value, value, ..." each at its own tier size.
        // These follow the Skolem lines, so tspanDys indices are offset by them.
        attributeEntries.forEach(([key, value], idx) => {
          const fontSize = attributeFontSizes[idx];
          textElement
            .append("tspan")
            .attr("x", 0)
            .attr("dy", `${tspanDys[1 + labelEntries.length + idx]}px`)
            .style("font-size", `${fontSize}px`)
            // textStyle.color for this attribute/tag line; null = inherit the
            // themed parent `.label` fill (so dark mode still lightens it).
            .style("fill", attributeStyles[key]?.color ?? null)
            .text(`${key}: ${value}`);
        });
      });
  }

  /**
   * Render nodes using D3 data binding with drag behavior
   */
  private renderNodes(nodes: Array<NodeWithMetadata>, layout: D3Layout): void {
    this.svgNodes = this.setupNodes(nodes, layout);
  }

  /**
   * True for edges with at least one group-attached end: legacy `_g_`
   * connectors (group `addEdge`) and inferredEdge `draw` edges carrying
   * per-end sourceGroupId/targetGroupId stamps. These edges route via
   * routeGroupEdge and are excluded from node-port machinery (port
   * distribution and the pluggable router — see isRouterEdge).
   */
  private hasGroupEndpoints(d: any): boolean {
    return !!(d?.sourceGroupId || d?.targetGroupId || d?.groupId || d?.id?.startsWith('_g_'));
  }

  /**
   * Synthesise temporary group bounds from a reference point/node when WebCola
   * hasn't computed them yet (first ticks under stability / fast convergence).
   * The reference is a member-anchored position, so the edge points the right
   * way until real bounds arrive.
   */
  private ensureGroupBounds(group: any, ref: any): void {
    const Rectangle = getCola()?.Rectangle;
    if (!group || group.bounds || !ref || ref.x == null || ref.y == null || !Rectangle) return;
    const hw = (ref.visualWidth ?? ref.width ?? 100) / 2;
    const hh = (ref.visualHeight ?? ref.height ?? 60) / 2;
    group.bounds = new Rectangle(ref.x - hw, ref.x + hw, ref.y - hh, ref.y + hh);
  }

  /**
   * Resolves a group edge's {source, target} into rendering endpoints.
   *
   * One contract for every group-ended edge: a stamped end (sourceGroupId /
   * targetGroupId — set by inferredEdge `draw` or by the `addEdge` connector
   * desugar) is replaced by its own group object, independently; either end,
   * or both, may be a group. Un-stamped ends stay node endpoints — including
   * whole edges with no stamps (plain edges, and the group-by-field connector
   * corner whose key is a middle atom of an n-ary tuple, which has always
   * rendered node-to-node).
   *
   * @param d - Edge data object.
   * @returns { source, target } with group-attached ends replaced by group objects.
   */
  /**
   * True when both ends are stamped with the SAME group: `draw: g -> g` on a
   * tuple whose two atoms pick the same group, or a single (unary) group named
   * at both ends. Rendered as a self-loop on the hull — the group analogue of
   * a node self-loop. (Both anchors sit on the same member node, so the rest
   * of the pipeline already treats these as self-loops.)
   */
  private isGroupSelfLoop(d: any): boolean {
    return !!(d?.sourceGroupId && d.sourceGroupId === d.targetGroupId);
  }

  /**
   * Route for a group self-loop: the node self-loop petal, drawn on the group
   * hull instead of the node rect. Loops on the same hull distribute around
   * its sides/rings independently of any node self-loops. Falls back to the
   * member-node petal when the group (or its bounds) is missing.
   */
  private createGroupSelfLoopRoute(edgeData: any, grid: boolean = false): Array<{ x: number; y: number }> {
    const groups = this.currentLayout?.groups || [];
    const grp = groups.find((g: any) => g.id === edgeData.sourceGroupId);
    if (grp) this.ensureGroupBounds(grp, edgeData.source);
    const bounds = grp?.bounds ?? undefined;
    const index = bounds ? this.getGroupSelfLoopIndex(edgeData) : undefined;
    return grid
      ? this.createGridSelfLoopRoute(edgeData, bounds, index)
      : this.createSelfLoopRoute(edgeData, bounds, index);
  }

  /**
   * 0-based index of this self-loop among all self-loops on its GROUP, so
   * multiple loops on one hull spread around its sides like node loops do.
   */
  private getGroupSelfLoopIndex(edgeData: any): number {
    let idx = 0;
    for (const e of ((this.currentLayout?.links as any[]) ?? [])) {
      if (this.isGroupSelfLoop(e) && e.sourceGroupId === edgeData.sourceGroupId) {
        if (e.id === edgeData.id) return idx;
        idx++;
      }
    }
    return 0;
  }

  private resolveGroupEdgeEndpoints(d: any): { source: any; target: any } {
    if (!(d.sourceGroupId || d.targetGroupId)) {
      return { source: d.source, target: d.target };
    }

    const groups = this.currentLayout?.groups || [];
    let source = d.source;
    let target = d.target;
    if (d.sourceGroupId) {
      const grp = groups.find((g: any) => g.id === d.sourceGroupId);
      if (grp) {
        this.ensureGroupBounds(grp, d.source);
        source = grp;
      }
    }
    if (d.targetGroupId) {
      const grp = groups.find((g: any) => g.id === d.targetGroupId);
      if (grp) {
        this.ensureGroupBounds(grp, d.target);
        target = grp;
      }
    }
    return { source, target };
  }

  /**
   * Update positions of all visual elements during layout animation.
   * Implements the complete WebCola tick behavior with proper bounds handling,
   * group edge routing, and element layering.
   */

  /**
   * Position only node rects, icons, and labels — skipping groups and edges
   * whose bounds/endpoints may not be initialized yet.
   * Used to show continuing nodes at their old positions while the solver
   * runs, before groups and edge data are available.
   */
  private updateNodePositionsOnly(): void {
    if (!this.svgNodes) return;

    this.svgNodes.select('rect')
      .attr('x', (d: any) => d.x != null ? d.x - (d.visualWidth ?? d.width) / 2 : 0)
      .attr('y', (d: any) => d.y != null ? d.y - (d.visualHeight ?? d.height) / 2 : 0)
      .attr('width', (d: any) => d.visualWidth ?? d.width)
      .attr('height', (d: any) => d.visualHeight ?? d.height);

    this.svgNodes.select('.node-icon')
      .attr('x', (d: any) => d.x == null ? 0 : this.iconX(d))
      .attr('y', (d: any) => d.y == null ? 0 : this.iconY(d));

    this.svgNodes.select('.mostSpecificTypeLabel')
      .attr('x', (d: any) => d.x != null ? d.x - ((d as any).visualWidth ?? d.width ?? 0) / 2 + 5 : 0)
      .attr('y', (d: any) => d.y != null ? d.y - ((d as any).visualHeight ?? d.height ?? 0) / 2 + 10 : 0);

    this.svgNodes.select('.label')
      .attr('x', (d: any) => d.x ?? 0)
      .attr('y', (d: any) => d.y ?? 0)
      .each((d: any, i: number, nodes: Array<any>) => {
        if (d.x == null) return;
        const verticalOffset = d._labelVerticalOffset || 0;
        const lineHeight = d._labelLineHeight || 12;
        const tspanDys: number[] | undefined = d._labelTspanDys;
        d3.select(nodes[i])
          .selectAll('tspan')
          .attr('x', d.x)
          .attr('dy', (_: any, tspanIdx: number) =>
            tspanDys
              ? `${tspanDys[tspanIdx] ?? lineHeight}px`
              : (tspanIdx === 0 ? `${verticalOffset}px` : `${lineHeight}px`));
      });
  }

  private updatePositions(): void {

    
    // Update group positions and sizes first (lower layer)
    this.svgGroups
      .attr('x', (d: any) => d.bounds.x)
      .attr('y', (d: any) => d.bounds.y )
      .attr('width', (d: any) => d.bounds.width() )
      .attr('height', (d: any) => d.bounds.height() )
      .lower();

    // Update node rectangles using visual dimensions (NOT d.bounds, which WebCola inflates
    // for collision avoidance). Rects should reflect the original visual size.
    this.svgNodes.select('rect')
      .each((d: any) => {
        if (d.bounds) {
          d.innerBounds = d.bounds.inflate(-1);
        }
      })
      .attr('x', (d: any) => d.x - (d.visualWidth ?? d.width) / 2)
      .attr('y', (d: any) => d.y - (d.visualHeight ?? d.height) / 2)
      .attr('width', (d: any) => d.visualWidth ?? d.width)
      .attr('height', (d: any) => d.visualHeight ?? d.height);

    // Update node icons with proper positioning
    this.svgNodes.select('.node-icon')
      .attr('x', (d: any) => this.iconX(d))
      .attr('y', (d: any) => this.iconY(d));

    // Update most specific type labels — positioned at top-left of the node
    this.svgNodes.select('.mostSpecificTypeLabel')
      .attr('x', (d: NodeWithMetadata) => d.x - ((d as any).visualWidth ?? d.width ?? 0) / 2 + 5)
      .attr('y', (d: NodeWithMetadata) => d.y - ((d as any).visualHeight ?? d.height ?? 0) / 2 + 10)
      .raise();

    // Update main node labels with tspan positioning
    this.svgNodes.select('.label')
      .attr('x', (d: NodeWithMetadata) => d.x)
      .attr('y', (d: NodeWithMetadata) => d.y)
      .each((d: any, i: number, nodes: Array<any>) => {
        const verticalOffset = d._labelVerticalOffset || 0;
        const lineHeight = d._labelLineHeight || 12;
        const tspanDys: number[] | undefined = d._labelTspanDys;
        d3.select(nodes[i])
          .selectAll('tspan')
          .attr('x', d.x)
          .attr('dy', (tspanData: any, tspanIdx: number) => {
            if (tspanDys) {
              return `${tspanDys[tspanIdx] ?? lineHeight}px`;
            }
            if (tspanIdx === 0) {
              return `${verticalOffset}px`;
            }
            return `${lineHeight}px`;
          });
      })
      .raise();

    // Update link paths with stable anchor-based routing to prevent jitter during dragging.
    // Select the main path (identified by data-link-id) so any highlight underlay sibling
    // is not picked up here — markers and .raise() apply only to the main edge.
    this.svgLinkGroups.select('path[data-link-id]')
      .attr('d', (d: EdgeWithMetadata) => {
        // Same group at both ends: a self-loop petal on the hull. (Resolving
        // endpoints would hand getStableEdgePath the same rect twice and
        // collapse the path to a point.)
        if (this.isGroupSelfLoop(d)) {
          return this.lineFunction(this.createGroupSelfLoopRoute(d));
        }

        // Resolve group-edge endpoints (group boundary instead of member node center).
        const { source, target } = this.resolveGroupEdgeEndpoints(d);

        // Use stable anchor-based edge routing to prevent jitter during dragging
        // This approach selects consistent edge anchor points based on dominant direction
        // rather than computing dynamic ray intersections that can jump erratically
        const route = stableEdgePath(source, target, d);
        return this.lineFunction(route);
      })
      .attr('marker-end', (d: EdgeWithMetadata) => {
        if (this.isAlignmentEdge(d)) return 'none';
        return 'url(#end-arrow)';
      })
      .attr('marker-start', (d: EdgeWithMetadata) => {
        // Add marker-start for bidirectional edges
        if (this.isAlignmentEdge(d) || !d.bidirectional) return 'none';
        return 'url(#start-arrow)';
      })
      .raise();

    // Mirror the main path's `d` onto the highlight underlay sibling.
    // Cheap: one getAttribute + setAttribute per highlighted edge per tick.
    this.svgLinkGroups.select('path.highlight-underlay')
      .attr('d', function (this: SVGPathElement) {
        const parent = this.parentNode as Element | null;
        const main = parent?.querySelector('path[data-link-id]');
        return main?.getAttribute('d') ?? null;
      });

    // Update link labels using path midpoint calculation
    this.svgLinkGroups.select('.linklabel')
      .attr('x', (d: EdgeWithMetadata, i: number, nodes: any) => {
        const pathElement = this.getLinkPathElement(nodes[i]);
        return pathElement ? this.calculateNewPosition(pathElement, 'x') : (d.source.x + d.target.x) / 2;
      })
      .attr('y', (d: EdgeWithMetadata, i: number, nodes: any) => {
        const pathElement = this.getLinkPathElement(nodes[i]);
        return pathElement ? this.calculateNewPosition(pathElement, 'y') : (d.source.y + d.target.y) / 2;
      })
      .style('font-size', (d: EdgeWithMetadata) => {
        // Zoom-aware font sizing: scale up slightly when zoomed out. Base off the
        // edge's own textStyle tier when it set one — this runs on every tick and
        // would otherwise clobber the size setupLinkLabels applied, collapsing
        // every tier back to the 12px default.
        const baseFontSize = d?.textStyle?.size ? resolveAttrFontSize(d.textStyle.size) : 12;
        const zoomScale = this.getCurrentZoomScale();
        // When zoomed out (scale < 1), increase font size slightly to maintain readability
        // When zoomed in (scale > 1), keep base size
        const adjustedSize = zoomScale < 1 ? baseFontSize / Math.sqrt(zoomScale) : baseFontSize;
        // Cap the zoom-out boost at a third above the base rather than a fixed
        // ceiling, so a `large` tier isn't shrunk back below its authored size.
        return `${Math.min(adjustedSize, baseFontSize * 4 / 3)}px`;
      })
      .raise();

    // Update edge endpoint markers for input mode
    this.updateEdgeEndpointMarkers();

    // Update group labels (center top of each group)
    this.svgGroupLabels
      .attr('x', (d: any) => {
        if (!d.bounds) return 0;
        return d.bounds.x + (d.bounds.width() / 2);
      })
      .attr('y', (d: any) => {
        if (!d.bounds) return 0;
        const labelFontSize = d._groupLabelFontSize || WebColaCnDGraph.DEFAULT_FONT_SIZE;
        return d.bounds.y + WebColaCnDGraph.GROUP_LABEL_PILL_OFFSET_Y + Math.max(4, labelFontSize * 0.35);
      })
      .attr('text-anchor', 'middle')
      .each(function (this: SVGTextElement, d: any) {
        const text = this;
        if (text.textContent && d.bounds) {
          const bbox = text.getBBox();
          d._labelBBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
        } else {
          d._labelBBox = null;
        }
      });

    // Position the pill backing rect to wrap the label text.
    if (this.svgGroupLabelBgs) {
      this.svgGroupLabelBgs
        .style('display', (d: any) => (d._labelBBox ? null : 'none'))
        .attr('x', (d: any) => d._labelBBox ? d._labelBBox.x - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X : 0)
        .attr('y', (d: any) => d._labelBBox ? d._labelBBox.y - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y : 0)
        .attr('width', (d: any) => d._labelBBox ? d._labelBBox.width + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X * 2 : 0)
        .attr('height', (d: any) => d._labelBBox ? d._labelBBox.height + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y * 2 : 0);
      this.svgGroupLabelBgs.raise();
    }
    this.svgGroupLabels.raise();

    // Ensure proper layering - raise important elements
    this.svgLinkGroups.selectAll('marker').raise();
    this.svgLinkGroups.selectAll('.linklabel').raise();
    this.svgNodes.selectAll('.error-node').raise();
  }

  /**
   * Update positions of edge endpoint markers
   * Positions them at the arrow/marker positions of edges
   */
  private updateEdgeEndpointMarkers(): void {
    // Skip when detached (e.g. dispose() via disconnectedCallback): path
    // geometry reads throw on non-rendered elements, and there is nothing
    // visible to update anyway.
    if (!this.svgLinkGroups || !this.isConnected) return;

    const place = (
      sel: d3.Selection<any, any, any, unknown>,
      position: 'start' | 'end'
    ): void => {
      sel
        .attr('transform', (d: EdgeWithMetadata, i: number, nodes: any) => {
          const point = this.getEdgePathPoint(nodes[i], position);
          const fallback = (position === 'end' ? d.target : d.source) as any;
          const x = point ? point.x : (fallback?.x || 0);
          const y = point ? point.y : (fallback?.y || 0);
          return `translate(${x}, ${y})`;
        })
        .attr('opacity', this.isInputModeActive ? 0.95 : 0)
        .style('pointer-events', this.isInputModeActive ? 'all' : 'none')
        .style('cursor', 'move')
        .raise(); // keep handles above the edge paths
    };

    // Target markers sit at the arrow end; source markers at the path start.
    place(this.svgLinkGroups.select('.target-marker'), 'end');
    place(this.svgLinkGroups.select('.source-marker'), 'start');
  }

  /**
   * Find the main edge path belonging to the same link-group as `el` — the
   * label, marker, or other sibling that needs the path's geometry.
   *
   * Scoped to the group rather than looked up by id across the shadow root:
   * edge ids are data and routinely contain selector syntax (`_inferred_<:
   * <:"k4"->n3` is a real one), so splicing an id into a selector made
   * querySelector throw SyntaxError and took the whole layout down with it.
   * Matching on the group also keeps this off the per-tick cost of scanning
   * the entire shadow root once per label.
   *
   * `data-link-id` marks the main path, so the optional highlight underlay
   * sibling is never picked up here.
   */
  private getLinkPathElement(el: Element | null | undefined): SVGPathElement | null {
    const group = el?.closest('.link-group');
    return (group?.querySelector('path[data-link-id]') ?? null) as SVGPathElement | null;
  }

  /**
   * Read the start or end point of the edge path `el` belongs to.
   *
   * Returns null when the path is missing or not rendered:
   * getTotalLength()/getPointAtLength() throw InvalidStateError on detached
   * or hidden paths, so callers fall back to layout coordinates.
   */
  private getEdgePathPoint(el: Element | null | undefined, position: 'start' | 'end'): { x: number; y: number } | null {
    const pathElement = this.getLinkPathElement(el);
    if (!pathElement) return null;
    try {
      return pathElement.getPointAtLength(position === 'end' ? pathElement.getTotalLength() : 0);
    } catch {
      return null;
    }
  }

  private gridUpdatePositions() {

    // Force recompute node bounds from current positions
    // This is critical for grid mode to work correctly with all node types
    this.ensureNodeBounds(true);
    
    const node = this.container.selectAll(".node");
    const mostSpecificTypeLabel = this.container.selectAll(".mostSpecificTypeLabel");
    const label = this.container.selectAll(".label");
    const group = this.container.selectAll(".group");
    const groupLabel = this.container.selectAll(".groupLabel");

    // UPDATE NODES AND NODE LABELS
    node.select("rect")
        .each(function (d: any) { d.innerBounds = d.bounds.inflate(-1); })
        .attr("x", function (d: any) { return d.bounds.x; })
        .attr("y", function (d: any) { return d.bounds.y; })
        .attr("width", function (d: any) { return d.bounds.width(); })
        .attr("height", function (d: any) { return d.bounds.height(); });
    

    // A full-bleed icon tracks the WebCola bounds here (rather than the visual
    // box) so it stays registered with the rect drawn from those same bounds.
    node.select(".node-icon")
        .attr("x", (d: any) => this.iconX(d, d.bounds.x))
        .attr("y", (d: any) => this.iconY(d, d.bounds.y))

    mostSpecificTypeLabel
        .attr("x", function (d: any) { return d.bounds.x + 5; })
        .attr("y", function (d: any) { return d.bounds.y + 10; })
        .raise();

    label
        .attr("x", (d: any) => d.x)
        .attr("y", (d: any) => d.y)
        .each(function (this: SVGTextElement, d: any) {
            var y = 0; // Initialize y offset for tspans
            d3.select(this).selectAll("tspan")
                .attr("x", d.x) // Align tspans with the node's x position
                .attr("dy", function () {
                    y += 1; // Increment y for each tspan to create line spacing
                    return y === 1 ? "0em" : "1em"; // Keep the first tspan in place, move others down
                });
        })
        .raise();

    // UPDATE GROUPS AND GROUP LABELS
    group.attr("x", function (d: any) { return d.bounds.x; })
        .attr("y", function (d: any) { return d.bounds.y; })
        .attr("width", function (d: any) { return d.bounds.width(); })
        .attr("height", function (d: any) { return d.bounds.height(); })
        .lower();

    // Render group labels
    groupLabel.attr("x", function (d: any) { return d.bounds.x + d.bounds.width() / 2; }) // Center horizontally
        .attr("y", function (d: any) {
          const labelFontSize = d._groupLabelFontSize || WebColaCnDGraph.DEFAULT_FONT_SIZE;
          return d.bounds.y + WebColaCnDGraph.GROUP_LABEL_PILL_OFFSET_Y + Math.max(4, labelFontSize * 0.35);
        })
        .attr("text-anchor", "middle")
        .each(function (this: SVGTextElement, d: any) {
          const text = this;
          if (text.textContent && d.bounds) {
            const bbox = text.getBBox();
            d._labelBBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
          } else {
            d._labelBBox = null;
          }
        });

    const groupLabelBg = this.container.selectAll(".groupLabelBg");
    groupLabelBg
      .style("display", (d: any) => (d._labelBBox ? null : "none"))
      .attr("x", (d: any) => d._labelBBox ? d._labelBBox.x - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X : 0)
      .attr("y", (d: any) => d._labelBBox ? d._labelBBox.y - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y : 0)
      .attr("width", (d: any) => d._labelBBox ? d._labelBBox.width + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X * 2 : 0)
      .attr("height", (d: any) => d._labelBBox ? d._labelBBox.height + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y * 2 : 0)
      .raise();
    groupLabel.raise();

    // UPDATE EDGES - Use orthogonal routing for grid mode
    // Maintain grid-like paths during drag for better visual consistency
    const linkGroups = this.container.selectAll(".link-group");
    linkGroups.select("path")
        .attr("d", (d: any) => {
            // Handle self-loops specially - they can't use orthogonal routing.
            // draw-stamped edges are exempt (coinciding anchors can still have
            // distinct hull endpoints); `_g_` connectors keep their historical
            // self-loop rendering (see createEdgeRoute).
            if (this.isGroupSelfLoop(d)) {
                return this.gridLineFunction(this.createGroupSelfLoopRoute(d, true));
            }
            if (d.source?.id === d.target?.id
                && (d.id?.startsWith('_g_') || !(d.sourceGroupId || d.targetGroupId))) {
                const route = this.createGridSelfLoopRoute(d);
                return this.gridLineFunction(route);
            }

            // Resolve group-edge endpoints: for _g_ edges the arrow should reach
            // the group boundary, not the member node center.
            const { source, target } = this.resolveGroupEdgeEndpoints(d);
            
            // Determine start/end coordinates.
            // For group endpoints (which have .bounds but no .x/.y), use bounds centre.
            const getCenter = (endpoint: any): { x: number; y: number } => {
              if (endpoint.bounds) {
                if (typeof endpoint.bounds.cx === 'function') {
                  return { x: endpoint.bounds.cx(), y: endpoint.bounds.cy() };
                }
                return {
                  x: (endpoint.bounds.x + endpoint.bounds.X) / 2,
                  y: (endpoint.bounds.y + endpoint.bounds.Y) / 2
                };
              }
              return { x: endpoint.x ?? 0, y: endpoint.y ?? 0 };
            };

            const src = getCenter(source);
            const tgt = getCenter(target);
            const dx = tgt.x - src.x;
            const dy = tgt.y - src.y;
            
            // Choose routing based on which direction is dominant
            if (Math.abs(dx) > Math.abs(dy)) {
                // Horizontal first, then vertical
                const midX = src.x + dx / 2;
                return this.gridLineFunction([
                    { x: src.x, y: src.y },
                    { x: midX, y: src.y },
                    { x: midX, y: tgt.y },
                    { x: tgt.x, y: tgt.y }
                ]);
            } else {
                // Vertical first, then horizontal
                const midY = src.y + dy / 2;
                return this.gridLineFunction([
                    { x: src.x, y: src.y },
                    { x: src.x, y: midY },
                    { x: tgt.x, y: midY },
                    { x: tgt.x, y: tgt.y }
                ]);
            }
        });
    
    // Update link labels to follow edges using path midpoint
    linkGroups.select("text.linklabel")
        .attr("x", (d: any, i: number, nodes: any) => {
            const pathElement = this.getLinkPathElement(nodes[i]);
            if (pathElement) {
                const pathLength = pathElement.getTotalLength();
                const midpoint = pathElement.getPointAtLength(pathLength / 2);
                return midpoint.x;
            }
            // Use node.x/y as primary source (same as default mode)
            const sourceX = d.source?.x ?? d.source?.bounds?.cx() ?? 0;
            const targetX = d.target?.x ?? d.target?.bounds?.cx() ?? 0;
            return (sourceX + targetX) / 2;
        })
        .attr("y", (d: any, i: number, nodes: any) => {
            const pathElement = this.getLinkPathElement(nodes[i]);
            if (pathElement) {
                const pathLength = pathElement.getTotalLength();
                const midpoint = pathElement.getPointAtLength(pathLength / 2);
                return midpoint.y;
            }
            // Use node.x/y as primary source (same as default mode)
            const sourceY = d.source?.y ?? d.source?.bounds?.cy() ?? 0;
            const targetY = d.target?.y ?? d.target?.bounds?.cy() ?? 0;
            return (sourceY + targetY) / 2;
        })
        .raise();
  }

  /**
   * Advanced edge routing with curvature calculation and overlap handling.
   * Implements sophisticated routing for multiple edges between nodes, self-loops,
   * and group edges with proper collision detection and label positioning.
   */
  private routeEdges(): void {
    try {
      // Force-recompute bounds using visual dimensions (visualWidth/visualHeight) before
      // routing. After layout converges, WebCola has set node.bounds to the inflated
      // collision size (width/height + padding). Without forceRecompute=true the stale
      // check would pass (center still matches x,y) and leave inflated bounds in place,
      // causing edges to stop short of the visual node boundary.
      this.ensureNodeBounds(true);

      // Precompute the obstacle set once (positions are frozen now) so each
      // edge's buildRouterObstacles is an O(N) filter, not O(N) bounds
      // recomputation. Built BEFORE the routing caches: side selection in
      // buildEdgeRoutingCaches consults the obstacle set.
      this.buildRouterObstacleCache();

      // Build caches for optimization before routing edges (includes
      // obstacle-aware exit/entry side selection for the standard pipeline)
      this.buildEdgeRoutingCaches();

      // Sort edge ports by angle to minimize crossings at shared nodes
      this.sortEdgePortsByAngle();

      // Start the pass with an empty route set, so the hooks below never see
      // the previous pass's routes through host.routes.
      this.computedRoutes.clear();

      // Batch hook: routers that route all edges in one transaction
      // (e.g. libavoid) do the work here and serve routeEdge from a cache.
      this.edgeRouter.beginPass?.(this.routerHost());

      // Compute routes for all edges (stored in computedRoutes map)
      this.computeAllRoutes();

      // Router post-pass (taut: corridor separation — near-parallel overlaps;
      // crossings are already minimized by shortest-path routing and
      // port-angle ordering).
      this.edgeRouter.finalize?.(this.routerHost());

      // Apply computed routes to SVG
      this.applyRoutesToSVG();

      // Update link labels with proper positioning
      this.updateLinkLabelsAfterRouting();

      // Auto-fit viewport to content
      this.fitViewportToContent();
    } catch (error) {
      // Routing runs from the solver's 'end' handler, after updatePositions has
      // already painted the nodes — so the diagram is on screen with bad edges,
      // which is the degraded case, not the total one.
      this.showError(
        `Edge routing failed: ${(error as Error).message}`,
        { phase: 'routing', cause: error, fatal: false }
      );
    } finally {
      // Drop the per-pass obstacle cache so it can't go stale across renders.
      this.routerObstacleCache = null;
    }
  }

  /**
   * Ensures all nodes have bounds (Rectangle) objects computed.
   * This is necessary for edge routing to work correctly.
   * 
   * When using prior positions with minimal iterations, WebCola may not
   * have computed bounds for nodes. This method manually creates Rectangle
   * bounds based on node x, y, width, height properties.
   * 
   * @param forceRecompute - If true, always recompute bounds even if they exist
   */
  private ensureNodeBounds(forceRecompute: boolean = false): void {
    const Rectangle = getCola()?.Rectangle;
    if (!this.currentLayout?.nodes || !Rectangle) return;

    for (const node of this.currentLayout.nodes) {
      // Skip if bounds already exist and are valid, unless forceRecompute is true
      if (!forceRecompute && node.bounds && typeof node.bounds.rayIntersection === 'function') {
        // Check if bounds are still in sync with position
        const currentCx = node.bounds.cx();
        const currentCy = node.bounds.cy();
        const tolerance = 1; // Allow small floating point differences
        if (Math.abs(currentCx - (node.x || 0)) < tolerance && 
            Math.abs(currentCy - (node.y || 0)) < tolerance) {
          continue;
        }
      }

      // Compute bounds from node position and visual dimensions (not the inflated
      // collision dimensions stored in node.width/height).
      // Rectangle constructor: (x, X, y, Y) where x,y is top-left and X,Y is bottom-right
      const halfWidth = ((node as any).visualWidth ?? node.width ?? 50) / 2;
      const halfHeight = ((node as any).visualHeight ?? node.height ?? 30) / 2;
      const x = (node.x || 0) - halfWidth;
      const X = (node.x || 0) + halfWidth;
      const y = (node.y || 0) - halfHeight;
      const Y = (node.y || 0) + halfHeight;

      node.bounds = new Rectangle(x, X, y, Y);
      node.innerBounds = node.bounds.inflate(-1);
    }
  }

  /**
   * Builds caches for edge routing optimization.
   * Pre-computes edge relationships and alignment edge sets to avoid redundant calculations.
   */
  private buildEdgeRoutingCaches(): void {
    // Clear existing caches
    this.edgeRoutingCache.edgesBetweenNodes.clear();
    this.edgeRoutingCache.alignmentEdges.clear();
    this.edgeRoutingCache.nodeEdgesBySide.clear();

    if (!this.currentLayout?.links) return;

    // Build alignment edge set for O(1) lookups
    this.currentLayout.links.forEach((edge: EdgeWithMetadata) => {
      if (edge.id?.startsWith("_alignment_")) {
        this.edgeRoutingCache.alignmentEdges.add(edge.id);
      }
    });

    // Build edges-between-nodes cache and node-edges-by-side cache
    this.currentLayout.links.forEach((edge: EdgeWithMetadata) => {
      if (this.isAlignmentEdge(edge)) return; // Skip alignment edges

      const sourceId = edge.source.id;
      const targetId = edge.target.id;

      // Create sorted key to handle bidirectional lookups
      const key = this.getNodePairKey(sourceId, targetId);

      if (!this.edgeRoutingCache.edgesBetweenNodes.has(key)) {
        this.edgeRoutingCache.edgesBetweenNodes.set(key, []);
      }
      this.edgeRoutingCache.edgesBetweenNodes.get(key)!.push(edge);

      // Skip self-loops and group edges for port ordering
      if (sourceId === targetId || this.hasGroupEndpoints(edge)) return;

      // Sides are re-derived every pass (positions move, sides can flip), so
      // stale stamps from a previous pass must never survive into this one.
      delete (edge as any)._exitSide;
      delete (edge as any)._entrySide;
      delete (edge as any)._sourcePortIndex;
      delete (edge as any)._sourcePortCount;
      delete (edge as any)._targetPortIndex;
      delete (edge as any)._targetPortCount;

      const sourceCx = edge.source.x || 0;
      const sourceCy = edge.source.y || 0;
      const targetCx = edge.target.x || 0;
      const targetCy = edge.target.y || 0;

      let sourceSide: 'top' | 'bottom' | 'left' | 'right' | null;
      let targetSide: 'top' | 'bottom' | 'left' | 'right' | null;
      if (!this.useGridPipeline) {
        // Standard pipeline: obstacle-aware side choice. A long edge whose
        // straight line is blocked (e.g. a skip pointer over a chain) flips
        // to a matching over/under pair instead of hooking around the first
        // blocker — see routing/port-sides.ts. The stamps drive bucketing
        // here, the attachment point/normal in getPortAttachment, and the
        // distribution axis in applyPortBasedEndpoints, so all three agree.
        const sB = renderedBounds(edge.source);
        const tB = renderedBounds(edge.target);
        const choice = choosePortSides(
          { minX: sB.x, minY: sB.y, maxX: sB.x + sB.width(), maxY: sB.y + sB.height() },
          { minX: tB.x, minY: tB.y, maxX: tB.x + tB.width(), maxY: tB.y + tB.height() },
          this.buildRouterObstacles(edge)
        );
        (edge as any)._exitSide = choice.exitSide;
        (edge as any)._entrySide = choice.entrySide;
        sourceSide = choice.exitSide;
        targetSide = choice.entrySide;
      } else {
        // Grid pipeline: historical dominant-direction bucketing.
        const angle = Math.atan2(targetCy - sourceCy, targetCx - sourceCx);
        const sourceDirRaw = dominantDirection(angle);
        const targetDirRaw = dominantDirection(angle + Math.PI); // Reverse direction for target
        // Map getDominantDirection's 'up'/'down' to our 'top'/'bottom' side names
        const dirToSide = (d: string | null): 'top' | 'bottom' | 'left' | 'right' | null => {
          if (d === 'up') return 'top';
          if (d === 'down') return 'bottom';
          if (d === 'left' || d === 'right') return d;
          return null;
        };
        sourceSide = dirToSide(sourceDirRaw);
        targetSide = dirToSide(targetDirRaw);
      }

      if (sourceSide) {
        if (!this.edgeRoutingCache.nodeEdgesBySide.has(sourceId)) {
          this.edgeRoutingCache.nodeEdgesBySide.set(sourceId, { top: [], bottom: [], left: [], right: [] });
        }
        this.edgeRoutingCache.nodeEdgesBySide.get(sourceId)![sourceSide].push(
          { edge, role: 'source', remoteX: targetCx, remoteY: targetCy }
        );
      }
      if (targetSide) {
        if (!this.edgeRoutingCache.nodeEdgesBySide.has(targetId)) {
          this.edgeRoutingCache.nodeEdgesBySide.set(targetId, { top: [], bottom: [], left: [], right: [] });
        }
        this.edgeRoutingCache.nodeEdgesBySide.get(targetId)![targetSide].push(
          { edge, role: 'target', remoteX: sourceCx, remoteY: sourceCy }
        );
      }
    });
  }

  /**
   * Sorts edges at each node side by the angular position of their remote endpoint.
   * This ensures edges don't cross each other unnecessarily where they leave/enter a node.
   *
   * For top/bottom sides: sorts left-to-right by remote node's x-coordinate.
   * For left/right sides: sorts top-to-bottom by remote node's y-coordinate.
   *
   * Stamps _sourcePortIndex, _sourcePortCount, _targetPortIndex, _targetPortCount
   * on each edge data object for use during offset calculation.
   */
  private sortEdgePortsByAngle(): void {
    const tieBreak = (a: any, b: any): number => {
      const ida = String(a.edge?.id ?? '');
      const idb = String(b.edge?.id ?? '');
      if (ida < idb) return -1;
      if (ida > idb) return 1;
      return 0;
    };
    for (const [, sides] of this.edgeRoutingCache.nodeEdgesBySide) {
      // Top/bottom: sort by remote x (left to right), edge id as tie-breaker
      for (const side of ['top', 'bottom'] as const) {
        const entries = sides[side];
        if (entries.length <= 1) continue;
        entries.sort((a: any, b: any) => (a.remoteX - b.remoteX) || tieBreak(a, b));
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.role === 'source') {
            entry.edge._sourcePortIndex = i;
            entry.edge._sourcePortCount = entries.length;
          } else {
            entry.edge._targetPortIndex = i;
            entry.edge._targetPortCount = entries.length;
          }
        }
      }
      // Left/right: sort by remote y (top to bottom), edge id as tie-breaker
      for (const side of ['left', 'right'] as const) {
        const entries = sides[side];
        if (entries.length <= 1) continue;
        entries.sort((a: any, b: any) => (a.remoteY - b.remoteY) || tieBreak(a, b));
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.role === 'source') {
            entry.edge._sourcePortIndex = i;
            entry.edge._sourcePortCount = entries.length;
          } else {
            entry.edge._targetPortIndex = i;
            entry.edge._targetPortCount = entries.length;
          }
        }
      }
    }
  }

  /**
   * Creates a consistent cache key for a node pair (order-independent).
   * 
   * @param sourceId - ID of the source node
   * @param targetId - ID of the target node
   * @returns Cache key string in format "id1:id2" where id1 < id2 lexicographically
   */
  private getNodePairKey(sourceId: string, targetId: string): string {
    return sourceId < targetId ? `${sourceId}:${targetId}` : `${targetId}:${sourceId}`;
  }

  private gridify(nudgeGap: number, margin: number, groupMargin: number): void {
    // Guard against re-entrance - prevents infinite loops when gridify is called
    // while already in progress (e.g., from event handlers or callbacks)
    if (this.isGridifyingInProgress) {
      console.warn('[gridify] Already in progress, skipping re-entrant call');
      return;
    }

    this.isGridifyingInProgress = true;

    try {
      this.gridifyInternal(nudgeGap, margin, groupMargin);
    } catch (e) {
      console.error('[gridify] GridRouter failed; falling back to straight doglegs.', e);

      // Fall back to simple orthogonal routing on error
      try {
        this.fallbackGridRouting(this.currentLayout?.links ?? []);
      } catch (fallbackError) {
        console.error("Fallback grid routing also failed:", fallbackError);
      }

      let runtimeMessages = document.getElementById("runtime_messages") as HTMLElement;
      if (runtimeMessages) {
        let dismissableAlert = document.createElement("div");
        dismissableAlert.className = "alert alert-danger alert-dismissible fade show";
        dismissableAlert.setAttribute("role", "alert");
        dismissableAlert.innerHTML = `Runtime (WebCola) error when gridifying edges. You may have to click and drag these nodes slightly to un-stick layout.`;

        // Make sure we don't have duplicate alerts
        let existingAlerts = runtimeMessages.querySelectorAll(".alert");
        existingAlerts.forEach(alert => {
            if (alert.innerHTML === dismissableAlert.innerHTML) {
                alert.remove();
            }
        });

        runtimeMessages.appendChild(dismissableAlert);
      }
    } finally {
      // Always reset the guard flag, even if an error occurred
      this.isGridifyingInProgress = false;
    }
  }

  /**
   * Internal implementation of gridify, separated to ensure proper cleanup in finally block.
   */
  private gridifyInternal(nudgeGap: number, margin: number, groupMargin: number): void {
      const nodes = this.currentLayout?.nodes ?? [];
      const groups = this.currentLayout?.groups ?? [];
      const edges = this.currentLayout?.links ?? [];

      if (nodes.length === 0) {
        console.warn("No nodes available for GridRouter; skipping gridify.");
        return;
      }

      if (edges.length === 0) {
        console.warn("No edges to route in GridRouter");
        return;
      }

      // Force recompute all node bounds from current positions
      // This ensures bounds are always in sync with node x/y coordinates
      // Note: ensureNodeBounds provides default width/height (50/30) for nodes without explicit dimensions
      this.ensureNodeBounds(true);

      // Safety check: ensure all nodes have valid numeric positions AFTER bounds setup
      // Only check x/y positions - width/height are now guaranteed by ensureNodeBounds defaults
      // Invalid positions (NaN, Infinity) can cause GridRouter to hang
      const invalidNodes = nodes.filter((n: any) => 
        !Number.isFinite(n.x) || !Number.isFinite(n.y)
      );
      if (invalidNodes.length > 0) {
        console.warn('[gridify] Found nodes with invalid positions, falling back to default routing:', 
          invalidNodes.map((n: any) => ({ id: n.id, x: n.x, y: n.y })));
        // Fall back to simple orthogonal routing without GridRouter
        this.fallbackGridRouting(edges);
        return;
      }

      // Build/refresh port assignments so parallel edges use distinct entry/exit
      // points. Without this, multiple A→B edges share the same orthogonal route.
      this.buildEdgeRoutingCaches();
      this.sortEdgePortsByAngle();

      // Create the grid router
      const gridrouter = buildGridRouter(nodes, groups, margin, groupMargin);

      // Route all edges using the GridRouter
      let routes: any[] = [];
      
      // Filter edges for GridRouter:
      // 1. Must have routerNode on both source and target
      // 2. Must NOT be self-loops (source === target) - GridRouter can't handle these
      const routableEdges = edges.filter((edge: any) => {
        const hasRouterNodes = edge?.source?.routerNode && edge?.target?.routerNode;
        const isSelfLoop = edge?.source?.id === edge?.target?.id;
        return hasRouterNodes && !isSelfLoop;
      });
      
      // Identify self-loop edges for special handling
      const selfLoopEdges = edges.filter((edge: any) => edge?.source?.id === edge?.target?.id);
      
      // Every edge should be routable or a self-loop; anything else lost its
      // router node and is about to fall back to a dogleg, so say which.
      if (routableEdges.length + selfLoopEdges.length !== edges.length) {
        const unroutableEdges = edges.filter((edge: any) => 
          (!edge?.source?.routerNode || !edge?.target?.routerNode) && 
          edge?.source?.id !== edge?.target?.id
        );
        unroutableEdges.forEach((e: any) => {
          console.warn('[gridify] Unroutable edge:', e.id, 
            'source routerNode:', !!e?.source?.routerNode,
            'target routerNode:', !!e?.target?.routerNode,
            'source:', e?.source?.id, 'x:', e?.source?.x, 'y:', e?.source?.y,
            'target:', e?.target?.id, 'x:', e?.target?.x, 'y:', e?.target?.y);
        });
      }
      
      routes = gridrouter.routeEdges(
        routableEdges,
        nudgeGap,
        function (e: any) { return e.source.routerNode.id; },
        function (e: any) { return e.target.routerNode.id; }
      );

      const routesByEdgeId = new Map<string, any>();
      // Tier 1.4: only run bend flattening on graphs below the same threshold as
      // crossing optimization — keeps cost zero on dense graphs (matching policy
      // at CROSSING_OPTIMIZATION_EDGE_THRESHOLD).
      const shouldFlattenBends = routableEdges.length <= CROSSING_OPTIMIZATION_EDGE_THRESHOLD;
      routableEdges.forEach((edge: any, index: number) => {
        const route = routes[index];
        if (edge?.id && route) {
          let processed = this.adjustGridRouteForEdge(edge, route);
          if (shouldFlattenBends) {
            processed = flattenGridRouteBends(processed, nodes, edge);
          }
          routesByEdgeId.set(edge.id, processed);
        }
      });

      // Note: port distribution for parallel edges is applied at draw time
      // inside adjustGridRouteForArrowPositioning, after rectangle clipping —
      // doing it here would be overwritten by that pass.

      // Edges the router returned nothing for, collected during the draw pass
      // below and reported once: this runs per edge, and a graph that loses
      // its routes loses all of them at once.
      const fellBackTo: string[] = [];

      const linkGroups = this.container.selectAll(".link-group").data(edges, (d: any) => d.id ?? d);

      linkGroups.select("path")
        .attr("d", (edgeData: any) => {
          // Handle self-loops specially - GridRouter can't route these
          if (edgeData.source?.id === edgeData.target?.id) {
            if (this.isGroupSelfLoop(edgeData)) {
              return this.gridLineFunction(this.createGroupSelfLoopRoute(edgeData, true));
            }
            // Orthogonal "out and back" hook to match grid mode's rectilinear style
            const route = this.createGridSelfLoopRoute(edgeData);
            return this.gridLineFunction(route);
          }

          const route = routesByEdgeId.get(edgeData.id);
          if (!route) {
            // Create orthogonal fallback path for unroutable edges
            // Use node.x/y as primary source (same as default mode) with bounds as fallback
            const sourceX = edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0;
            const sourceY = edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0;
            const targetX = edgeData.target?.x ?? edgeData.target?.bounds?.cx() ?? 0;
            const targetY = edgeData.target?.y ?? edgeData.target?.bounds?.cy() ?? 0;
            
            fellBackTo.push(edgeData.id);

            const dx = targetX - sourceX;
            const dy = targetY - sourceY;
            
            // Choose routing based on dominant direction
            if (Math.abs(dx) > Math.abs(dy)) {
              const midX = sourceX + dx / 2;
              return this.gridLineFunction([
                { x: sourceX, y: sourceY },
                { x: midX, y: sourceY },
                { x: midX, y: targetY },
                { x: targetX, y: targetY }
              ]);
            } else {
              const midY = sourceY + dy / 2;
              return this.gridLineFunction([
                { x: sourceX, y: sourceY },
                { x: sourceX, y: midY },
                { x: targetX, y: midY },
                { x: targetX, y: targetY }
              ]);
            }
          }
          const cornerradius = 5;
          const arrowwidth = 3;
          const arrowheight = 7;
          
          // Get the route path from GridRouter
          const p = requireCola().GridRouter.getRoutePath(route, cornerradius, arrowwidth, arrowheight);
          
          // Adjust the route to properly terminate at node boundaries for arrow positioning
          // The GridRouter path may extend beyond the visual node bounds
          const adjustedPath = this.adjustGridRouteForArrowPositioning(edgeData, p.routepath, route);
          return adjustedPath || p.routepath;
        });

      if (fellBackTo.length > 0) {
        console.warn(
          `[gridify] ${fellBackTo.length} of ${routableEdges.length} edges had no grid route ` +
          `and were drawn as straight doglegs: ${fellBackTo.slice(0, 10).join(', ')}` +
          (fellBackTo.length > 10 ? `, and ${fellBackTo.length - 10} more` : '')
        );
      }

      // NOTE: Do NOT update node positions here!
      // gridify() should only reroute edges, not move nodes.
      // Node positions are set by the layout algorithm (cola) and should not change when switching routing modes.
      // The edges use the same node.x/y coordinates as default mode, ensuring consistency.
      
      // NOTE: Do NOT update group or label positions either - keep them consistent with default mode.
      
      // Position link labels at route midpoints
      this.gridUpdateLinkLabels(edges, routesByEdgeId);

      this.fitViewportToContent();

      // Dispatch event that relations are available
      this.dispatchEvent(new Event('relationsAvailable', ));
  }

  /**
   * Fallback grid routing that uses simple orthogonal paths without the GridRouter.
   * This is used when GridRouter fails or when nodes have invalid positions.
   */
  private fallbackGridRouting(edges: any[]): void {
    const linkGroups = this.container.selectAll(".link-group").data(edges, (d: any) => d.id ?? d);

    linkGroups.select("path")
      .attr("d", (edgeData: any) => {
        // Handle self-loops specially
        if (edgeData.source?.id === edgeData.target?.id) {
          if (this.isGroupSelfLoop(edgeData)) {
            return this.gridLineFunction(this.createGroupSelfLoopRoute(edgeData, true));
          }
          const route = this.createGridSelfLoopRoute(edgeData);
          return this.gridLineFunction(route);
        }

        // Create orthogonal fallback path
        const sourceX = edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0;
        const sourceY = edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0;
        const targetX = edgeData.target?.x ?? edgeData.target?.bounds?.cx() ?? 0;
        const targetY = edgeData.target?.y ?? edgeData.target?.bounds?.cy() ?? 0;
        
        const dx = targetX - sourceX;
        const dy = targetY - sourceY;
        
        // Choose routing based on dominant direction
        if (Math.abs(dx) > Math.abs(dy)) {
          const midX = sourceX + dx / 2;
          return this.gridLineFunction([
            { x: sourceX, y: sourceY },
            { x: midX, y: sourceY },
            { x: midX, y: targetY },
            { x: targetX, y: targetY }
          ]);
        } else {
          const midY = sourceY + dy / 2;
          return this.gridLineFunction([
            { x: sourceX, y: sourceY },
            { x: sourceX, y: midY },
            { x: targetX, y: midY },
            { x: targetX, y: targetY }
          ]);
        }
      });

    this.fitViewportToContent();
  }

  private gridUpdateLinkLabels(edges: any[], routesByEdgeId: Map<string, any>) {
    const linkGroups = this.container.selectAll(".link-group");

    linkGroups
      .filter((d: any) => !this.isAlignmentEdge(d))
      .select("text.linklabel")
      .attr("x", (edgeData: any, i: number, nodes: any) => {
        // Use the actual rendered path element to find the true midpoint
        const pathElement = this.getLinkPathElement(nodes[i]);
        if (pathElement) {
          try {
            const pathLength = pathElement.getTotalLength();
            const midpoint = pathElement.getPointAtLength(pathLength / 2);
            return midpoint.x;
          } catch (e) {
            // Fallback if path is not yet rendered
          }
        }
        const midpoint = gridRouteMidpoint(edgeData, routesByEdgeId);
        // Use node.x/y as primary source (same as default mode)
        return midpoint?.x ?? (edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0);
      })
      .attr("y", (edgeData: any, i: number, nodes: any) => {
        // Use the actual rendered path element to find the true midpoint
        const pathElement = this.getLinkPathElement(nodes[i]);
        if (pathElement) {
          try {
            const pathLength = pathElement.getTotalLength();
            const midpoint = pathElement.getPointAtLength(pathLength / 2);
            return midpoint.y;
          } catch (e) {
            // Fallback if path is not yet rendered
          }
        }
        const midpoint = gridRouteMidpoint(edgeData, routesByEdgeId);
        // Use node.x/y as primary source (same as default mode)
        return midpoint?.y ?? (edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0);
      })
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle");
  }

  private adjustGridRouteForEdge(edgeData: any, route: any[]) {
    if (!this.hasGroupEndpoints(edgeData)) {
      return route;
    }

    const points = gridRouteToPoints(route);
    if (points.length < 2) {
      return route;
    }

    const adjustedPoints = this.routeGroupEdge(edgeData, points);
    return pointsToGridRoute(adjustedPoints);
  }

  /**
   * Adjust grid route path for proper arrow positioning at node boundaries.
   * Ensures the path terminates at the node boundary rather than center.
   */
  private adjustGridRouteForArrowPositioning(edgeData: any, routePath: string, route: any[]): string | null {
    if (!routePath || !edgeData.source || !edgeData.target) {
      return null;
    }

    try {
      // Extract points from the route
      const points = gridRouteToPoints(route);
      if (points.length < 2) {
        return null;
      }

      const source = edgeData.source;
      const target = edgeData.target;

      // Use the *visible* rectangle (visualWidth/visualHeight for plain nodes,
      // inset bounds for groups) so the arrowhead lands on the rendered border
      // rather than on WebCola's inflated collision rectangle.
      const sourceBounds = visibleBounds(source) ?? source.bounds ?? {
        x: source.x - (source.width || 0) / 2,
        y: source.y - (source.height || 0) / 2,
        width: () => source.width || 0,
        height: () => source.height || 0
      };
      const targetBounds = visibleBounds(target) ?? target.bounds ?? {
        x: target.x - (target.width || 0) / 2,
        y: target.y - (target.height || 0) / 2,
        width: () => target.width || 0,
        height: () => target.height || 0
      };

      // Check if nodes are near-touching and determine which direction
      const NEAR_TOUCH_THRESHOLD = 5;
      const touching = touchDirection(sourceBounds, targetBounds, NEAR_TOUCH_THRESHOLD);

      if (touching !== 'none') {
        // Nodes are near-touching, route edge from perpendicular sides
        const { sourcePoint, targetPoint, middlePoints } = perpendicularRoute(
          sourceBounds, targetBounds, touching
        );
        
        // Build new path: source anchor -> middle waypoints -> target anchor
        const newPoints = [sourcePoint, ...middlePoints, targetPoint];
        return this.gridLineFunction(newPoints);
      }

      // Standard case: clip endpoints to the visible boundary while preserving
      // orthogonality of the entry/exit segments. GridRouter outputs axis-aligned
      // segments, so we just snap route[0]/route[last] along the egress axis to
      // the visible side. Falls back to a center-line rectangle intersection if
      // the original segment isn't orthogonal (defensive — shouldn't happen).
      points[0] = clipEndpointToVisibleBoundary(points[0], points[1], sourceBounds);
      points[points.length - 1] = clipEndpointToVisibleBoundary(
        points[points.length - 1],
        points[points.length - 2],
        targetBounds
      );

      // Distribute parallel edges across distinct ports on the visible boundary
      // and re-orthogonalize the entry/exit segments. Must run AFTER rectangle
      // clipping so the port shift isn't overwritten.
      const portAdjusted = applyPortBasedEndpointsOrthogonal(edgeData, points);

      // Final cleanup: the clip and port-shift steps above introduce backtrack
      // spurs (an L-bend that doubles back along the entry axis) and small
      // stair-steps. The gridify-time flatten pass ran BEFORE these draw-time
      // adjustments, so it can't see them — clean the final polyline here.
      const cleaned = cleanupOrthogonalRoute(
        portAdjusted,
        this.currentLayout?.nodes ?? [],
        edgeData,
        (this.currentLayout?.links?.length ?? 0) <= CROSSING_OPTIMIZATION_EDGE_THRESHOLD
      );

      // Convert points back to SVG path using grid line function for sharp turns
      return this.gridLineFunction(cleaned);
    } catch (error) {
      console.warn('Error adjusting grid route for arrow positioning:', error);
      return null;
    }
  }

  /**
   * Stage 1: Compute route arrays for all edges and store in computedRoutes map.
   * This enables post-routing optimization before SVG rendering.
   */
  private computeAllRoutes(): void {
    this.computedRoutes.clear();

    this.container.selectAll('.link-group path').each((d: any) => {
      try {
        const route = this.computeSingleRoute(d);
        if (route) {
          this.computedRoutes.set(d.id, route);
        }
      } catch (error) {
        console.error(`Error routing edge ${d.id} from ${d.source.id} to ${d.target.id}:`, error);
        this.showRuntimeAlert(d.source.id, d.target.id);
        // Fallback to simple line
        this.computedRoutes.set(d.id, [
          { x: d.source.x || 0, y: d.source.y || 0 },
          { x: d.target.x || 0, y: d.target.y || 0 }
        ]);
      }
    });
  }

  /**
   * Stage 2: Apply computed routes to SVG path elements.
   */
  private applyRoutesToSVG(): void {
    this.container.selectAll('.link-group path')
      .attr('d', (d: any) => {
        const route = this.computedRoutes.get(d.id);
        if (!route) return null;
        // Taut router: interpolate the routed polyline exactly with bounded
        // fillets (no curveBasis bulge). Self-loops and alignment edges keep the
        // legacy smoothing — their routes are built for it / are straight.
        if (d.source.id !== d.target.id && !this.isAlignmentEdge(d)) {
          return filletPath(route);
        }
        return this.lineFunction(addTangentGuides(route));
      });
  }

  /**
   * True for edges the standard pipeline hands to the pluggable router: the
   * complement of computeSingleRoute's special cases (alignment edges,
   * self-loops, group-attached edges). Also drives RouterHost.routerEdges(),
   * so batch/finalize passes see exactly the edges routeEdge will be asked
   * about.
   */
  private isRouterEdge(d: any): boolean {
    return !this.isAlignmentEdge(d)
      && d?.source?.id !== d?.target?.id
      && !(d?.id?.startsWith('_g_') || d?.sourceGroupId || d?.targetGroupId);
  }

  /**
   * Computes route points for a single edge.
   * Returns an array of {x, y} points (not an SVG path string).
   *
   * @param edgeData - The edge data object
   * @returns Array of route points, or null if edge should not be rendered
   */
  private computeSingleRoute(edgeData: any): Array<{ x: number; y: number }> | null {
    // Common case: a plain node-to-node edge goes to the active pluggable
    // router (taut by default — see ./routing).
    if (this.isRouterEdge(edgeData)) {
      return this.edgeRouter.routeEdge(edgeData, this.routerHost());
    }

    // Alignment edges are invisible guides — a straight segment suffices.
    if (this.isAlignmentEdge(edgeData)) {
      return [
        { x: edgeData.source.x || 0, y: edgeData.source.y || 0 },
        { x: edgeData.target.x || 0, y: edgeData.target.y || 0 }
      ];
    }

    // Self-loops: build the petal route directly. WebCola's routeEdge can't
    // produce a usable polyline for self-loops (source == target collapses to
    // a single point, failing the length>=2 check), and the previous
    // implementation would silently fall back to a degenerate 2-point route
    // with both endpoints at the node center — rendering as an empty path.
    // inferredEdge `draw` edges with group stamps are exempt: their anchors may
    // coincide (two groups sharing a member) while the rendered endpoints — the
    // hulls — differ. `_g_` connectors are NOT exempt: a key-is-its-own-anchor
    // connector kept its historical self-loop rendering through the stamp
    // unification, so desugared connectors stay on this path.
    if (this.isGroupSelfLoop(edgeData)) {
      // Both ends are the same group: a self-loop petal on the hull.
      return this.createGroupSelfLoopRoute(edgeData);
    }
    if (edgeData.source.id === edgeData.target.id
        && (edgeData.id?.startsWith('_g_')
            || !(edgeData.sourceGroupId || edgeData.targetGroupId))) {
      return this.createSelfLoopRoute(edgeData);
    }

    // Everything left is group-attached: the boundary-snap contract.
    return this.computeGroupEdgeRoute(edgeData);
  }

  /**
   * Routes a group-attached edge: feed perimeter clip points so
   * routeGroupEdge's member-snap reference is correct, then snap the
   * group-attached end(s) to the group boundary.
   */
  private computeGroupEdgeRoute(edgeData: any): Array<{ x: number; y: number }> {
    const sB = renderedBounds(edgeData.source);
    const tB = renderedBounds(edgeData.target);
    const sC = { x: sB.x + sB.width() / 2, y: sB.y + sB.height() / 2 };
    const tC = { x: tB.x + tB.width() / 2, y: tB.y + tB.height() / 2 };
    const seed = [
      clipLineToRectExit(sC, tC, sB),
      clipLineToRectExit(tC, sC, tB),
    ];
    return this.routeGroupEdge(edgeData, seed);
  }

  /**
   * Builds routerObstacleCache: one inflated visible-rectangle per node. Called
   * once per routeEdges pass, after positions are frozen, so the expensive
   * getRenderedBounds work happens N times instead of E·N times.
   */
  private buildRouterObstacleCache(): void {
    const cache: Array<{ id: string; minX: number; minY: number; maxX: number; maxY: number }> = [];
    const c = EDGE_CLEARANCE_PX;
    for (const node of this.currentLayout?.nodes || []) {
      const b = renderedBounds(node);
      const w = b.width(), h = b.height();
      if (w <= 0 && h <= 0) continue;
      cache.push({ id: node.id, minX: b.x - c, minY: b.y - c, maxX: b.x + w + c, maxY: b.y + h + c });
    }
    this.routerObstacleCache = cache;
  }

  /**
   * Returns the obstacle set for the taut router: every node except the edge's
   * own source and target, as its *visible* rectangle inflated by
   * EDGE_CLEARANCE_PX. Groups are not obstacles (inter-group edges must cross
   * boundaries — matches the legacy direct-line fast path).
   *
   * Reuses routerObstacleCache when inside a routeEdges pass (an O(N) filter);
   * otherwise (e.g. unit tests calling this directly) builds the list inline.
   */
  private buildRouterObstacles(
    edgeData: any
  ): Array<{ minX: number; minY: number; maxX: number; maxY: number }> {
    const srcId = edgeData.source.id, tgtId = edgeData.target.id;
    if (this.routerObstacleCache) {
      return this.routerObstacleCache.filter(o => o.id !== srcId && o.id !== tgtId);
    }
    const obstacles: Array<{ minX: number; minY: number; maxX: number; maxY: number }> = [];
    if (!this.currentLayout?.nodes) return obstacles;
    const c = EDGE_CLEARANCE_PX;
    for (const node of this.currentLayout.nodes) {
      if (node.id === srcId || node.id === tgtId) continue;
      const b = renderedBounds(node);
      const w = b.width(), h = b.height();
      if (w <= 0 && h <= 0) continue;
      obstacles.push({ minX: b.x - c, minY: b.y - c, maxX: b.x + w + c, maxY: b.y + h + c });
    }
    return obstacles;
  }


  /**
   * Creates a self-loop route for edges that connect a node to itself.
   *
   * Geometry: a *tulip petal* — start and end land on the same side of the
   * node, close together, and the curve bulges out perpendicular to that
   * side, forming a teardrop. Loops distribute around all four sides (in
   * the order right → bottom → left → top), so a node with two self-loops
   * gets one petal on the right and one on the bottom rather than two
   * stacked on the top. The 5th+ loop on a node goes into an outer ring
   * (further bulge).
   *
   * The 5-point polyline gives curveBasis enough control to produce both a
   * rounded apex and arrival tangents perpendicular to the side, so the
   * arrowhead points back into the node rather than skimming along the
   * perimeter.
   *
   * @param edgeData - The edge data object
   * @returns Array of route points for the self-loop
   */
  private createSelfLoopRoute(edgeData: any, boundsOverride?: any, indexOverride?: number): Array<{ x: number; y: number }> {
    const source = edgeData.source;
    const bounds = boundsOverride ?? source.bounds;

    if (!bounds) {
      // Fallback for missing bounds — minimal upward petal.
      return [
        { x: source.x - 8, y: source.y },
        { x: source.x - 8, y: source.y - 20 },
        { x: source.x,     y: source.y - 28 },
        { x: source.x + 8, y: source.y - 20 },
        { x: source.x + 8, y: source.y },
      ];
    }

    const width = bounds.X - bounds.x;
    const height = bounds.Y - bounds.y;
    const cx = bounds.x + width / 2;
    const cy = bounds.y + height / 2;

    const selfLoopIndex = indexOverride ?? this.getSelfLoopIndex(edgeData);
    const sideIdx = selfLoopIndex % 4;
    const ring = Math.floor(selfLoopIndex / 4);
    const ringScale = 1 + ring * WebColaCnDGraph.SELF_LOOP_CURVATURE_SCALE;

    // Side geometry: pick a "center" on the side, an outward unit normal,
    // and a unit tangent along the side. The petal opens outward; start and
    // end are placed symmetrically along the tangent.
    let sideCenter: { x: number; y: number };
    let outwardX = 0, outwardY = 0;
    let alongX = 0, alongY = 0;
    switch (sideIdx) {
      case 0: // right
        sideCenter = { x: bounds.X, y: cy };
        outwardX = 1;  alongX = 0;  outwardY = 0;  alongY = 1;
        break;
      case 1: // bottom
        sideCenter = { x: cx, y: bounds.Y };
        outwardX = 0;  alongX = -1; outwardY = 1;  alongY = 0;
        break;
      case 2: // left
        sideCenter = { x: bounds.x, y: cy };
        outwardX = -1; alongX = 0;  outwardY = 0;  alongY = -1;
        break;
      case 3: // top
      default:
        sideCenter = { x: cx, y: bounds.y };
        outwardX = 0;  alongX = 1;  outwardY = -1; alongY = 0;
        break;
    }

    // Spread = how far apart start and end sit along the side. Bulge = how
    // far the apex extends perpendicular to the side. Both scale with the
    // shorter node dimension so petals stay proportional on long-thin
    // rectangles, and grow with the ring index so outer-ring petals are
    // visibly distinct from inner ones.
    const minDim = Math.min(width, height);
    const sideLen = (sideIdx === 0 || sideIdx === 2) ? height : width;
    const spread = Math.min(sideLen * 0.45, minDim * 0.5);
    const bulge = minDim * 0.7 * ringScale;
    const tangentLen = spread * 0.55; // sets how sharply the petal "opens"

    const startPoint = {
      x: sideCenter.x - alongX * spread / 2,
      y: sideCenter.y - alongY * spread / 2,
    };
    const endPoint = {
      x: sideCenter.x + alongX * spread / 2,
      y: sideCenter.y + alongY * spread / 2,
    };
    // Tangent guides at start/end push the curve outward perpendicular to
    // the side; the apex closes the bulge at the petal's far point.
    const ctrlA = {
      x: startPoint.x + outwardX * tangentLen,
      y: startPoint.y + outwardY * tangentLen,
    };
    const ctrlB = {
      x: endPoint.x + outwardX * tangentLen,
      y: endPoint.y + outwardY * tangentLen,
    };
    const apex = {
      x: sideCenter.x + outwardX * bulge,
      y: sideCenter.y + outwardY * bulge,
    };

    return [startPoint, ctrlA, apex, ctrlB, endPoint];
  }

  /**
   * Grid-mode self-loop route: an orthogonal "petal" — a rectangular bump
   * perpendicular to one side of the node. Mirrors the side / ring
   * distribution from createSelfLoopRoute() (the curvy default-mode petal)
   * so the two routing modes agree on which side each self-loop lives on.
   *
   * Geometry (e.g. for top-side petal):
   *   p0 = (cx - spread/2, top)   ← start
   *   p1 = (cx - spread/2, top - bulge)
   *   p2 = (cx + spread/2, top - bulge)
   *   p3 = (cx + spread/2, top)   ← end
   * Drawn with the rectilinear gridLineFunction → three right-angle bends.
   */
  private createGridSelfLoopRoute(edgeData: any, boundsOverride?: any, indexOverride?: number): Array<{ x: number; y: number }> {
    const source = edgeData.source;
    const bounds = boundsOverride ?? source.bounds;

    if (!bounds) {
      return [
        { x: source.x - 10, y: source.y },
        { x: source.x - 10, y: source.y - 24 },
        { x: source.x + 10, y: source.y - 24 },
        { x: source.x + 10, y: source.y },
      ];
    }

    const width = bounds.X - bounds.x;
    const height = bounds.Y - bounds.y;
    const cx = bounds.x + width / 2;
    const cy = bounds.y + height / 2;

    const selfLoopIndex = indexOverride ?? this.getSelfLoopIndex(edgeData);
    const sideIdx = selfLoopIndex % 4;
    const ring = Math.floor(selfLoopIndex / 4);
    const ringScale = 1 + ring * WebColaCnDGraph.SELF_LOOP_CURVATURE_SCALE;

    // Spread and bulge scaled to the node so petals stay proportional.
    const minDim = Math.min(width, height);
    const sideLen = (sideIdx === 0 || sideIdx === 2) ? height : width;
    const spread = Math.min(sideLen * 0.45, minDim * 0.5);
    const bulge = minDim * 0.55 * ringScale;

    let p0: { x: number; y: number };
    let p1: { x: number; y: number };
    let p2: { x: number; y: number };
    let p3: { x: number; y: number };

    switch (sideIdx) {
      case 0: // right side: bump out to the right
        p0 = { x: bounds.X,           y: cy - spread / 2 };
        p1 = { x: bounds.X + bulge,   y: cy - spread / 2 };
        p2 = { x: bounds.X + bulge,   y: cy + spread / 2 };
        p3 = { x: bounds.X,           y: cy + spread / 2 };
        break;
      case 1: // bottom side: bump down
        p0 = { x: cx + spread / 2,    y: bounds.Y };
        p1 = { x: cx + spread / 2,    y: bounds.Y + bulge };
        p2 = { x: cx - spread / 2,    y: bounds.Y + bulge };
        p3 = { x: cx - spread / 2,    y: bounds.Y };
        break;
      case 2: // left side: bump to the left
        p0 = { x: bounds.x,           y: cy + spread / 2 };
        p1 = { x: bounds.x - bulge,   y: cy + spread / 2 };
        p2 = { x: bounds.x - bulge,   y: cy - spread / 2 };
        p3 = { x: bounds.x,           y: cy - spread / 2 };
        break;
      case 3: // top side: bump up
      default:
        p0 = { x: cx - spread / 2,    y: bounds.y };
        p1 = { x: cx - spread / 2,    y: bounds.y - bulge };
        p2 = { x: cx + spread / 2,    y: bounds.y - bulge };
        p3 = { x: cx + spread / 2,    y: bounds.y };
        break;
    }

    return [p0, p1, p2, p3];
  }

  /**
   * Returns the 0-based index of this self-loop among all self-loops on its node.
   * Uses the edgesBetweenNodes cache when available.
   */
  private getSelfLoopIndex(edgeData: any): number {
    const nodeId = edgeData.source.id;
    const key = this.getNodePairKey(nodeId, nodeId);
    const selfLoops = this.edgeRoutingCache.edgesBetweenNodes.get(key);
    if (selfLoops) {
      // Group self-loops share the node-pair key (both anchors on one member)
      // but render on the hull — don't let them consume node-petal sides.
      const nodeLoops = selfLoops.filter((e: any) => !this.isGroupSelfLoop(e));
      const idx = nodeLoops.findIndex((e: any) => e.id === edgeData.id);
      return idx >= 0 ? idx : 0;
    }
    // Fallback: scan all links
    if (this.currentLayout?.links) {
      let idx = 0;
      for (const edge of this.currentLayout.links as any[]) {
        if (edge.source.id === nodeId && edge.target.id === nodeId && !this.isGroupSelfLoop(edge)) {
          if (edge.id === edgeData.id) return idx;
          idx++;
        }
      }
    }
    return 0;
  }

  /**
   * Routes group edges: snaps each stamped end (sourceGroupId / targetGroupId,
   * from inferredEdge `draw` or the `addEdge` connector desugar) to its own
   * group's boundary; un-stamped ends keep their node endpoint. Edges with no
   * stamps (the group-by-field middle-key connector corner) get no snapping —
   * only the waypoint simplification, matching their historical rendering.
   *
   * @param edgeData - The edge data object
   * @param route - Initial route points
   * @returns Modified route points for group edge
   */
  private routeGroupEdge(edgeData: any, route: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
    const groups = this.currentLayout?.groups || [];
    const sGroup = edgeData.sourceGroupId ? groups.find((g: any) => g.id === edgeData.sourceGroupId) : null;
    const tGroup = edgeData.targetGroupId ? groups.find((g: any) => g.id === edgeData.targetGroupId) : null;
    if (edgeData.sourceGroupId && !sGroup) {
      console.warn('[routeGroupEdge] Source group not found for edge:', edgeData.id, '— sourceGroupId:', edgeData.sourceGroupId);
    }
    if (edgeData.targetGroupId && !tGroup) {
      console.warn('[routeGroupEdge] Target group not found for edge:', edgeData.id, '— targetGroupId:', edgeData.targetGroupId);
    }

    // route[0] is near the source anchor, route[last] near the target anchor —
    // member-anchored positions, good synthesis references when WebCola hasn't
    // computed group bounds yet (stability / few-iteration layouts).
    if (sGroup) this.ensureGroupBounds(sGroup, route[0]);
    if (tGroup) this.ensureGroupBounds(tGroup, route[route.length - 1]);

    const centerOf = (b: any) => (typeof b.cx === 'function')
      ? { x: b.cx(), y: b.cy() }
      : { x: (b.x + b.X) / 2, y: (b.y + b.Y) / 2 };

    // Aim each snap at the opposite end: its hull centre when it has one,
    // else its route endpoint (a node-boundary clip point).
    const srcRef = sGroup?.bounds ? centerOf(sGroup.bounds) : route[0];
    const tgtRef = tGroup?.bounds ? centerOf(tGroup.bounds) : route[route.length - 1];

    if (tGroup?.bounds) {
      route[route.length - 1] = closestPointOnRect(tGroup.bounds, srcRef);
    }
    if (sGroup?.bounds) {
      const bounds = sGroup.bounds.inflate?.(-1) ?? sGroup.bounds;
      // Aim at the (possibly just-snapped) target point for a taut line.
      route[0] = closestPointOnRect(bounds, tGroup?.bounds ? route[route.length - 1] : tgtRef);
    }

    // Simplify route — remove intermediate waypoints for group edges.
    if (route.length > 2) {
      route.splice(1, route.length - 2);
    }

    return route;
  }

  /**
   * Handles routing for multiple edges between the same pair of nodes.
   * Applies curvature and offset to prevent edge overlap.
   * 
   * @param edgeData - The edge data object
   * @param route - Initial route points
   * @returns Modified route points with curvature and offset
   */
  private handleMultipleEdgeRouting(
    edgeData: any,
    route: Array<{ x: number; y: number }>,
    curvatureScale: number = 1
  ): Array<{ x: number; y: number }> {
    const allEdgesBetweenNodes = this.getAllEdgesBetweenNodes(edgeData.source.id, edgeData.target.id);

    // Early return for single edge - no curvature needed
    if (allEdgesBetweenNodes.length <= 1) {
      return route;
    }

    // Add midpoint if route only has start and end
    if (route.length === 2) {
      const midpoint = {
        x: (route[0].x + route[1].x) / 2,
        y: (route[0].y + route[1].y) / 2
      };
      route.splice(1, 0, midpoint);
    }

    // The two mechanisms need two different directions, and sharing one was the
    // bug: an endpoint offset slides ports ALONG the node side the route leaves
    // from, so its axis comes from the exit normal — the route's first segment.
    // A curvature bows the route sideways, so its axis comes from where the
    // edge actually travels, end to end. On a taut route those differ by 90°:
    // the first segment is the perpendicular exit stub, and bowing across it
    // stretches the route along its own direction of travel instead of moving
    // it off its siblings (two wrap-around edges over the same row of nodes
    // stayed exactly on top of each other, each overshooting the row).
    const exitAngle = Math.atan2(route[1].y - route[0].y, route[1].x - route[0].x);
    const last = route.length - 1;
    const travelAngle = Math.atan2(route[last].y - route[0].y, route[last].x - route[0].x);

    // Endpoint offsets are capped against the room the whole route has; the bow
    // is a fraction of how far apart the ENDS are. Charging it the full
    // polyline length instead made a long detour bow in proportion to its own
    // detour, which is how an 800px route ended up 500px off the diagram.
    const routeLength = getRouteLength(route);
    const endSpan = Math.hypot(route[last].x - route[0].x, route[last].y - route[0].y);

    // Find edge index once and reuse for both offset and curvature
    const edgeIndex = allEdgesBetweenNodes.findIndex(edge => edge.id === edgeData.id);

    // Apply offset and curvature only if we found the edge. curvatureScale < 1
    // shrinks the fan bulge (used by the taut router to back off a fan that
    // would clip an obstacle) — endpoint/port offsets are not scaled because
    // they stay on the node perimeter by construction.
    if (edgeIndex !== -1) {
      route = applyEdgeOffsetWithIndex(edgeData, route, exitAngle, edgeIndex, routeLength);
      const curvature = calculateCurvatureWithIndex(allEdgesBetweenNodes, edgeIndex);
      const cappedCurvature = clampCurvature(curvature) * curvatureScale;
      route = applyCurvatureToRoute(route, cappedCurvature, travelAngle, endSpan);
    }

    return route;
  }

  /**
   * Gets all non-alignment edges between two nodes (bidirectional).
   * Uses cached results for O(1) lookup performance during edge routing.
   * 
   * @param sourceId - Source node ID
   * @param targetId - Target node ID
   * @returns Array of edges between the nodes
   */
  private getAllEdgesBetweenNodes(sourceId: string, targetId: string): EdgeWithMetadata[] {
    if (!this.currentLayout?.links) return [];
    
    // Use cache if available (during routing phase)
    const key = this.getNodePairKey(sourceId, targetId);
    if (this.edgeRoutingCache.edgesBetweenNodes.has(key)) {
      return this.edgeRoutingCache.edgesBetweenNodes.get(key)!;
    }
    
    // Fallback to direct filtering if cache not built (shouldn't happen during routing)
    return this.currentLayout.links.filter((edge: EdgeWithMetadata) => {
      return !this.isAlignmentEdge(edge) && (
        (edge.source.id === sourceId && edge.target.id === targetId) ||
        (edge.source.id === targetId && edge.target.id === sourceId)
      );
    });
  }

  /**
   * Updates link label positions after edge routing is complete.
   *
   * Pipeline:
   *   1. Position each label at the midpoint of its routed path.
   *   2. Run a batch overlap-resolution pass that pushes overlapping labels
   *      apart along the smaller-overlap axis (Tier 1.1 of the visual-polish
   *      plan — see resolveLinkLabelOverlaps).
   *   3. Raise labels above edges so the white halo from `paint-order: stroke
   *      fill` reads correctly.
   */
  private updateLinkLabelsAfterRouting(): void {
    this.container.selectAll('.link-group .linklabel')
      .attr('x', (d: any, i: number, nodes: any) => {
        const pathElement = this.getLinkPathElement(nodes[i]);
        if (!pathElement) return 0;

        const pathLength = pathElement.getTotalLength();
        const midpoint = pathElement.getPointAtLength(pathLength / 2);
        return midpoint.x;
      })
      .attr('y', (d: any, i: number, nodes: any) => {
        const pathElement = this.getLinkPathElement(nodes[i]);
        if (!pathElement) return 0;

        const pathLength = pathElement.getTotalLength();
        const midpoint = pathElement.getPointAtLength(pathLength / 2);
        return midpoint.y;
      })
      .attr('text-anchor', 'middle');

    this.resolveLinkLabelOverlaps();

    this.container.selectAll('.link-group .linklabel').raise();
  }

  /**
   * Pushes overlapping link labels apart so they don't stack on top of each
   * other when multiple parallel edges or dense regions place several labels
   * at the same midpoint (Tier 1.1 of the visual-polish plan).
   *
   * Algorithm: minimum-translation-vector (MTV) pairwise pushaway, repeated
   * for up to MAX_LABEL_OVERLAP_PASSES passes. For each overlapping pair, both
   * labels move half the overlap (plus a small margin) along the *smaller*
   * overlap axis — the axis where moving disrupts position the least.
   *
   * Per-label cumulative displacement is capped at MAX_LABEL_DISPLACEMENT_PX
   * so labels never drift far from their edge midpoints. If an overlap can't
   * be resolved within the cap, we accept the residual rather than fly the
   * label off the canvas.
   *
   * Cost: O(N² × passes) where N = number of labels. Called once per layout
   * after routing, not per-tick — fine for typical graphs (≤ a few hundred
   * labels). For sparse graphs the inner loop short-circuits on `isOverlapping`.
   */
  private resolveLinkLabelOverlaps(): void {
    // `container` is the untyped vendored d3, so selectAll takes no type args —
    // assert the element type on the way out instead.
    const labels = Array.from(
      this.container.selectAll('.link-group .linklabel').nodes()
    ) as SVGTextElement[];
    if (labels.length < 2) return;

    const MAX_PASSES = 4;
    const MAX_DISPLACEMENT_PX = 28;
    const MARGIN_PX = 2;

    // Track cumulative displacement per label so we can clamp to MAX_DISPLACEMENT_PX.
    const dxMap = new Map<SVGTextElement, number>();
    const dyMap = new Map<SVGTextElement, number>();

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      let resolvedAny = false;

      for (let i = 0; i < labels.length; i++) {
        const a = labels[i];
        for (let j = i + 1; j < labels.length; j++) {
          const b = labels[j];
          if (!isOverlapping(a, b)) continue;

          const ba = a.getBBox();
          const bb = b.getBBox();

          const cax = ba.x + ba.width / 2;
          const cay = ba.y + ba.height / 2;
          const cbx = bb.x + bb.width / 2;
          const cby = bb.y + bb.height / 2;

          const overlapX = (ba.width + bb.width) / 2 - Math.abs(cax - cbx);
          const overlapY = (ba.height + bb.height) / 2 - Math.abs(cay - cby);
          if (overlapX <= 0 || overlapY <= 0) continue;

          // Push along the axis with the smaller overlap (less movement).
          // For identical-position labels the sign falls out of the >= compare,
          // giving a deterministic split.
          if (overlapY <= overlapX) {
            const push = (overlapY + MARGIN_PX) / 2;
            const sign = cay >= cby ? 1 : -1;
            applyLabelDisplacement(a, 0, sign * push, dxMap, dyMap, MAX_DISPLACEMENT_PX);
            applyLabelDisplacement(b, 0, -sign * push, dxMap, dyMap, MAX_DISPLACEMENT_PX);
          } else {
            const push = (overlapX + MARGIN_PX) / 2;
            const sign = cax >= cbx ? 1 : -1;
            applyLabelDisplacement(a, sign * push, 0, dxMap, dyMap, MAX_DISPLACEMENT_PX);
            applyLabelDisplacement(b, -sign * push, 0, dxMap, dyMap, MAX_DISPLACEMENT_PX);
          }
          resolvedAny = true;
        }
      }

      if (!resolvedAny) break;
    }
  }

  /**
   * Fits the viewport to show all content with appropriate zoom and pan.
   * Uses D3 zoom transform for smooth, consistent behavior.
   * Only performs fit if:
   * - This is the initial render, OR
   * - User has not manually zoomed/panned, OR
   * - Force parameter is true (e.g., from reset button)
   * 
   * @param force - If true, fit regardless of user interaction state
   */
  private fitViewportToContent(force: boolean = false): void {
    const svgElement = this.svg?.node();
    if (!svgElement || !this.zoomBehavior) return;
    
    // Skip if user has manually zoomed and this isn't the initial render or forced
    if (this.userHasManuallyZoomed && !this.isInitialRender && !force) {
      return;
    }

    // Calculate content bounds
    const bounds = this.calculateContentBounds();
    if (!bounds) return;
    
    // Get container dimensions
    const containerWidth = svgElement.clientWidth || svgElement.parentElement?.clientWidth || 800;
    const containerHeight = svgElement.clientHeight || svgElement.parentElement?.clientHeight || 600;
    
    // Calculate padding
    const padding = WebColaCnDGraph.VIEWBOX_PADDING * 4; // Increase padding for comfortable view
    
    // Calculate scale to fit content
    const scaleX = (containerWidth - padding * 2) / bounds.width;
    const scaleY = (containerHeight - padding * 2) / bounds.height;
    const scale = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 1:1
    
    // Clamp scale to zoom extent
    const [minScale, maxScale] = this.zoomBehavior.scaleExtent();
    const clampedScale = Math.max(minScale, Math.min(maxScale, scale));
    
    // Calculate center of content
    const contentCenterX = bounds.x + bounds.width / 2;
    const contentCenterY = bounds.y + bounds.height / 2;
    
    // Calculate translation to center content
    const translateX = containerWidth / 2 - contentCenterX * clampedScale;
    const translateY = containerHeight / 2 - contentCenterY * clampedScale;
    
    // Create the transform
    const transform = d3.zoomIdentity
      .translate(translateX, translateY)
      .scale(clampedScale);
    
    // Apply with smooth transition (or instant on initial render)
    if (this.isInitialRender) {
      // Instant on first render
      this.svg.call(this.zoomBehavior.transform, transform);
      this.isInitialRender = false;
    } else {
      // Smooth transition for subsequent fits
      this.svg.transition()
        .duration(300)
        .ease(d3.easeCubicOut)
        .call(this.zoomBehavior.transform, transform);
    }
    
    // Update control states after transform
    this.updateZoomControlStates();
  }
  
  /**
   * Resets the view to fit all content, clearing user zoom state.
   * Called when user clicks the reset/fit button.
   */
  public resetViewToFitContent(): void {
    this.userHasManuallyZoomed = false;
    this.fitViewportToContent(true);
  }

  /**
   * Manually calculates the bounding box of all content to ensure accurate viewport fitting.
   * This method examines all nodes, edges, and groups to determine the true content bounds.
   * @returns Bounding box with x, y, width, height properties or null if calculation fails
   */
  private calculateContentBounds(): { x: number; y: number; width: number; height: number } | null {
    try {
      if (!this.currentLayout || !this.container) return null;

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      const updateBounds = (x0: number, x1: number, y0: number, y1: number): void => {
        if (![x0, x1, y0, y1].every(Number.isFinite)) return;
        minX = Math.min(minX, x0);
        maxX = Math.max(maxX, x1);
        minY = Math.min(minY, y0);
        maxY = Math.max(maxY, y1);
      };

      // Check all nodes
      const nodes = this.currentLayout.nodes;
      if (nodes && nodes.length > 0) {
        nodes.forEach((node: NodeWithMetadata) => {
          if (this.isHiddenNode(node)) return;
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

          // WebCola node positions are centered at (x, y).
          const nodeWidth = (node as any).visualWidth ?? node.width ?? 0;
          const nodeHeight = (node as any).visualHeight ?? node.height ?? 0;
          const halfWidth = nodeWidth / 2;
          const halfHeight = nodeHeight / 2;
          updateBounds(
            node.x - halfWidth,
            node.x + halfWidth,
            node.y - halfHeight,
            node.y + halfHeight
          );
        });
      }

      const self = this;

      // Check rendered (non-alignment) edge paths.
      const edgePaths = this.container.selectAll('.link-group path');
      if (!edgePaths.empty()) {
        edgePaths.each(function(this: SVGPathElement, edgeData: any) {
          if (edgeData?.id && self.isAlignmentEdge(edgeData)) return;
          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              updateBounds(
                bbox.x,
                bbox.x + bbox.width,
                bbox.y,
                bbox.y + bbox.height
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Check all node groups by examining actual DOM elements
      const nodeGroups = this.container.selectAll('.node, .error-node');
      if (!nodeGroups.empty()) {
        nodeGroups.each(function(this: SVGGElement, nodeData: NodeWithMetadata) {
          if (self.isHiddenNode(nodeData)) return;
          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              updateBounds(
                bbox.x,
                bbox.x + bbox.width,
                bbox.y,
                bbox.y + bbox.height
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Check all text elements separately for better text bounds calculation
      const textElements = this.container.selectAll('text');
      if (!textElements.empty()) {
        textElements.each(function(this: SVGTextElement, boundData: any) {
          if (
            boundData &&
            (typeof boundData.name === 'string' || typeof boundData.id === 'string') &&
            self.isHiddenNode(boundData)
          ) {
            return;
          }

          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              // Add extra padding for text elements due to font metrics
              const textPadding = 5;
              updateBounds(
                bbox.x - textPadding,
                bbox.x + bbox.width + textPadding,
                bbox.y - textPadding,
                bbox.y + bbox.height + textPadding
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Check for any group elements
      const groupElements = this.container.selectAll('.group');
      if (!groupElements.empty()) {
        groupElements.each(function(this: SVGGElement) {
          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              updateBounds(
                bbox.x,
                bbox.x + bbox.width,
                bbox.y,
                bbox.y + bbox.height
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Return null if no valid bounds were found
      if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
        console.warn('Could not calculate content bounds - no valid elements found');
        return null;
      }

      const bounds = {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      };
      
      return bounds;

    } catch (error) {
      console.error('Error calculating content bounds:', error);
      return null;
    }
  }

  /**
   * Dispatches a custom event when relations become available after layout rendering.
   * Includes all available relations in the event detail for external listeners.
   * 
   * @private
   */
  private dispatchRelationsAvailableEvent(): void {
    // Get all available relations
    const relations = this.getAllRelations();
    
    // Create custom event with comprehensive details
    const event = new CustomEvent('relations-available', {
      detail: {
        relations: relations,           // Yes, include all relations
        count: relations.length,        // Convenient count property
        timestamp: Date.now(),          // When the event was created
        graphId: this.id || 'unknown'   // Which graph instance
      },
      bubbles: true,    // Allow event to bubble up the DOM tree
      cancelable: true  // Allow event to be cancelled by listeners
    });

    // Dispatch the event from this element
    this.dispatchEvent(event);
    
    // console.log('🎯 Dispatched relations-available event:', {
    //   relations,
    //   count: relations.length
    // });
  }

  /** Public API for relation highlighting */

  /**
   * Gets all unique relation names from the current layout.
   * @returns An array of the set of relation names in the current layout.
   */
  public getAllRelations(): string[] {
    if (!this.currentLayout?.links) return [];

    const relNames = new Set(
      this.currentLayout.links
        .filter((edge: any) => !this.isAlignmentEdge(edge))
        .map((edge: any) => edge.relName)
        .filter(Boolean)
    );

    return Array.from(relNames);
  }

  /**
   * Highlights all the links or inferred links by its relation name.
   * @param relName - The name of the relation to highlight
   * @returns True if the relation was successfully highlighted, false otherwise
   */
  public highlightRelation(relName: string): boolean {
    if (!this.currentLayout?.links) return false;

    (this.svgLinkGroups as d3.Selection<SVGGElement, any, any, unknown>)
      .filter((d) => d.relName === relName && !this.isAlignmentEdge(d))
      .selectAll('path')
      .classed('highlighted', true);
    
    return true;
  }

  /**
   * Clears highlighting of the given relation name.
   * @param relName - The name of the relation to clear highlighting for
   * @returns True if the relation highlighting was successfully cleared, false otherwise
   */
  public clearHighlightRelation(relName: string): boolean {
    if (!this.currentLayout?.links) return false;

    (this.svgLinkGroups as d3.Selection<SVGGElement, any, any, unknown>)
      .filter((d) => d.relName === relName && !this.isAlignmentEdge(d))
      .selectAll('path')
      .classed('highlighted', false);
    
    return true;
  }

  /** Public API for node highlighting */

  /**
   * Highlights nodes based on their IDs (for unary selector results).
   * This is useful for visualizing the results of a selector expression.
   * 
   * @param nodeIds - Array of node IDs to highlight (e.g., from evaluator.selectedAtoms())
   * @returns True if any nodes were highlighted, false otherwise
   * 
   * @example
   * ```typescript
   * const result = evaluator.evaluate('Student');
   * const nodeIds = result.selectedAtoms();
   * graph.highlightNodes(nodeIds);
   * ```
   */
  public highlightNodes(nodeIds: string[]): boolean {
    if (!this.currentLayout?.nodes || !this.svgNodes) return false;
    if (!nodeIds || nodeIds.length === 0) return false;

    const nodeIdSet = new Set(nodeIds);
    let highlighted = false;

    (this.svgNodes as d3.Selection<SVGGElement, any, any, unknown>)
      .each((d: any, i: number, nodes: ArrayLike<SVGGElement>) => {
        if (nodeIdSet.has(d.id)) {
          d3.select(nodes[i]).classed('highlighted', true);
          highlighted = true;
        }
      });
    
    return highlighted;
  }

  /**
   * Highlights node pairs based on binary selector results.
   * Shows visual correspondence between first and second elements using different colors.
   * 
   * Note: If a node appears in multiple pairs with different roles (both first and second),
   * it will receive both 'highlighted-first' and 'highlighted-second' classes, and if badges
   * are enabled, only the last badge will be visible (this is intentional to avoid cluttering).
   * 
   * @param nodePairs - Array of [first, second] node ID pairs (e.g., from evaluator.selectedTwoples())
   * @param options - Optional configuration for highlighting
   * @param options.showBadges - If true, shows "1" and "2" badges on nodes (default: false)
   * @returns True if any nodes were highlighted, false otherwise
   * 
   * @example
   * ```typescript
   * const result = evaluator.evaluate('friend');
   * const pairs = result.selectedTwoples(); // [["Alice", "Bob"], ["Charlie", "Diana"]]
   * graph.highlightNodePairs(pairs, { showBadges: true });
   * ```
   */
  public highlightNodePairs(
    nodePairs: string[][], 
    options: { showBadges?: boolean } = {}
  ): boolean {
    if (!this.currentLayout?.nodes || !this.svgNodes) return false;
    if (!nodePairs || nodePairs.length === 0) return false;

    const { showBadges = false } = options;
    
    // Build sets of first and second node IDs
    const firstNodeIds = new Set<string>();
    const secondNodeIds = new Set<string>();
    
    // Validate and process pairs
    nodePairs.forEach((pair, index) => {
      if (!Array.isArray(pair)) {
        console.warn(`highlightNodePairs: Pair at index ${index} is not an array, skipping`);
        return;
      }
      if (pair.length !== 2) {
        console.warn(`highlightNodePairs: Pair at index ${index} has ${pair.length} elements (expected 2), skipping`);
        return;
      }
      
      const [first, second] = pair;
      if (first) firstNodeIds.add(first);
      if (second) secondNodeIds.add(second);
    });

    let highlighted = false;

    (this.svgNodes as d3.Selection<SVGGElement, any, any, unknown>)
      .each((d: any, i: number, nodes: ArrayLike<SVGGElement>) => {
        const nodeGroup = d3.select(nodes[i]);
        
        // Check if this node is a first element
        if (firstNodeIds.has(d.id)) {
          nodeGroup.classed('highlighted-first', true);
          highlighted = true;
          
          if (showBadges) {
            this.addHighlightBadge(nodeGroup, d, '1', '#007aff');
          }
        }
        
        // Check if this node is a second element
        if (secondNodeIds.has(d.id)) {
          nodeGroup.classed('highlighted-second', true);
          highlighted = true;
          
          if (showBadges) {
            // For nodes that are both first and second, show a combined badge
            if (firstNodeIds.has(d.id)) {
              this.addHighlightBadge(nodeGroup, d, '1,2', '#9B59B6'); // Purple for dual role
            } else {
              this.addHighlightBadge(nodeGroup, d, '2', '#ff3b30');
            }
          }
        }
      });
    
    return highlighted;
  }

  /**
   * Clears all node highlights (both unary and binary).
   * 
   * @returns True if the operation completed successfully
   */
  public clearNodeHighlights(): boolean {
    if (!this.svgNodes) return false;

    (this.svgNodes as d3.Selection<SVGGElement, any, any, unknown>)
      .classed('highlighted', false)
      .classed('highlighted-first', false)
      .classed('highlighted-second', false)
      .selectAll('.highlight-badge, .highlight-badge-bg')
      .remove();
    
    return true;
  }

  /**
   * Adds a visual badge to a highlighted node to show first/second correspondence.
   * 
   * @param nodeGroup - D3 selection of the node group
   * @param nodeData - Node data containing position and dimensions
   * @param label - Badge label text ('1' or '2')
   * @param color - Badge background color
   */
  private addHighlightBadge(
    nodeGroup: any, 
    nodeData: any, 
    label: string, 
    color: string
  ): void {
    // Remove any existing badges first
    nodeGroup.selectAll('.highlight-badge, .highlight-badge-bg').remove();

    const badgeSize = 16;
    const padding = 4;
    
    // Position badge at top-right corner of the node
    const badgeX = (nodeData.width || 0) / 2 - badgeSize / 2 - padding;
    const badgeY = -(nodeData.height || 0) / 2 + badgeSize / 2 + padding;

    // Add circle background for badge
    nodeGroup.append('circle')
      .attr('class', 'highlight-badge-bg')
      .attr('cx', badgeX)
      .attr('cy', badgeY)
      .attr('r', badgeSize / 2)
      .attr('fill', color);

    // Add text label
    nodeGroup.append('text')
      .attr('class', 'highlight-badge')
      .attr('x', badgeX)
      .attr('y', badgeY)
      .attr('dy', '0.35em')
      .text(label);
  }

  /**
   * Shows a runtime alert for edge routing errors.
   * 
   * @param sourceId - Source node ID
   * @param targetId - Target node ID
   */
  private showRuntimeAlert(sourceId: string, targetId: string): void {
    console.warn(`Runtime (WebCola) error when laying out an edge from ${sourceId} to ${targetId}. You may have to click and drag these nodes slightly to un-stick layout.`);
  }

  /**
   * Get CSS styles for the component
   */
  private getCSS(): string {
    return getGraphCSS({
      fontImports: this.getFontImports(),
      fontFamily: this.getFontFamily(),
      canvasBackground: this.getCanvasBackground(),
    });
  }


  private calculateNewPosition(pathElement : any, axis : 'x' | 'y'): number {
        const pathLength = pathElement.getTotalLength();
        const midpointLength = pathLength / 2;
        const offset = 0; //getRandomOffsetAlongPath(); // commenting out to remove jitter

        let targetLength = midpointLength + offset;

        if (targetLength >= pathLength) {
            targetLength = midpointLength;
        }

        const point = pathElement.getPointAtLength(targetLength);
        return axis === 'x' ? point.x : point.y;
    }

  

  /**
   * Show loading indicator
   */
  private showLoading(): void {
    const loading = this.root.querySelector('#loading') as HTMLElement;
    if (this.loadingShowTimer !== null) {
      window.clearTimeout(this.loadingShowTimer);
      this.loadingShowTimer = null;
    }
    if (!loading.classList.contains('visible')) {
      this.loadingShowTimer = window.setTimeout(() => {
        loading.classList.add('visible');
        this.loadingShowTimer = null;
      }, WebColaCnDGraph.LOADING_INDICATOR_DELAY_MS);
    }
    this.hideError();
  }

  /**
   * Update loading progress message
   */
  private updateLoadingProgress(message: string): void {
    const loading = this.root.querySelector('#loading') as HTMLElement;
    const progressEl = this.root.querySelector('#loading-progress') as HTMLElement;
    if (progressEl) {
      progressEl.textContent = message;
      loading.setAttribute('aria-label', message);
    }
  }

  /**
   * Hide loading indicator
   */
  private hideLoading(): void {
    if (this.loadingShowTimer !== null) {
      window.clearTimeout(this.loadingShowTimer);
      this.loadingShowTimer = null;
    }
    // Null-guarded: teardownInflightRender() may call this before the template
    // is attached or after the shadow root is torn down.
    const loading = this.root?.querySelector('#loading') as HTMLElement | null;
    loading?.classList.remove('visible');
  }

  /**
   * Show error message
   */
  private showError(
    message: string,
    detail?: { phase: LayoutErrorDetail['phase']; cause?: unknown; fatal?: boolean }
  ): void {
    const error = this.root.querySelector('#error') as HTMLElement;
    this.hideLoading();
    error.style.display = 'block';
    error.textContent = message;
    this.emitLayoutError({
      message,
      phase: detail?.phase ?? 'render',
      fatal: detail?.fatal ?? true,
      cause: detail?.cause,
    });
  }

  /**
   * Clear the error box.
   *
   * Called from teardownInflightRender, so every render starts from a clean
   * canvas. This used to happen only inside showLoading — which a morph render
   * never calls — so a failed render could leave its red box sitting on top of
   * the diagram that replaced it.
   */
  private hideError(): void {
    const error = this.root?.querySelector('#error') as HTMLElement | null;
    if (!error) return;
    error.style.display = 'none';
    error.textContent = '';
  }

  /**
   * Report a render failure to the host, and log it once.
   *
   * The single place this element says "something went wrong", so a host wires
   * up one listener rather than watching the console. Both other channels are
   * dead ends on their own: the console is not somewhere a diagram's author
   * looks, and the error box lives in this element's shadow root, inside
   * whatever panel or iframe the host embedded it in.
   */
  private emitLayoutError(detail: LayoutErrorDetail): void {
    // One line, named, with the original error attached — rather than the
    // several unattributed messages a failing render used to scatter.
    console.error(`[spytial] ${detail.message}`, detail.cause ?? '');
    this.dispatchEvent(new CustomEvent<LayoutErrorDetail>('layout-error', {
      detail,
      bubbles: true,
    }));
  }

  /**
   * Note that the diagram is empty on purpose. Deliberately not showError: an
   * empty instance is a normal result, and red text reads as a crash.
   */
  private showEmptyState(message: string): void {
    const empty = this.root?.querySelector('#empty-state') as HTMLElement | null;
    if (!empty) return;
    empty.textContent = message;
    empty.hidden = false;
  }

  /** Remove the empty-diagram note. Safe before the first render. */
  private hideEmptyState(): void {
    const empty = this.root?.querySelector('#empty-state') as HTMLElement | null;
    if (!empty) return;
    empty.hidden = true;
    empty.textContent = '';
  }

  /**
   * Renders the advisory-warning badge for the layout being drawn, and emits
   * `layout-warnings` so hosts with richer UI can route them elsewhere.
   *
   * These ride on the layout rather than arriving through a host callback because
   * every host — the docs site, the demos, spytial-py, spytial-rust — calls
   * `generateLayout` itself and hands this element only the resulting layout.
   * Anything needing extra host wiring would reach nobody.
   *
   * The badge is rebuilt per render, so on an animated trace it reflects the
   * current frame instead of accumulating across frames.
   *
   * The list mixes two kinds: per-item selector diagnostics, and deprecation
   * notices `parseLayoutSpec` raised about the spec's own forms. A deprecation
   * carries no selector, so its detail row is the label and message only.
   */
  private renderLayoutWarnings(warnings: LayoutWarning[]): void {
    const container = this.root.querySelector('#layout-warnings') as HTMLElement | null;
    const badge = this.root.querySelector('#layout-warnings-badge') as HTMLElement | null;
    const count = this.root.querySelector('#layout-warnings-count') as HTMLElement | null;
    const panel = this.root.querySelector('#layout-warnings-panel') as HTMLElement | null;
    if (!container || !badge || !count || !panel) {
      return; // Template not rendered yet (or overridden by a subclass)
    }

    if (warnings.length === 0) {
      container.hidden = true;
      panel.hidden = true;
      badge.setAttribute('aria-expanded', 'false');
      panel.replaceChildren();
      this.dismissedWarningSignature = null;
      return;
    }

    // A dismissal applies to the warnings that were on screen when it happened.
    // Re-showing an identical set every frame of a trace would make the dismiss
    // button useless; staying hidden after the warnings *change* would hide new
    // information. Keying on the set itself gets both.
    //
    // Sorted, so the key is genuinely the set and not the order it arrived in:
    // warnings are collected as the render walks the instance, and two frames
    // holding the same warnings can collect them in a different order. Without
    // the sort, that reads as a change and the badge reappears after dismissal.
    const signature = warnings
      .map(w => `${w.code}|${w.specType ?? ''}|${w.specIndex ?? ''}|${w.name ?? w.selector ?? w.message}`)
      .sort()
      .join('\n');
    if (this.dismissedWarningSignature === signature) {
      container.hidden = true;
      return;
    }
    this.dismissedWarningSignature = null;

    // "spec", not "selector": the list now mixes selector diagnostics with
    // deprecation notices raised while parsing the spec.
    count.textContent = `${warnings.length} spec ${warnings.length === 1 ? 'warning' : 'warnings'}`;
    badge.setAttribute('title', 'Click for detail');
    this.currentWarningSignature = signature;

    // Built with DOM calls rather than innerHTML: every field below is authored
    // data (selector text, an instance's own names) and must not be parsed as markup.
    panel.replaceChildren(...warnings.map(w => {
      const item = document.createElement('div');
      item.className = 'layout-warning-item';

      const label = document.createElement('div');
      label.className = 'layout-warning-label';
      label.textContent = w.label ?? `${w.specType ?? 'spec'}[${w.specIndex ?? 0}]`;
      item.appendChild(label);

      const message = document.createElement('div');
      message.className = 'layout-warning-message';
      message.textContent = w.message;
      item.appendChild(message);

      // The suggestion is available on hover, not as visible text. It is only a
      // guess: simple-graph-query takes the nearest name within one or two edits,
      // over atom ids as well as types and relations, so it can propose something
      // meaningless in selector position. Putting it behind a hover keeps it
      // reachable without letting a guess read like a diagnosis. It remains on
      // `LayoutWarning.suggestion` for programmatic consumers either way.
      if (w.suggestion) {
        item.title = `Did you mean '${w.suggestion}'?`;
      }

      // Where it came from — only for warnings that are about a selector. A
      // deprecation has none (it is raised at parse time), and the label already
      // names the directive, so the row would be an empty `<code>`.
      if (w.selector) {
        const where = document.createElement('div');
        where.className = 'layout-warning-message';
        where.append(`${w.context}: `);
        const selector = document.createElement('code');
        selector.textContent = w.selector;
        where.appendChild(selector);
        item.appendChild(where);
      }

      return item;
    }));

    // Preserve the open/closed state across re-renders — on an animated trace the
    // badge re-renders every frame, and a panel that snapped shut each time would
    // be unreadable.
    panel.hidden = badge.getAttribute('aria-expanded') !== 'true';
    container.hidden = false;

    this.dispatchEvent(new CustomEvent('layout-warnings', {
      detail: { warnings },
      bubbles: true
    }));
  }

  /** Wires the warning bar's expand/collapse and dismiss controls. Called once, at setup. */
  private setupLayoutWarningsToggle(): void {
    const container = this.root.querySelector('#layout-warnings') as HTMLElement | null;
    const badge = this.root.querySelector('#layout-warnings-badge') as HTMLElement | null;
    const panel = this.root.querySelector('#layout-warnings-panel') as HTMLElement | null;
    const dismiss = this.root.querySelector('#layout-warnings-dismiss') as HTMLElement | null;
    if (!container || !badge || !panel || !dismiss) {
      return;
    }
    badge.addEventListener('click', () => {
      const willExpand = badge.getAttribute('aria-expanded') !== 'true';
      badge.setAttribute('aria-expanded', String(willExpand));
      panel.hidden = !willExpand;
    });
    dismiss.addEventListener('click', () => {
      this.dismissedWarningSignature = this.currentWarningSignature;
      container.hidden = true;
      panel.hidden = true;
      badge.setAttribute('aria-expanded', 'false');
    });
  }

  // =========================================
  // MODAL DIALOG METHODS
  // =========================================

  /**
   * Show a confirmation dialog
   */
  private showConfirmDialog(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';

      overlay.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Confirm Action</h3>
          </div>
          <div class="modal-body">
            <p class="modal-message">${message}</p>
          </div>
          <div class="modal-footer">
            <button class="modal-button secondary" data-action="cancel">Cancel</button>
            <button class="modal-button primary" data-action="confirm">Confirm</button>
          </div>
        </div>
      `;

      // Add event listeners
      overlay.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('modal-overlay')) {
          // Clicked outside dialog
          this.root.removeChild(overlay);
          resolve(false);
        } else if (target.dataset.action === 'cancel') {
          this.root.removeChild(overlay);
          resolve(false);
        } else if (target.dataset.action === 'confirm') {
          this.root.removeChild(overlay);
          resolve(true);
        }
      });

      // Handle escape key
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(false);
        }
      };
      document.addEventListener('keydown', handleKeydown);

      this.root.appendChild(overlay);
      
      // Focus the confirm button
      const confirmBtn = overlay.querySelector('[data-action="confirm"]') as HTMLButtonElement;
      confirmBtn?.focus();
    });
  }

  /**
   * Show a prompt dialog for text input
   */
  private showPromptDialog(message: string, defaultValue: string = ''): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';

      overlay.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Input Required</h3>
          </div>
          <div class="modal-body">
            <p class="modal-message">${message}</p>
            <input type="text" class="modal-input" value="${defaultValue}" placeholder="Enter text...">
          </div>
          <div class="modal-footer">
            <button class="modal-button secondary" data-action="cancel">Cancel</button>
            <button class="modal-button primary" data-action="ok">OK</button>
          </div>
        </div>
      `;

      const input = overlay.querySelector('.modal-input') as HTMLInputElement;

      // Add event listeners
      overlay.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('modal-overlay')) {
          // Clicked outside dialog
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'cancel') {
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'ok') {
          const value = input.value;
          this.root.removeChild(overlay);
          resolve(value);
        }
      });

      // Handle enter and escape keys
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          const value = input.value;
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(value);
        } else if (e.key === 'Escape') {
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(null);
        }
      };
      document.addEventListener('keydown', handleKeydown);

      this.root.appendChild(overlay);
      
      // Focus and select the input
      input.focus();
      input.select();
    });
  }

  /**
   * Show a prompt dialog for text input with a delete button option
   * @param message - Dialog message
   * @param defaultValue - Default input value
   * @returns Promise that resolves to: input value, null (cancel), or 'DELETE' (delete action)
   */
  private showEdgeEditDialog(message: string, defaultValue: string = ''): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';

      overlay.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Edit Edge</h3>
          </div>
          <div class="modal-body">
            <p class="modal-message">${message}</p>
            <input type="text" class="modal-input" value="${defaultValue}" placeholder="Enter text...">
          </div>
          <div class="modal-footer">
            <button class="modal-button secondary" data-action="cancel">Cancel</button>
            <button class="modal-button danger" data-action="delete" style="background: #dc3545; margin-right: auto;">Delete Edge</button>
            <button class="modal-button primary" data-action="ok">OK</button>
          </div>
        </div>
      `;

      const input = overlay.querySelector('.modal-input') as HTMLInputElement;

      // Add event listeners
      overlay.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('modal-overlay')) {
          // Clicked outside dialog
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'cancel') {
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'delete') {
          this.root.removeChild(overlay);
          resolve('DELETE'); // Special signal for deletion
        } else if (target.dataset.action === 'ok') {
          const value = input.value;
          this.root.removeChild(overlay);
          resolve(value);
        }
      });

      // Handle enter and escape keys
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          const value = input.value;
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(value);
        } else if (e.key === 'Escape') {
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(null);
        }
      };
      document.addEventListener('keydown', handleKeydown);

      this.root.appendChild(overlay);
      
      // Focus and select the input
      input.focus();
      input.select();
    });
  }

  // =========================================
  // SCREENSHOT / EXPORT API
  // =========================================

  /**
   * Captures the current graph as a high-resolution PNG and downloads it.
   * Inlines all computed styles and converts external images to base64
   * for accurate offline rendering.
   */
  public async takeScreenshot(): Promise<void> {
    const svg = this.shadowRoot?.querySelector('#svg') as SVGSVGElement | null;
    if (!svg) {
      console.warn('No SVG element found for screenshot.');
      return;
    }

    try {
      // Clone the SVG so we don't mutate the live DOM
      const svgClone = svg.cloneNode(true) as SVGSVGElement;

      // Read viewBox dimensions for proper sizing
      const viewBox = svg.getAttribute('viewBox');
      let width = svg.clientWidth || 800;
      let height = svg.clientHeight || 600;
      if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4) {
          width = parts[2];
          height = parts[3];
        }
        svgClone.setAttribute('viewBox', viewBox);
      }
      svgClone.setAttribute('width', String(width));
      svgClone.setAttribute('height', String(height));
      svgClone.removeAttribute('preserveAspectRatio');

      // Inline computed styles from the live SVG into the clone so they survive serialization
      this.inlineComputedStyles(svg, svgClone);

      // Convert <image> hrefs to base64 data URIs so icons render in the PNG
      await this.convertImagesToBase64(svgClone);

      // Match the on-screen canvas background so PNG exports look like the live view
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('width', '100%');
      bgRect.setAttribute('height', '100%');
      bgRect.setAttribute('fill', this.getCanvasBackground());
      svgClone.insertBefore(bgRect, svgClone.firstChild);

      // Serialize the clone to a string
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);

      // Render to a high-res canvas
      const scale = WebColaCnDGraph.SCREENSHOT_SCALE;
      const blob = await this.svgStringToPngBlob(svgString, width, height, scale);
      if (!blob) {
        console.error('Failed to generate PNG blob from SVG.');
        return;
      }

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `graph-screenshot-${timestamp}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Screenshot failed:', error);
    }
  }

  /**
   * Walks the original and cloned SVG trees in parallel, copying key computed
   * style properties onto the clone as inline styles.
   */
  private inlineComputedStyles(original: Element, clone: Element): void {
    const STYLE_PROPS = [
      'font-family', 'font-size', 'font-weight', 'font-style',
      'fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'stroke-linejoin',
      'opacity', 'text-anchor', 'dominant-baseline',
      'paint-order', 'visibility', 'display',
      // `color` carries the inlined icons' `currentColor`. The serialized clone
      // loses the shadow stylesheet (and the host's --cnd-* properties with it),
      // so without this an inlined glyph would fall back to the initial black.
      'color',
    ];

    const origChildren = original.children;
    const cloneChildren = clone.children;

    // Inline styles on the current element
    if (original instanceof HTMLElement || original instanceof SVGElement) {
      const computed = getComputedStyle(original);
      const cloneEl = clone as HTMLElement | SVGElement;
      for (const prop of STYLE_PROPS) {
        const val = computed.getPropertyValue(prop);
        if (val) {
          cloneEl.style.setProperty(prop, val);
        }
      }
    }

    // Recurse into children
    const len = Math.min(origChildren.length, cloneChildren.length);
    for (let i = 0; i < len; i++) {
      this.inlineComputedStyles(origChildren[i], cloneChildren[i]);
    }
  }

  /**
   * Finds all <image> elements in the cloned SVG and replaces their
   * href/xlink:href with base64 data URIs so they render when serialized.
   */
  private async convertImagesToBase64(svgClone: SVGSVGElement): Promise<void> {
    const images = svgClone.querySelectorAll('image');
    const promises: Promise<void>[] = [];

    images.forEach((img) => {
      const href = img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
      if (!href || href.startsWith('data:')) return;

      const promise = this.fetchImageAsBase64(href)
        .then((dataUri) => {
          img.setAttribute('href', dataUri);
          img.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
        })
        .catch(() => {
          // If CORS or network prevents fetching, remove the image gracefully
          img.remove();
        });
      promises.push(promise);
    });

    await Promise.all(promises);
  }

  /**
   * Fetches an image URL and returns a base64 data URI.
   */
  private fetchImageAsBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No canvas context')); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  }

  /**
   * Renders an SVG string to a PNG Blob at the given scale factor.
   */
  private svgStringToPngBlob(
    svgString: string,
    width: number,
    height: number,
    scale: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); resolve(null); return; }

        ctx.scale(scale, scale);
        ctx.fillStyle = this.getCanvasBackground();
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };

      img.src = url;
    });
  }

  // =========================================
  // EVENT-BASED STATE INTEGRATION API
  // =========================================

  // =========================================
  // LIFECYCLE METHODS AND CLEANUP
  // =========================================

  /**
   * Called when the custom element is disconnected from the DOM.
   * Performs cleanup to prevent memory leaks.
   *
   * The teardown is deferred by a microtask because a *move* fires this
   * callback too: `appendChild` of an element that already has a parent
   * removes it and re-inserts it, which is what host frameworks do whenever
   * they re-render around the graph. Disposing there wiped the shadow SVG of a
   * graph the host considered untouched, and every later renderLayout drew
   * nothing — silently, with no error to go on. The re-insert happens in the
   * same task, so by the time this runs a move is already reconnected.
   *
   * A genuine removal still disposes, and the element stays disposed if it is
   * added back later — as it always has. Rebuilding on re-attach needs the
   * subclasses to re-append their own chrome, which is a bigger change than
   * this one.
   */
  disconnectedCallback(): void {
    queueMicrotask(() => {
      if (this.isConnected) return;
      // Never let cleanup throw into the host's removeChild — an exception
      // here propagates into the caller's unmount (e.g. React's commit
      // phase) and can break the entire teardown.
      try {
        this.dispose();
      } catch (e) {
        console.warn('WebColaCnDGraph: error during disconnect cleanup', e);
      }
    });
  }

  /**
   * Disposes of resources to prevent memory leaks.
   * Should be called when the component is no longer needed.
   * 
   * This method cleans up:
   * - D3 selections and event listeners
   * - WebCola layout references
   * - Keyboard event handlers
   * - Temporary UI elements (modals, overlays)
   */
  public dispose(): void {
    // Stop the solver and morph timers, detach stale tick/end handlers, and
    // remove any morph snapshot layer — before tearing down the selections
    // those handlers would otherwise touch.
    this.teardownInflightRender();

    // Remove keyboard event handlers
    this.detachInputModeListeners();
    this.deactivateInputMode();

    // Clear D3 selections and remove event listeners
    if (this.svg) {
      this.svg.on('.zoom', null); // Remove zoom event handlers
      this.svg.selectAll('*').remove(); // Remove all child elements
    }

    if (this.container) {
      this.container.selectAll('*').remove(); // Remove all container children
    }

    // Clear node drag event handlers
    if (this.svgNodes) {
      this.svgNodes.on('.drag', null);
      this.svgNodes.on('.cnd', null);
    }

    // Clear stored references to help garbage collection
    this.currentLayout = null as any;
    this.colaLayout = null as any;
    this.svgNodes = null as any;
    this.svgLinkGroups = null as any;
    this.svgGroups = null as any;
    this.svgGroupLabels = null as any;
    this.svgGroupLabelBgs = null as any;
    this.zoomBehavior = null as any;
    this.storedTransform = null as any;
    
    // Clear drag state
    this.dragStartPositions.clear();
    
    // Clear edge creation state
    this.cleanupEdgeCreation();
    
    // Clear text measurement canvas
    if (this.textMeasurementCanvas) {
      this.textMeasurementCanvas = null;
    }
  }

  /**
   * Returns memory usage statistics for monitoring and debugging.
   * Useful for tracking memory consumption and identifying potential leaks.
   * 
   * @returns Object containing various memory-related metrics
   */
  public getMemoryStats(): {
    nodeCount: number;
    edgeCount: number;
    groupCount: number;
    constraintCount: number;
    hasActiveLayout: boolean;
  } {
    return {
      nodeCount: this.currentLayout?.nodes?.length || 0,
      edgeCount: this.currentLayout?.links?.length || 0,
      groupCount: this.currentLayout?.groups?.length || 0,
      constraintCount: this.currentLayout?.constraints?.length || 0,
      hasActiveLayout: !!this.colaLayout
    };
  }

}

// Register the custom element only in browser environments. The get() guard
// makes registration idempotent: this module is inlined into more than one
// bundle (main global + explorer global), and a second unguarded define on a
// page loading both throws NotSupportedError.
if (typeof customElements !== 'undefined' && typeof HTMLElement !== 'undefined'
    && !customElements.get('webcola-cnd-graph')) {
  customElements.define('webcola-cnd-graph', WebColaCnDGraph);
}
